import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { Box, Typography, TextField, Button, IconButton, Paper, List, ListItem, Chip, CircularProgress, Switch, Tooltip, Menu, Skeleton, InputAdornment } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import PlaylistAddIcon from '@mui/icons-material/PlaylistAdd';
import PanelEmptyState from '../../../Components/PanelEmptyState';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import ListAltIcon from '@mui/icons-material/ListAlt';
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined';
import ShareIcon from '@mui/icons-material/Share';
import MyLocationOutlinedIcon from '@mui/icons-material/MyLocationOutlined';
import AddLocationAltOutlinedIcon from '@mui/icons-material/AddLocationAltOutlined';
import EditLocationAltOutlinedIcon from '@mui/icons-material/EditLocationAltOutlined';
import PushPinOutlinedIcon from '@mui/icons-material/PushPinOutlined';
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutline';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import LanguageIcon from '@mui/icons-material/Language';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import FitScreenIcon from '@mui/icons-material/FitScreen';
import { colors, typography, layout } from '../../../styles/theme';
import { getScrollEdgeFadeSx, useScrollEdgeFade } from '../../../hooks/useScrollEdgeFade';
import {
  normalizeLotsData,
  getDisplayUnits,
  getUnitDisplayTitle,
  getUnitListIds,
  extractListMembership,
  isDirectLot,
  lotMatchesSearch,
} from '../../utils/userLotUnits';
import { countActiveParcelFilters } from '../../data/parcelFilterContract';
import { filterOwnedLotsByPanelFilters } from '../../utils/sharedListingFilters';
import { filterGroupedLotForSharedSearch } from '../../utils/brandedListingItems';
import {
  buildSharedListDockPolygonInfo,
  fetchSharedDirectLotDetail,
  fetchSharedUnitDetail,
  getShareTokenFromRow,
  mergeLotDetailFromApi,
  mergeSharedListDetailFromApi,
  resolveSharedListLotsForMap,
} from '../../utils/enrichSharedLotsForMap';
import { getLotRepresentativeLatLng } from '../../hooks/useLotSelection';
import { useLocatedLot } from '../../hooks/useLocatedLot';
import { enrichLotRowForLocate, hasUsableBoundary } from '../../utils/fitMapBoundsToLots';
import ListDetailLotCard from '../ListDetailLotCard/ListDetailLotCard';
import ListDetailRecordCards from './ListDetailRecordCards';
import LoadingRecordCards from '../RecordCards/LoadingRecordCards';
import RecordViewModeToggle from '../RecordCards/RecordViewModeToggle';
import { useRecordViewMode } from '../../hooks/useRecordViewMode';
import { useApiRecordCards } from '../../hooks/useApiRecordCards';
import { useDebouncedValue } from '../../hooks/useDebouncedValue';
import { lotRowMatchesSearch, parseSearchTerms, recordItemMatchesSearch } from '../../utils/recordSearch';
import { isRecordsApiSource } from '../../utils/recordsSource';
import {
  buildRecordCardItems,
  countRecordCardItems,
  sortRecordCardItems,
} from '../../utils/recordCards';
import ActionMenuItem, { ACTION_MENU_LIST_PROPS, ACTION_MENU_PAPER_PROPS } from '../ActionMenuItem';
import { toast } from '../../../Components/Toast';
import { confirmDialog } from '../../../Components/ConfirmDialog';
import PanelShell from '../PanelShell/PanelShell';
import PanelHeader, { panelCloseIconButtonSx } from '../PanelShell/PanelHeader';
import DockPanel, { EMPTY_SHARED_LIST_UNITS } from '../DockPanel/DockPanel';
import {
  getMyLots,
  createUserLot,
  createUserLotList,
  updateUserLotList,
  deleteUserLotList,
  getUserLotLists,
} from '../../../services/userLotsService';
import { getLotDetail } from '../../../services/lotsService';
import {
  SPACE_GUEST_EMPTY_LISTS_HINT,
  SPACE_GUEST_EMPTY_LISTS_TITLE,
  SPACE_GUEST_LIST_CHANGED_MESSAGE,
  isSpaceGuestListRemovalError,
  resolveSpaceGuestListsSubtitle,
  resolveSpaceGuestWriteError,
} from '../../utils/spaceMirrorLots';
import ShareUserLotListDialog from '../../../Components/ShareUserLotListDialog';
import CollectionActivityDialog from './CollectionActivityDialog';
import CollectionLeadsDialog from './CollectionLeadsDialog';
import ListDetailInquiryMetaRow from './ListDetailInquiryMetaRow';
import ListSortMenu from '../ListSortMenu/ListSortMenu';
import RecordCountsLabel from '../RecordCountsLabel';
import { CommercialFiltersAdornment, withCommercialAdornmentSx } from '../CommercialFilters/CommercialFiltersButton';
import {
  COMMERCIAL_SORT_OPTIONS,
  DEFAULT_COMMERCIAL_FILTERS,
  countActiveCommercialFilters,
  filterRecordsByCommercial,
  getRecordCommercialBlock,
  isCommercialSort,
  sortRecordsCommercial,
} from '../../../Leads/recordCommercialFilters';

import { useCollectionInquiryStats } from '../../../hooks/useCollectionInquiryStats';
import { useCollectionLeadCounts } from '../../../hooks/useCollectionLeadCounts';
import {
  orderLeadCountsIds,
  resolveCollectionLeadsEntry,
  SHOW_COLLECTION_LEADS_FROM_LIST_DETAIL,
} from '../../../Leads/leadCollections';
import { getCollectionInquiryItemCounts } from '../../utils/collectionInquiryStats';
import { resolveListsPanelReturnOrigin } from '../../utils/listsPanelReturnOrigin';
import {
  DEFAULT_LIST_SORT,
  MANAGE_LIST_SORT_OPTIONS,
  sortSharedGroupedLots,
} from '../../utils/listSort';
import {
  getSharedList,
  getSharedWithMeLists,
  leaveSharedUserLotList,
} from '../../../services/userLotsShareService';
import {
  guestShareActionErrorToast,
  mergeSharedWithMeItems,
} from '../../utils/sharedWithMeCollections';
import {
  emptyStateBoxSx,
  emptyStateIconBoxSx,
  emptyStateIconSx,
  emptyStateTitleSx,
  emptyStateHintSx,
  emptyStateCreateButtonSx,
  emptyStateCreateIconSx,
  detailAddButtonSx,
  detailListNameSx,
  cadDetailNameRowSx,
  cadDetailNameSx,
  detailListNameRowSx,
  detailListNamePrimarySx,
  detailListNameActionsSx,
  detailListNameEditFieldSx,
  listSx,
  listFiltersBoxSx,
  listSearchToolbarSx,
  listSearchFieldSx,
  recordCountsRowSx,
  recordToolbarControlsSx,
  listSearchIconSx,
  listAddButtonSx,
  listFilterChipsRowSx,
  listsSectionToolbarRowSx,
  listsIndexSectionsStackSx,
  getListFilterChipSx,
  getListFilterIconChipSx,
  getListItemRootSx,
  getListRowSurfaceSx,
  listRowPreviewActiveSx,
  editRowBoxSx,
  textFieldSx,
  saveEditIconButtonSx,
  saveEditProgressSx,
  cancelEditIconButtonSx,
  viewRowBoxSx,
  viewRowMainSx,
  viewRowTitleRowSx,
  listNameTypographySx,
  listCardMetaRowSx,
  getListItemCountTypographySx,
  getListVisibilityBadgeSx,
  listCardActionIconSx,
  rowActionsBoxSx,
  createListFormBoxSx,
  createListLabelSx,
  createListNameFieldSx,
  createListActionsRowSx,
  createListCancelButtonSx,
  createListSubmitButtonSx,
  listsSectionEmptySx,
  fixedBottomZoneSx,
} from './UserLotListsPanel.styles';
import MyListsSection from './MyListsSection';
import SharedWithMeListsSection from './SharedWithMeListsSection';
import { useActiveSpace } from '../../context/ActiveSpaceContext';
import AddLotsToListFlow from './AddLotsToListFlow';
import CadCollectionListRow from './CadCollectionListRow';
import CadInListsDetail from '../../../Showroom/components/CadInListsDetail';
import CadUploadFlow from '../../../Showroom/components/CadUploadFlow/CadUploadFlow';
import NewListKindChoice from '../../../Showroom/components/CadUploadFlow/NewListKindChoice';
import ShareCadProjectDialog from '../../../Showroom/components/ShareCadProjectDialog';
import { useCadCollectionsForLists } from '../../../Showroom/hooks/useCadCollectionsForLists';
import { filterCadProjectsForLists } from '../../../Showroom/utils/cadCollectionsForLists';
import { canEditCadCollection, canManageCadProject, canPublishCadProject, getCadProjectId, mergeCadProjectFromReload, mergeCadProjectSummary, resolveCadProjectFromCatalog } from '../../../Showroom/utils/cadAccess';
import { hasCadGeorefAnchor } from '../../../Showroom/utils/cadGeoref';
import { CAD_PROJECT_NAME_MAX } from '../../../Showroom/data/cadConstants';
import {
  deleteCadProject,
  getCadProject,
  leaveSharedCadProject,
  updateCadProject,
} from '../../../services/cadService';
import { canLocateCadProjectOnMap, isCadMapDisplay, isCadPlanDisplay, shouldPaintCadOnMap } from '../../../Showroom/utils/cadGeometry';
import { formatCadParcelsCountsLabel, formatCadProjectDisplayName } from '../../../Showroom/utils/cadParcelDisplay';
import { countCadSellableLots } from '../../../Showroom/utils/cadMasterplan';
import {
  formatListMembershipCountsLabel,
  formatRecordsSummary,
  getListCountsFromGroupedLots,
  withListCountsFromApi,
} from '../../utils/lotContentCounts';
import { mergeListCommercial, onCommercialChanged } from '../../../Leads/commercialSummary';

/**
 * Detalle de lista gestionada: orden de siempre + comerciales. «Más / Menos consultas»
 * de la lista ya existen (consultas por la lista), así que no se suman los comerciales
 * de consultas para no repetir.
 */
const LIST_DETAIL_COMMERCIAL_SORT_OPTIONS = [
  ...MANAGE_LIST_SORT_OPTIONS,
  ...COMMERCIAL_SORT_OPTIONS.filter((o) => !o.value.startsWith('commercial-inquiries')),
];

const PANEL_BG = '#1c1c1c';
const PANEL_BORDER = '#4a4a4a';

const SIDEBAR_RAIL_WIDTH = layout.sidebarRailWidth;

/** Tarjetas renderizadas por tanda en detalle de lista (listas grandes congelaban el panel). */
const LIST_RENDER_CHUNK = 60;

const createEmptyStaging = () => ({
  directLotIds: new Set(),
  unitIds: new Set(),
  lotUnitPicks: {},
  lotMeta: {},
  /** lotId | `unit:${id}` → 'records' | 'map' */
  itemSources: {},
});

const cloneAddStaging = (prev) => ({
  directLotIds: new Set(prev.directLotIds),
  unitIds: new Set(prev.unitIds),
  lotUnitPicks: { ...prev.lotUnitPicks },
  lotMeta: { ...prev.lotMeta },
  itemSources: { ...(prev.itemSources || {}) },
});

const setStagingSource = (next, key, source) => {
  if (!key || !source) return next;
  next.itemSources[key] = source;
  return next;
};

const clearStagingSource = (next, key) => {
  if (!key || !next.itemSources) return next;
  delete next.itemSources[key];
  return next;
};

const notifyToast = ({ title, text, icon = 'info' }) => {
  const payload = text ? { title, message: text } : title;
  if (icon === 'success') toast.success(payload);
  else if (icon === 'error') toast.error(payload);
  else if (icon === 'warning') toast.warning(payload);
  else toast.info(payload);
};

const getDeleteTooltip = (row) => {
  if (row.item_count > 0) return `Eliminar lista (se desvincularán ${row.item_count} unidad(es))`;
  return 'Eliminar lista';
};

const countCadProjectParcels = (project) => (
  countCadSellableLots(project?.parcels?.features)
);

const getShareBadgeType = (row) => {
  if (!row?.is_shared) return 'none';
  return row?.access_mode === 'restricted' ? 'restricted' : 'public';
};

function findActivityLotInList(lots, target) {
  if (!target || !Array.isArray(lots)) return null;
  if (target.itemType === 'lot') {
    const lotId = String(target.lotId || '');
    const lot = lots.find((row) => String(row?.lot_id || '') === lotId);
    return lot ? { lot, unit: null } : null;
  }
  if (target.itemType === 'unit') {
    const userLotId = String(target.userLotId || '');
    for (const lot of lots) {
      const unit = getDisplayUnits(lot).find(
        (row) => String(row?.id || row?.user_lot_id || '') === userLotId,
      );
      if (unit) return { lot, unit };
    }
  }
  return null;
}

/** Chips Todos / Público / Restringido en Mis listas. */
const matchesVisibilityFilter = (row, filter) => {
  if (filter === 'all') return true;
  // Público y Restringido solo listan listas compartidas
  if (!row?.is_shared) return false;
  const mode = String(row?.access_mode || row?.accessMode || '').toLowerCase();
  if (filter === 'public') return mode === 'public';
  if (filter === 'private') return mode === 'restricted';
  return true;
};


const fixedTopZoneSx = {
  px: 2,
  pt: 0,
  pb: 2,
  display: 'flex',
  flexDirection: 'column',
  gap: 1.5,
};

const scrollableListZoneSx = {
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  overflowY: 'auto',
  px: 2,
  pb: 2,
  scrollbarGutter: 'stable',
  scrollbarWidth: 'thin',
  scrollbarColor: 'rgba(255,255,255,0.22) transparent',
  '&::-webkit-scrollbar': { width: 6 },
  '&::-webkit-scrollbar-track': { background: 'transparent' },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: 'rgba(255,255,255,0.10)',
    borderRadius: 8,
  },
  '&::-webkit-scrollbar-thumb:hover': {
    backgroundColor: 'rgba(255,255,255,0.18)',
  },
};

/** Índice de listas: flex column; el scroll vive dentro de cada sección expandida. */
const listsIndexZoneSx = {
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  minHeight: 0,
  overflow: 'hidden',
  px: 2,
  pb: 2,
};

/** Alta desde un plano: el formulario es más alto que el manual y necesita scroll propio. */
const listsCreateFromPlanZoneSx = {
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  minHeight: 0,
  overflowY: 'auto',
  overflowX: 'hidden',
  px: 2,
  pb: 2,
  '&::-webkit-scrollbar': { width: 5 },
  '&::-webkit-scrollbar-track': { backgroundColor: 'transparent' },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: 'rgba(255,255,255,0.10)',
    borderRadius: 8,
  },
  '&::-webkit-scrollbar-thumb:hover': {
    backgroundColor: 'rgba(255,255,255,0.18)',
  },
};

/** Detalle CAD: el scroll vive dentro de CadInListsDetail / ManagePanel. */
const cadDetailZoneSx = {
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  minHeight: 0,
  overflow: 'hidden',
};

const LoadingListRows = ({ count = 7 }) => {
  const rowSx = {
    borderRadius: '8px',
    border: 'none',
    backgroundColor: '#242424',
    px: '12px',
    py: '10px',
    minHeight: 92,
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
  };

  const skPrimarySx = { bgcolor: '#383838', borderRadius: '4px' };
  const skMutedSx = { bgcolor: '#2E2E2E', borderRadius: '4px' };
  const skAccentSx = { bgcolor: 'rgba(109,168,167,0.24)', borderRadius: '4px' };
  const skIconSx = { bgcolor: '#383838' };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, pt: 1 }}>
      {Array.from({ length: count }).map((_, idx) => (
        <Box key={idx} sx={rowSx}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
            <Skeleton variant="rounded" width={`${48 - (idx % 3) * 6}%`} height={18} sx={skPrimarySx} />
            {idx % 2 === 0 ? (
              <Skeleton variant="rounded" width={72} height={22} sx={skAccentSx} />
            ) : (
              <Skeleton variant="rounded" width={18} height={18} sx={{ ...skAccentSx, borderRadius: '50%' }} />
            )}
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
            <Skeleton variant="rounded" width={`${34 - (idx % 3) * 4}%`} height={14} sx={skMutedSx} />
            <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
              <Skeleton variant="circular" width={24} height={24} sx={skIconSx} />
              <Skeleton variant="circular" width={24} height={24} sx={skIconSx} />
            </Box>
          </Box>
        </Box>
      ))}
    </Box>
  );
};

const LoadingLotCards = ({ count = 7 }) => {
  const cardSx = {
    borderRadius: '8px',
    border: 'none',
    backgroundColor: '#242424',
    px: '12px',
    py: '10px',
    minHeight: 96,
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    boxShadow: 'none',
  };

  const skPrimarySx = { bgcolor: '#383838', borderRadius: '4px' };
  const skMutedSx = { bgcolor: '#2E2E2E', borderRadius: '4px' };
  const skAccentSx = { bgcolor: 'rgba(109,168,167,0.24)', borderRadius: '4px' };
  const skActionSx = { bgcolor: '#383838' };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1 }}>
      {Array.from({ length: count }).map((_, idx) => (
        <Box key={idx} sx={cardSx}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
            <Skeleton variant="rounded" width={20} height={20} sx={{ ...skMutedSx, flexShrink: 0, mt: '1px' }} />
            <Box sx={{ minWidth: 0, flex: 1, display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <Skeleton variant="rounded" width={`${68 - (idx % 4) * 6}%`} height={18} sx={skPrimarySx} />
              <Skeleton variant="rounded" width={`${44 - (idx % 3) * 5}%`} height={14} sx={skMutedSx} />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {idx % 3 === 0 && (
                  <Skeleton variant="circular" width={6} height={6} sx={{ bgcolor: colors.primary }} />
                )}
                <Skeleton variant="rounded" width={idx % 2 === 0 ? 78 : 68} height={26} sx={skAccentSx} />
              </Box>
            </Box>
            <Skeleton variant="circular" width={24} height={24} sx={{ ...skActionSx, flexShrink: 0 }} />
          </Box>
        </Box>
      ))}
    </Box>
  );
};

export default function UserLotListsPanel({
  uiLeftInset = SIDEBAR_RAIL_WIDTH,
  onClose,
  /** Leads L2 (flag): abrir la ficha de un lead desde la bandeja de consultas. */
  onOpenLead,
  /** Comparación de mercado: «Ver en el mapa» de la parcela → modo Comparables (MapPage). */
  onShowMarketComparables = undefined,
  onOpenLot,
  onSharedLotDockChange,
  onLocateLot,
  onFitInView,
  onHighlightLotIdsChange,
  onHighlightLotsChange,
  /** IDs de lotes stagiados (aún no en la lista) para highlight PMTiles */
  onPendingAddLotIdsChange,
  /** Filas con lat/lng de stagiados para pins a zoom bajo */
  onPendingAddLotsChange,
  lotBuildingDisplayMode = 'hidden',
  onLotBuildingDisplayModeChange,
  lotBuildingStatus = 'idle',
  /** Click en parcela del mapa que pertenece a la lista activa: { lotId, lot, ts } */
  mapLotClickRequest = null,
  /** Lote de la lista bajo el cursor del mapa. Marca la card del detalle. */
  mapHoveredLotId = null,
  /** Hover de una card del detalle: `{ lotId, item }` o null. */
  onListRecordHover = null,
  /** true mientras el detalle (propio o compartido) muestra cards, no el índice. */
  onListDetailHoverActive = null,
  /** Click en parcela durante modo agregar (pick): { lotId, ts } */
  mapAddPickRequest = null,
  mapGeorefPickRequest = null,
  mapPinPlaceRequest = null,
  /**
   * Notifica al mapa el modo agregar:
   * `{ active, showTooltip, stagedLotIds }` — `active` intercepta clicks;
   * `showTooltip` solo en tab "Desde el mapa".
   */
  onListAddPickModeChange,
  parcelFilters = null,
  /** Filtros `cad-list-v1` del header cuando hay una lista CAD abierta. */
  cadParcelFilters = null,
  onCadFilterValueChange,
  onFilterScopeContextChange,
  /** Pedido one-shot para abrir un detalle: `{ listId, listName, ts }`. */
  openListDetailRequest = null,
  /**
   * Pedido one-shot para abrir un showroom in-panel (desde Registros):
   * `{ slug, parcelId, parcelLabel, project, ts }`.
   */
  openCadDetailRequest = null,
  /**
   * Abierto desde otro panel (Registros → lote CAD, lead → lista / CAD):
   * Volver cierra lo abierto y reabre el origen. Ver
   * `resolveListsPanelReturnOrigin` (`Map/utils/listsPanelReturnOrigin.js`).
   */
  onReturnToOrigin,
  /** Contexto del volver: `Registros`, `Lead: Ana`. */
  returnOriginLabel = 'Registros',
  /**
   * Snapshot de cámara / catastro **antes** de encuadrar el CAD.
   * `{ open: true | false }`
   */
  onCadViewChange,
  /** Overlay GeoJSON sobre el mapa país. Ver CadInListsDetail. */
  onCadOverlayChange,
  /** Detalle de lista CAD abierto: ocultar pines (no el catastro). */
  onCadListOpenChange,
  /** Sacar del mapa país los lotes de una lista CAD que ya no existe. */
  onCadOwnerOverlayInvalidate,
  /** Tras Referenciar: el pin sigue el display_mode / lat lng nuevos. */
  onCadProjectPinSync,
  /** Encuadrar el loteo CAD (mapa país o plano canvas). */
  onCadFitInView,
  onQueryCadRefLotBbox,
}) {
  const {
    activeOwnerUserId,
    activeSpace,
    isViewingSharedSpace,
  } = useActiveSpace();

  const [lists, setLists] = useState([]);
  const [loadingLists, setLoadingLists] = useState(true);

  const [newName, setNewName] = useState('');
  const [newIsPublic, setNewIsPublic] = useState(false);
  const [creating, setCreating] = useState(false);
  const [showCreateListForm, setShowCreateListForm] = useState(false);
  /** Alta de lista: 'manual' (la de siempre) o 'cad' (desde un plano). */
  const [newListKind, setNewListKind] = useState('manual');
  /** Un .dxf elegido o ya parseado bloquea volver a «Lista vacía». */
  const [cadWorkLocksEmptyList, setCadWorkLocksEmptyList] = useState(false);

  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState('');
  const [editIsPublic, setEditIsPublic] = useState(false);
  const [savingId, setSavingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [deletingCadSlug, setDeletingCadSlug] = useState(null);
  const [editingCadSlug, setEditingCadSlug] = useState(null);
  const [cadEditName, setCadEditName] = useState('');
  const [savingCadSlug, setSavingCadSlug] = useState(null);

  const [listSearch, setListSearch] = useState('');
  const [visibilityFilter, setVisibilityFilter] = useState('all'); // all | public | private
  const [myListsSectionExpanded, setMyListsSectionExpanded] = useState(true);
  const [sharedListsSectionExpanded, setSharedListsSectionExpanded] = useState(true);

  const [viewMode, setViewMode] = useState(() => {
    if (openCadDetailRequest?.slug) return 'cad';
    if (openListDetailRequest?.listId) return 'detail';
    return 'lists';
  }); // lists | detail | sharedDetail | cad
  const [selectedList, setSelectedList] = useState(() => (
    openListDetailRequest?.listId
      ? { id: openListDetailRequest.listId, name: openListDetailRequest.listName || 'Lista' }
      : null
  ));
  const [selectedCadProject, setSelectedCadProject] = useState(() => {
    const req = openCadDetailRequest;
    if (!req?.slug) return null;
    return req.project || { slug: req.slug, name: req.projectName || req.slug };
  });
  const [lots, setLots] = useState([]);
  const [loadingLots, setLoadingLots] = useState(() => Boolean(openListDetailRequest?.listId));
  const [lotsSearch, setLotsSearch] = useState('');
  const [listSortBy, setListSortBy] = useState(DEFAULT_LIST_SORT);
  // Vista por registro (default) / dos por fila / agrupada por lote. Misma preferencia que Registros.
  const {
    effectiveViewMode: recordViewMode,
    setViewMode: setRecordViewMode,
    canUseGrid: canUseRecordGrid,
    isGrouped: isRecordGrouped,
  } = useRecordViewMode();
  const [listCommercialFilters, setListCommercialFilters] = useState(DEFAULT_COMMERCIAL_FILTERS);
  const [previewListId, setPreviewListId] = useState(
    () => openListDetailRequest?.listId || null,
  );
  const [previewLoadingId, setPreviewLoadingId] = useState(null);
  const [updatingUnitId, setUpdatingUnitId] = useState(null);
  const [expandedLotIds, setExpandedLotIds] = useState(() => new Set());

  const [previewSharedListId, setPreviewSharedListId] = useState(null);
  const [previewSharedLoadingId, setPreviewSharedLoadingId] = useState(null);
  const [previewCadSlug, setPreviewCadSlug] = useState(null);
  const [previewCadLoadingSlug, setPreviewCadLoadingSlug] = useState(null);

  const [addMode, setAddMode] = useState(false);
  const [addStep, setAddStep] = useState('pick');
  const [addSourceTab, setAddSourceTab] = useState('records');
  const [addStaging, setAddStaging] = useState(createEmptyStaging);
  const [addExpandedLotIds, setAddExpandedLotIds] = useState(() => new Set());
  /** Lote clickeado en el mapa cuya info todavía se está resolviendo. */
  const [resolvingMapPickLotId, setResolvingMapPickLotId] = useState(null);
  const [allLots, setAllLots] = useState([]);
  const [loadingAllLots, setLoadingAllLots] = useState(false);
  const [addSearch, setAddSearch] = useState('');

  const [rowMenuAnchor, setRowMenuAnchor] = useState(null);
  const [rowMenuId, setRowMenuId] = useState(null);
  const [detailMenuAnchor, setDetailMenuAnchor] = useState(null);

  const [sharedRowMenuAnchor, setSharedRowMenuAnchor] = useState(null);
  const [sharedRowMenuId, setSharedRowMenuId] = useState(null);

  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [shareDialogList, setShareDialogList] = useState(null);
  const [activityDialogTarget, setActivityDialogTarget] = useState(null);
  const [inquiryDialogTarget, setInquiryDialogTarget] = useState(null);

  // 'all' es el tab por defecto: muestra mis listas + compartidas conmigo unificadas
  const [listsPanelTab, setListsPanelTab] = useState('all'); // all | mine | shared
  const [sharedWithMeLists, setSharedWithMeLists] = useState([]);
  const [loadingSharedWithMe, setLoadingSharedWithMe] = useState(false);
  const [guestShareBusyKey, setGuestShareBusyKey] = useState(null);
  const {
    loading: loadingCadCollections,
    owned: cadOwnedProjects,
    shared: cadSharedProjects,
    reload: reloadCadCollections,
  } = useCadCollectionsForLists({ ownerUserId: activeOwnerUserId });
  const spaceOwnerIdForFetch = activeOwnerUserId ? String(activeOwnerUserId).trim() : '';
  const withSpaceOwner = useCallback((opts = {}) => (
    spaceOwnerIdForFetch ? { ...opts, ownerUserId: spaceOwnerIdForFetch } : opts
  ), [spaceOwnerIdForFetch]);
  // F2: escrituras aditivas del invitado (crear lista, renombrar, agregar) con
  // `owner_user_id` en el body. En el propio espacio → null (request igual que siempre).
  const listWriteOwnerUserId = isViewingSharedSpace && spaceOwnerIdForFetch
    ? spaceOwnerIdForFetch
    : null;
  const [cadShareProject, setCadShareProject] = useState(null);
  const [cadSharePatch, setCadSharePatch] = useState(null);
  const [cadParcelCounts, setCadParcelCounts] = useState(null);
  const [cadParcelLabel, setCadParcelLabel] = useState(() => {
    const label = typeof openCadDetailRequest?.parcelLabel === 'string'
      ? openCadDetailRequest.parcelLabel.trim()
      : '';
    return label || null;
  });
  const [cadGeorefOpen, setCadGeorefOpen] = useState(false);
  const [cadAutoGeoref, setCadAutoGeoref] = useState(false);
  /** `area_m2` de la lista abierta: recorta las bandas de superficie del panel. */
  const [cadAreaExtent, setCadAreaExtent] = useState(null);
  const [cadInitialParcelId, setCadInitialParcelId] = useState(() => {
    const id = openCadDetailRequest?.parcelId;
    return id == null ? null : String(id).trim() || null;
  });
  const cadDeselectParcelRef = useRef(null);
  const cadStartGeorefRef = useRef(null);
  const cadStartPinsRef = useRef(null);

  const [selectedSharedListMeta, setSelectedSharedListMeta] = useState(null);
  const [sharedDetailLots, setSharedDetailLots] = useState([]);
  const [sharedDetailCounts, setSharedDetailCounts] = useState({ totalLots: 0, totalUnits: 0 });
  const [sharedDetailListName, setSharedDetailListName] = useState('');
  const [loadingSharedDetail, setLoadingSharedDetail] = useState(false);
  const [sharedDetailError, setSharedDetailError] = useState('');
  const [sharedLotsSearch, setSharedLotsSearch] = useState('');
  /** Detalle de unidad abierto desde lista (reemplaza el panel, como SharedListPage) */
  const [sharedLotDock, setSharedLotDock] = useState(null);
  const [sharedDockDetail, setSharedDockDetail] = useState(null);
  const [sharedDockDetailLoading, setSharedDockDetailLoading] = useState(false);
  const [mineListLotDock, setMineListLotDock] = useState(null);
  const previewRequestSeqRef = useRef(0);
  const previewSharedRequestSeqRef = useRef(0);
  const previewCadRequestSeqRef = useRef(0);
  const viewModeRef = useRef(viewMode);
  viewModeRef.current = viewMode;
  /** Cache catastral por lot_id: re-abrir un lote no vuelve a pegarle a getalllots. */
  const catastralCacheRef = useRef(new Map());
  /** Cache de detalle compartido por user_lot_id o lot:lot_id. */
  const sharedDockDetailCacheRef = useRef(new Map());
  /** Lote actualmente abierto en el dock (para descartar merges tardíos). */
  const activeDockKeyRef = useRef(null);
  const pendingActivityOpenRef = useRef(null);
  const [visibleCardCount, setVisibleCardCount] = useState(LIST_RENDER_CHUNK);
  const filteredLotsRef = useRef([]);

  const emitHighlightLots = useCallback((rows) => {
    const safeRows = Array.isArray(rows) ? rows : [];
    onHighlightLotIdsChange?.(safeRows.map((lot) => lot?.lot_id).filter(Boolean));
    onHighlightLotsChange?.(safeRows);
  }, [onHighlightLotIdsChange, onHighlightLotsChange]);

  useEffect(() => {
    if (viewMode === 'detail' && selectedList?.id) {
      onFilterScopeContextChange?.({
        listId: selectedList.id,
        listName: selectedList.name,
      });
      return undefined;
    }
    // Lista CAD: mismo canal, `kind` distinto. El alcance catastral no aplica.
    if (viewMode === 'cad' && selectedCadProject) {
      onFilterScopeContextChange?.({
        kind: 'cad',
        projectId: getCadProjectId(selectedCadProject) || selectedCadProject.slug || null,
        slug: selectedCadProject.slug || '',
        listName: selectedCadProject.name || '',
        areaExtent: cadAreaExtent,
      });
      return undefined;
    }
    onFilterScopeContextChange?.(null);
    return undefined;
  }, [
    viewMode,
    selectedList?.id,
    selectedList?.name,
    selectedCadProject,
    cadAreaExtent,
    onFilterScopeContextChange,
  ]);

  useEffect(() => () => onFilterScopeContextChange?.(null), [onFilterScopeContextChange]);

  const emitPendingAddLots = useCallback((rows) => {
    const safeRows = Array.isArray(rows) ? rows : [];
    onPendingAddLotIdsChange?.(safeRows.map((lot) => String(lot?.lot_id)).filter(Boolean));
    onPendingAddLotsChange?.(safeRows);
  }, [onPendingAddLotIdsChange, onPendingAddLotsChange]);

  const handleCadRefLotHighlight = useCallback((row) => {
    if (row?.lot_id) emitHighlightLots([row]);
    else emitHighlightLots([]);
  }, [emitHighlightLots]);

  const clearCadastralIndexPreview = useCallback(() => {
    previewRequestSeqRef.current += 1;
    previewSharedRequestSeqRef.current += 1;
    setPreviewListId(null);
    setPreviewLoadingId(null);
    setPreviewSharedListId(null);
    setPreviewSharedLoadingId(null);
    emitPendingAddLots([]);
    emitHighlightLots([]);
  }, [emitHighlightLots, emitPendingAddLots]);

  const clearCadIndexPreview = useCallback(() => {
    previewCadRequestSeqRef.current += 1;
    setPreviewCadSlug(null);
    setPreviewCadLoadingSlug(null);
    if (viewModeRef.current !== 'cad') {
      onCadOverlayChange?.({ enabled: false });
    }
  }, [onCadOverlayChange]);

  const clearMapListPreview = useCallback(() => {
    clearCadastralIndexPreview();
    clearCadIndexPreview();
  }, [clearCadIndexPreview, clearCadastralIndexPreview]);

  const openRowMenu = (e, row) => {
    e.stopPropagation();
    setRowMenuAnchor(e.currentTarget);
    setRowMenuId(row.id);
  };

  const closeRowMenu = () => {
    setRowMenuAnchor(null);
    setRowMenuId(null);
  };

  const openSharedRowMenu = (e, row) => {
    e.stopPropagation();
    setSharedRowMenuAnchor(e.currentTarget);
    setSharedRowMenuId(row?.list_id || null);
  };

  const closeSharedRowMenu = () => {
    setSharedRowMenuAnchor(null);
    setSharedRowMenuId(null);
  };

  const openDetailMenu = (e) => {
    e.stopPropagation();
    setDetailMenuAnchor(e.currentTarget);
  };

  const closeDetailMenu = () => {
    setDetailMenuAnchor(null);
  };

  const openShareDialog = (row) => {
    if (!row?.id) return;
    let next = row;
    // Si estamos en el detalle de esa lista, preferir conteos vivos de los lotes cargados.
    if (selectedList?.id === row.id) {
      const fromDetail = getListCountsFromGroupedLots(lots);
      next = {
        ...row,
        lot_count: fromDetail.lot_count,
        item_count: fromDetail.item_count,
        lot_content_count: fromDetail.lot_content_count,
      };
    }
    // Evita aria-hidden sobre el trigger del menú que aún retiene foco.
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
    setShareDialogList(next);
    window.setTimeout(() => {
      setShareDialogOpen(true);
    }, 0);
  };

  const closeShareDialog = () => {
    setShareDialogOpen(false);
    setShareDialogList(null);
  };

  // Cambio de espacio: el diálogo de compartir es del espacio anterior.
  useEffect(() => {
    setShareDialogOpen(false);
    setShareDialogList(null);
    // F3-CAD: el share CAD tampoco cruza de espacio (ni su patch local).
    setCadShareProject(null);
    setCadSharePatch(null);
  }, [activeOwnerUserId]);

  const openCadShare = (project) => {
    // Espacio ajeno (F3-CAD): el diálogo va con `ownerUserId` y exige `can_share`.
    if (!project?.slug) return;
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
    window.setTimeout(() => {
      setCadShareProject(project);
    }, 0);
  };

  const closeCadShare = () => {
    setCadShareProject(null);
  };

  const refreshListsAbortRef = useRef(null);
  const refreshSharedAbortRef = useRef(null);

  const refreshLists = useCallback(async () => {
    refreshListsAbortRef.current?.abort();
    const controller = new AbortController();
    refreshListsAbortRef.current = controller;

    setLoadingLists(true);
    try {
      const spaceOwnerId = activeOwnerUserId ? String(activeOwnerUserId).trim() : '';
      const listsRes = await getUserLotLists({
        signal: controller.signal,
        ...(spaceOwnerId ? { ownerUserId: spaceOwnerId } : {}),
      });
      if (controller.signal.aborted) return;
      const baseLists = Array.isArray(listsRes?.data) ? listsRes.data : [];
      setLists(withListCountsFromApi(baseLists));
    } catch (e) {
      if (e?.name === 'AbortError') return;
      console.error(e);
      setLists([]);
    } finally {
      if (!controller.signal.aborted) setLoadingLists(false);
    }
  }, [activeOwnerUserId]);

  const refreshSharedWithMeLists = useCallback(async () => {
    refreshSharedAbortRef.current?.abort();
    // Espacio ajeno: solo el espacio del owner; compartidas conmigo son del JWT.
    if (isViewingSharedSpace) {
      setSharedWithMeLists([]);
      setLoadingSharedWithMe(false);
      return;
    }
    const controller = new AbortController();
    refreshSharedAbortRef.current = controller;

    setLoadingSharedWithMe(true);
    try {
      const res = await getSharedWithMeLists(controller.signal);
      if (res._httpStatus === 401) {
        setSharedWithMeLists([]);
        return;
      }
      if (Array.isArray(res?.data)) setSharedWithMeLists(res.data);
      else setSharedWithMeLists([]);
    } catch (e) {
      if (e?.name === 'AbortError') return;
      console.error(e);
      setSharedWithMeLists([]);
    } finally {
      if (!controller.signal.aborted) setLoadingSharedWithMe(false);
    }
  }, [isViewingSharedSpace]);

  const refreshSharedWithMePanel = useCallback(async () => {
    await Promise.all([
      refreshSharedWithMeLists(),
      reloadCadCollections(),
    ]);
  }, [refreshSharedWithMeLists, reloadCadCollections]);

  const patchListCountsFromDetailLots = useCallback((listId, detailLots) => {
    if (!listId) return;
    const counts = getListCountsFromGroupedLots(detailLots);
    setLists((prev) =>
      prev.map((row) =>
        row?.id === listId
          ? { ...row, ...counts }
          : row
      )
    );
  }, []);

  const refreshSelectedListLots = useCallback(
    async (listId) => {
      if (!listId) return;
      const isDetailOfThisList = viewMode === 'detail' && selectedList?.id === listId;
      const shouldSyncMapHighlights = previewListId === listId && !isDetailOfThisList;
      const requestId = ++previewRequestSeqRef.current;
      setLoadingLots(true);
      try {
        const res = await getMyLots(withSpaceOwner({
          listId,
          includeFilterAttributes: true,
          includePrivateAttributes: true,
          includeCommercialSummary: true,
          // Solo si el refresco es del detalle abierto (cards); no en preview del mapa.
          includeLotContentSummary: isDetailOfThisList,
        }));
        if (requestId !== previewRequestSeqRef.current) return;
        const nextLots = normalizeLotsData(res?.data);
        setLots(nextLots);
        patchListCountsFromDetailLots(listId, nextLots);
        if (shouldSyncMapHighlights) {
          emitHighlightLots(nextLots);
        }
      } catch (e) {
        if (requestId !== previewRequestSeqRef.current) return;
        console.error('Error refreshing lots for list:', e);
        setLots([]);
        patchListCountsFromDetailLots(listId, []);
        if (shouldSyncMapHighlights) emitHighlightLots([]);
      } finally {
        if (requestId === previewRequestSeqRef.current) {
          setLoadingLots(false);
        }
      }
    },
    [emitHighlightLots, patchListCountsFromDetailLots, previewListId, selectedList?.id, viewMode, withSpaceOwner]
  );

  const openListDetail = async (row) => {
    previewSharedRequestSeqRef.current += 1;
    setPreviewSharedListId(null);
    setPreviewSharedLoadingId(null);

    const requestId = ++previewRequestSeqRef.current;
    setSelectedList(row);
    setViewMode('detail');
    setPreviewListId(row.id);
    setLotsSearch('');
    setLoadingLots(true);
    emitHighlightLots([]);
    try {
      const res = await getMyLots(withSpaceOwner({
        listId: row.id,
        includeFilterAttributes: true,
        includePrivateAttributes: true,
        // Resumen comercial (dueño / editor del espacio): cards + UF del detalle.
        includeCommercialSummary: true,
        includeLotContentSummary: true,
      }));
      if (requestId !== previewRequestSeqRef.current) return;
      const nextLots = normalizeLotsData(res?.data);
      setLots(nextLots);
      setExpandedLotIds(new Set());
      patchListCountsFromDetailLots(row.id, nextLots);
    } catch (e) {
      if (requestId !== previewRequestSeqRef.current) return;
      console.error('Error fetching lots for list:', e);
      setLots([]);
      emitHighlightLots([]);
    } finally {
      if (requestId === previewRequestSeqRef.current) {
        setLoadingLots(false);
      }
    }
  };

  const lastOpenListDetailTsRef = useRef(0);
  useEffect(() => {
    const req = openListDetailRequest;
    if (!req?.listId || !req.ts || req.ts === lastOpenListDetailTsRef.current) return;
    lastOpenListDetailTsRef.current = req.ts;
    const row = lists.find((item) => String(item?.id) === String(req.listId))
      || { id: req.listId, name: req.listName || 'Lista' };
    void openListDetail(row);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- openListDetail se recrea cada render; el ts evita reabrir el mismo pedido.
  }, [openListDetailRequest, lists]);

  // Resumen comercial del detalle de lista: tras `terrio:commercial-changed`
  // (debounce 400 ms) se vuelve a pedir la lista con el include y solo se pegan
  // los bloques comerciales (sin loading ni reemplazar lotes: no se pierde
  // scroll, selección ni expandido). Nunca en share ni mapa público.
  const commercialRefreshRef = useRef(null);
  commercialRefreshRef.current = async () => {
    try {
      if (viewMode === 'detail' && selectedList?.id) {
        const listId = selectedList.id;
        const res = await getMyLots(withSpaceOwner({
          listId,
          includeFilterAttributes: true,
          includePrivateAttributes: true,
          includeCommercialSummary: true,
        }));
        if (!Array.isArray(res?.data)) return;
        setLots((prev) => (selectedListIdRef.current === listId ? mergeListCommercial(prev, res.data) : prev));
        return;
      }
      if (viewMode === 'sharedDetail' && selectedSharedListMeta?.can_edit === true) {
        const token = getShareTokenFromRow(selectedSharedListMeta);
        if (!token) return;
        const res = await getSharedList(token, { format: 'grouped', includeCommercialSummary: true });
        if (!Array.isArray(res?.data?.lots)) return;
        setSharedDetailLots((prev) => mergeListCommercial(prev, res.data.lots));
      }
    } catch (_) { /* se mantiene el último */ }
  };
  const selectedListIdRef = useRef(null);
  selectedListIdRef.current = viewMode === 'detail' ? selectedList?.id || null : null;
  useEffect(() => {
    let timer = null;
    const off = onCommercialChanged(() => {
      clearTimeout(timer);
      timer = setTimeout(() => { commercialRefreshRef.current?.(); }, 400);
    });
    return () => {
      clearTimeout(timer);
      off();
    };
  }, []);

  const syncListMembership = useCallback(
    async ({ listId, lotIds, userLotIds, successMessage, updatingKey }) => {
      // Espacio ajeno (F2): el invitado solo agrega (la selección incluye todo lo
      // actual); quitar sigue oculto y los handlers de quitar cortan antes.
      if (!listId) return false;
      setUpdatingUnitId(updatingKey || `list:${listId}`);
      try {
        const res = await updateUserLotList({
          list_id: listId,
          lot_ids: lotIds,
          user_lot_ids: userLotIds,
          ownerUserId: listWriteOwnerUserId,
        });
        if (listWriteOwnerUserId && isSpaceGuestListRemovalError(res)) {
          // El dueño cambió la lista mientras tanto: refrescar, no reintentar.
          notifyToast({ title: 'Lista actualizada', text: SPACE_GUEST_LIST_CHANGED_MESSAGE, icon: 'warning' });
          await refreshLists();
          await refreshSelectedListLots(listId);
          return false;
        }
        const spaceError = res?.error
          // 404 = ya no es miembro del espacio → "sin permiso".
          ? resolveSpaceGuestWriteError(res._httpStatus, listWriteOwnerUserId, { notFoundAsForbidden: true })
          : null;
        if (spaceError) {
          notifyToast({ title: 'Error', text: spaceError, icon: 'error' });
          return false;
        }
        if (res?.error) throw new Error(res.error);
        if (successMessage) toast.success(successMessage);
        await refreshLists();
        await refreshSelectedListLots(listId);
        return true;
      } catch (e) {
        console.error('Error syncing list membership:', e);
        notifyToast({ title: 'Error', text: 'No se pudo actualizar la lista.', icon: 'error' });
        return false;
      } finally {
        setUpdatingUnitId(null);
      }
    },
    [listWriteOwnerUserId, refreshLists, refreshSelectedListLots]
  );

  const updateUnitMembership = useCallback(
    async ({ unit, nextListIds, successMessage }) => {
      if (!unit?.id || !unit?.lot_id) return false;
      const normalized = Array.isArray(nextListIds) ? nextListIds.filter(Boolean) : [];
      setUpdatingUnitId(unit.id);
      try {
        const res = await createUserLot({
          user_lot_id: unit.id,
          lot_id: unit.lot_id,
          list_ids: normalized,
        });
        if (!res?._httpOk || res.error) {
          throw new Error(res?.error || `El backend respondió ${res?._httpStatus}`);
        }
        if (successMessage) {
          toast.success(successMessage);
        }
        await refreshLists();
        await refreshSelectedListLots(selectedList?.id);
        return true;
      } catch (e) {
        console.error('Error updating unit membership:', e);
        notifyToast({ title: 'Error', text: 'No se pudo actualizar la lista de la unidad.', icon: 'error' });
        return false;
      } finally {
        setUpdatingUnitId(null);
      }
    },
    [refreshLists, refreshSelectedListLots, selectedList?.id]
  );

  const handleRemoveFromCurrentList = async (unit) => {
    // Espacio ajeno: quitar de la lista es destructivo → solo el dueño.
    if (isViewingSharedSpace) return;
    const listId = selectedList?.id;
    if (!listId || !unit?.id) return;
    const currentListIds = getUnitListIds(unit);
    const next = currentListIds.filter((id) => id !== listId);

    const listName = selectedList?.name || 'esta lista';
    const unitLabel = getUnitDisplayTitle(unit);
    const willBeUnlisted = next.length === 0;
    const confirm = await confirmDialog({
      title: '¿Eliminar de la lista?',
      description: willBeUnlisted
        ? `Se eliminará definitivamente "${unitLabel}" de "${listName}". La unidad quedará sin listas asignadas.`
        : `Se eliminará definitivamente "${unitLabel}" vinculado a esta lista.`,
      confirmLabel: 'Eliminar',
      cancelLabel: 'Cancelar',
      variant: 'danger',
    });
    if (!confirm.isConfirmed) return;

    await updateUnitMembership({
      unit,
      nextListIds: next,
      successMessage: next.length ? 'Unidad quitada de la lista' : 'Unidad desvinculada de todas las listas',
    });
  };

  const handleRemoveLotFromCurrentList = async (lot) => {
    if (isViewingSharedSpace) return;
    const listId = selectedList?.id;
    if (!listId || !lot?.lot_id) return;

    const units = getDisplayUnits(lot);
    const isDirect = units.length === 0;
    const lotLabel = lot.lot_name || lot.lot_id || 'este lote';
    const listName = selectedList?.name || 'esta lista';

    const confirm = await confirmDialog({
      title: '¿Eliminar de la lista?',
      description: isDirect
        ? `Se eliminará definitivamente el lote "${lotLabel}" de "${listName}".`
        : units.length === 1
          ? `Se eliminará definitivamente "${getUnitDisplayTitle(units[0])}" vinculado a esta lista.`
          : `Se eliminarán ${units.length} unidades funcionales del lote "${lotLabel}" de "${listName}".`,
      confirmLabel: 'Eliminar',
      cancelLabel: 'Cancelar',
      variant: 'danger',
    });
    if (!confirm.isConfirmed) return;

    const { lot_ids, user_lot_ids } = extractListMembership(lots);

    if (isDirect) {
      await syncListMembership({
        listId,
        lotIds: lot_ids.filter((id) => id !== lot.lot_id),
        userLotIds: user_lot_ids,
        successMessage: 'Lote quitado de la lista',
      });
      return;
    }

    const unitIdsToRemove = new Set(units.map((unit) => unit.id).filter(Boolean));
    await syncListMembership({
      listId,
      lotIds: lot_ids,
      userLotIds: user_lot_ids.filter((id) => !unitIdsToRemove.has(id)),
      successMessage: units.length === 1 ? 'Unidad quitada de la lista' : 'Lote quitado de la lista',
    });
  };

  const toggleLotExpanded = (e, lotId) => {
    e.stopPropagation();
    setExpandedLotIds((prev) => {
      const next = new Set(prev);
      if (next.has(lotId)) next.delete(lotId);
      else next.add(lotId);
      return next;
    });
  };

  const openAddLotsInline = async () => {
    if (!selectedList?.id) return;
    setAddMode(true);
    setAddStep('pick');
    setAddSourceTab('records');
    setAddStaging(createEmptyStaging());
    setAddExpandedLotIds(new Set());
    setAddSearch('');
    setLoadingAllLots(true);
    try {
      const res = await getMyLots(withSpaceOwner());
      const nextLots = normalizeLotsData(res?.data);
      setAllLots(nextLots);
    } catch (e) {
      console.error('Error fetching all lots:', e);
      setAllLots([]);
    } finally {
      setLoadingAllLots(false);
    }
  };

  const closeAddMode = useCallback((options = {}) => {
    const shouldRestoreHighlights = options?.restoreHighlights !== false;
    setAddMode(false);
    setAddStep('pick');
    setAddSourceTab('records');
    setAddStaging(createEmptyStaging());
    setAddExpandedLotIds(new Set());
    setAddSearch('');
    setAllLots([]);
    setLoadingAllLots(false);
    emitPendingAddLots([]);
    if (shouldRestoreHighlights && viewMode === 'detail') {
      emitHighlightLots(filteredLotsRef.current);
    }
  }, [viewMode, emitHighlightLots, emitPendingAddLots]);

  const currentListDirectLotIds = useMemo(
    () => new Set(lots.filter((lot) => isDirectLot(lot)).map((lot) => lot.lot_id).filter(Boolean)),
    [lots]
  );

  const getAddableUnitsForLot = useCallback((lot, listId) => {
    if (!lot || !listId) return [];
    return getDisplayUnits(lot).filter((unit) => !getUnitListIds(unit).includes(listId));
  }, []);

  const lotHasUnitsInList = useCallback((lot, listId) => {
    if (!lot || !listId) return false;
    return getDisplayUnits(lot).some((unit) => getUnitListIds(unit).includes(listId));
  }, []);

  const addableRecordsLots = useMemo(() => {
    const listId = selectedList?.id;
    if (!listId) return [];
    const term = addSearch.trim().toLowerCase();

    return allLots.reduce((acc, lot) => {
      if (!lot?.lot_id) return acc;

      if (isDirectLot(lot)) {
        if (currentListDirectLotIds.has(lot.lot_id)) return acc;
        if (term && !lotMatchesSearch(lot, term)) return acc;
        acc.push({ lot, addableUnits: [] });
        return acc;
      }

      let addableUnits = getAddableUnitsForLot(lot, listId);
      if (!addableUnits.length) return acc;

      if (term) {
        const lotMatch = lotMatchesSearch(lot, term);
        if (!lotMatch) {
          addableUnits = addableUnits.filter((unit) => {
            const unitHaystack = [
              getUnitDisplayTitle(unit),
              unit.description,
              unit.lot_name,
            ]
              .filter(Boolean)
              .join(' ')
              .toLowerCase();
            return unitHaystack.includes(term);
          });
          if (!addableUnits.length) return acc;
        }
      }

      acc.push({ lot, addableUnits });
      return acc;
    }, []);
  }, [allLots, addSearch, selectedList?.id, currentListDirectLotIds, getAddableUnitsForLot]);

  const stagedCount = useMemo(() => {
    const listId = selectedList?.id;
    let count = addStaging.unitIds.size;
    addStaging.directLotIds.forEach((lotId) => {
      const lot = addStaging.lotMeta[lotId] || allLots.find((row) => row.lot_id === lotId);
      // Ignorar parcela stagiada si el lote ya tiene UF en la lista.
      if (lot && lotHasUnitsInList(lot, listId)) return;
      count += 1;
    });
    Object.values(addStaging.lotUnitPicks).forEach((pick) => {
      count += pick.unitIds.size;
    });
    return count;
  }, [addStaging, allLots, lotHasUnitsInList, selectedList?.id]);

  const stagedMapLots = useMemo(() => {
    const listId = selectedList?.id;
    const rows = [];
    addStaging.directLotIds.forEach((lotId) => {
      const lot = addStaging.lotMeta[lotId] || allLots.find((row) => row.lot_id === lotId);
      if (!lot) return;
      rows.push({
        lot,
        addableUnits: listId ? getAddableUnitsForLot(lot, listId) : [],
      });
    });
    Object.values(addStaging.lotUnitPicks).forEach(({ lot, addableUnits }) => {
      if (!lot || rows.some((row) => row.lot.lot_id === lot.lot_id)) return;
      rows.push({ lot, addableUnits: addableUnits || [] });
    });
    return rows;
  }, [addStaging, allLots, selectedList?.id, getAddableUnitsForLot]);

  const reviewGroups = useMemo(() => {
    const listId = selectedList?.id;
    const sources = addStaging.itemSources || {};
    const emptyBucket = () => ({ directLots: [], lotPicks: [], standaloneUnits: [], count: 0 });
    const records = emptyBucket();
    const map = emptyBucket();

    const bucketFor = (key) => (sources[key] === 'map' ? map : records);

    addStaging.directLotIds.forEach((lotId) => {
      const lot = addStaging.lotMeta[lotId] || allLots.find((row) => row.lot_id === lotId);
      if (!lot) return;
      if (lotHasUnitsInList(lot, listId)) return;
      const bucket = bucketFor(lotId);
      bucket.directLots.push(lot);
      bucket.count += 1;
    });

    // Borradores vacíos (parcela ya en lista, esperando UF) no van a revisión.
    Object.entries(addStaging.lotUnitPicks)
      .filter(([, pick]) => pick.unitIds?.size > 0)
      .forEach(([lotId, pick]) => {
        const bucket = bucketFor(lotId);
        bucket.lotPicks.push({
          lot: pick.lot,
          lotId,
          addableUnits: pick.addableUnits,
          selectedUnitIds: pick.unitIds,
        });
        bucket.count += 1;
      });

    const coveredUnitIds = new Set();
    [...records.lotPicks, ...map.lotPicks].forEach(({ selectedUnitIds }) => {
      selectedUnitIds.forEach((id) => coveredUnitIds.add(id));
    });

    addStaging.unitIds.forEach((unitId) => {
      if (coveredUnitIds.has(unitId)) return;
      for (const lot of allLots) {
        const unit = getDisplayUnits(lot).find((row) => row.id === unitId);
        if (unit) {
          const bucket = bucketFor(`unit:${unitId}`);
          bucket.standaloneUnits.push({ lot, unit });
          bucket.count += 1;
          break;
        }
      }
    });

    return {
      records,
      map,
      // Compat: flat views (footer / legacy)
      directLots: [...records.directLots, ...map.directLots],
      lotPicks: [...records.lotPicks, ...map.lotPicks],
      standaloneUnits: [...records.standaloneUnits, ...map.standaloneUnits],
      hasInvalidLotPicks: false,
    };
  }, [addStaging, allLots, lotHasUnitsInList, selectedList?.id]);

  const isRecordLotSelected = useCallback((lot, addableUnits) => {
    const listId = selectedList?.id;
    if (lotHasUnitsInList(lot, listId) || currentListDirectLotIds.has(lot.lot_id)) {
      const pick = addStaging.lotUnitPicks[lot.lot_id];
      if (pick) return addableUnits.length > 0 && pick.unitIds.size === addableUnits.length;
      return addableUnits.length > 0 && addableUnits.every((unit) => addStaging.unitIds.has(unit.id));
    }
    if (addStaging.directLotIds.has(lot.lot_id)) return true;
    if (isDirectLot(lot)) return false;
    const pick = addStaging.lotUnitPicks[lot.lot_id];
    if (pick) return addableUnits.length > 0 && pick.unitIds.size === addableUnits.length;
    return addableUnits.length > 0 && addableUnits.every((unit) => addStaging.unitIds.has(unit.id));
  }, [addStaging, currentListDirectLotIds, lotHasUnitsInList, selectedList?.id]);

  const isRecordLotPartial = useCallback((lot, addableUnits) => {
    const listId = selectedList?.id;
    if (
      (!lotHasUnitsInList(lot, listId) && !currentListDirectLotIds.has(lot.lot_id))
      && (addStaging.directLotIds.has(lot.lot_id) || isDirectLot(lot))
    ) {
      return false;
    }
    const pick = addStaging.lotUnitPicks[lot.lot_id];
    const selectedCount = pick
      ? pick.unitIds.size
      : addableUnits.filter((unit) => addStaging.unitIds.has(unit.id)).length;
    return selectedCount > 0 && selectedCount < addableUnits.length;
  }, [addStaging, currentListDirectLotIds, lotHasUnitsInList, selectedList?.id]);

  const toggleAddLotExpanded = (e, lotId) => {
    e.stopPropagation();
    setAddExpandedLotIds((prev) => {
      const next = new Set(prev);
      if (next.has(lotId)) next.delete(lotId);
      else next.add(lotId);
      return next;
    });
  };

  const stageDirectLot = useCallback((lot, source) => {
    if (!lot?.lot_id) return;
    const listId = selectedList?.id;
    // Si ya hay UF de este lote en la lista, no stagiar la parcela: solo se eligen unidades.
    if (listId && lotHasUnitsInList(lot, listId)) return;
    const resolvedSource = source || addSourceTab;
    const rep = getLotRepresentativeLatLng(lot);
    const lotWithCoords = rep
      ? { ...lot, lat: lot.lat ?? rep.lat, lng: lot.lng ?? rep.lng }
      : lot;
    setAddStaging((prev) => {
      const next = cloneAddStaging(prev);
      next.directLotIds.add(lotWithCoords.lot_id);
      next.lotMeta[lotWithCoords.lot_id] = lotWithCoords;
      delete next.lotUnitPicks[lotWithCoords.lot_id];
      setStagingSource(next, lotWithCoords.lot_id, resolvedSource);
      return next;
    });
  }, [selectedList?.id, lotHasUnitsInList, addSourceTab]);

  const unstageDirectLot = useCallback((lotId) => {
    setAddStaging((prev) => {
      const next = cloneAddStaging(prev);
      next.directLotIds.delete(lotId);
      delete next.lotMeta[lotId];
      clearStagingSource(next, lotId);
      return next;
    });
  }, []);

  const unstageLotPick = useCallback((lotId) => {
    setAddStaging((prev) => {
      if (!prev.lotUnitPicks[lotId]) return prev;
      const next = cloneAddStaging(prev);
      delete next.lotUnitPicks[lotId];
      if (!next.directLotIds.has(lotId)) clearStagingSource(next, lotId);
      return next;
    });
  }, []);

  /** Abre selección de UF sin staging de parcela (p. ej. parcela ya en la lista). */
  const openUnitPickDraft = useCallback((lot, addableUnits, source) => {
    if (!lot?.lot_id) return;
    const resolvedSource = source || addSourceTab;
    setAddStaging((prev) => {
      const next = cloneAddStaging(prev);
      next.directLotIds.delete(lot.lot_id);
      delete next.lotMeta[lot.lot_id];
      next.lotUnitPicks[lot.lot_id] = {
        lot,
        addableUnits: Array.isArray(addableUnits) ? addableUnits : [],
        unitIds: new Set(),
      };
      setStagingSource(next, lot.lot_id, resolvedSource);
      return next;
    });
  }, [addSourceTab]);

  const stageLotWithUnits = useCallback((lot, unitIds, addableUnits) => {
    if (!lot?.lot_id || !unitIds?.length) return;
    setAddStaging((prev) => {
      const next = cloneAddStaging(prev);
      next.directLotIds.delete(lot.lot_id);
      delete next.lotMeta[lot.lot_id];
      unitIds.forEach((id) => next.unitIds.delete(id));
      next.lotUnitPicks[lot.lot_id] = {
        lot,
        addableUnits: Array.isArray(addableUnits) ? addableUnits : [],
        unitIds: new Set(unitIds),
      };
      setStagingSource(next, lot.lot_id, addSourceTab);
      return next;
    });
  }, [addSourceTab]);

  const toggleStagedUnit = useCallback((lot, unit, addableUnits) => {
    if (!unit?.id || !lot?.lot_id) return;
    const listId = selectedList?.id;
    const unitPickOnly =
      currentListDirectLotIds.has(lot.lot_id) || lotHasUnitsInList(lot, listId);
    setAddStaging((prev) => {
      const next = cloneAddStaging(prev);
      const existingPick = next.lotUnitPicks[lot.lot_id];
      const safeAddable = Array.isArray(addableUnits) ? addableUnits : [];

      if (existingPick) {
        const unitIds = new Set(existingPick.unitIds);
        if (unitIds.has(unit.id)) unitIds.delete(unit.id);
        else unitIds.add(unit.id);
        if (unitIds.size === 0) {
          delete next.lotUnitPicks[lot.lot_id];
          // Solo vuelve a parcela si el lote aún no está vinculado a la lista.
          if (!unitPickOnly) {
            next.directLotIds.add(lot.lot_id);
            next.lotMeta[lot.lot_id] = lot;
            setStagingSource(next, lot.lot_id, next.itemSources[lot.lot_id] || addSourceTab);
          } else {
            clearStagingSource(next, lot.lot_id);
          }
        } else {
          next.lotUnitPicks[lot.lot_id] = { ...existingPick, unitIds };
          setStagingSource(next, lot.lot_id, next.itemSources[lot.lot_id] || addSourceTab);
        }
        return next;
      }

      // Si estaba como parcela sola, al elegir una UF pasa a selección por unidades.
      next.directLotIds.delete(lot.lot_id);
      delete next.lotMeta[lot.lot_id];
      next.unitIds.delete(unit.id);
      next.lotUnitPicks[lot.lot_id] = {
        lot,
        addableUnits: safeAddable,
        unitIds: new Set([unit.id]),
      };
      setStagingSource(next, lot.lot_id, next.itemSources[lot.lot_id] || addSourceTab);
      return next;
    });
  }, [currentListDirectLotIds, lotHasUnitsInList, selectedList?.id, addSourceTab]);

  const handleToggleRecordLot = useCallback((lot, addableUnits) => {
    const lotId = lot?.lot_id;
    if (!lotId) return;

    const safeAddable = Array.isArray(addableUnits) ? addableUnits : [];
    const pick = addStaging.lotUnitPicks[lotId];
    const selectedUnitCount = pick
      ? pick.unitIds.size
      : safeAddable.filter((unit) => addStaging.unitIds.has(unit.id)).length;
    const hasUnitSelection = selectedUnitCount > 0;
    const isStaged = addStaging.directLotIds.has(lotId) || hasUnitSelection;
    const listId = selectedList?.id;
    const unitPickOnly =
      currentListDirectLotIds.has(lotId) || lotHasUnitsInList(lot, listId);

    // Lote ya vinculado (parcela o UF): el checkbox solo maneja UF pendientes.
    if (unitPickOnly) {
      if (hasUnitSelection) {
        unstageLotPick(lotId);
        unstageDirectLot(lotId);
        if (safeAddable.length) {
          setAddStaging((prev) => {
            const next = cloneAddStaging(prev);
            safeAddable.forEach((unit) => next.unitIds.delete(unit.id));
            return next;
          });
        }
        return;
      }
      if (!safeAddable.length) return;
      stageLotWithUnits(lot, safeAddable.map((unit) => unit.id), safeAddable);
      setAddExpandedLotIds((prev) => {
        const next = new Set(prev);
        next.add(lotId);
        return next;
      });
      return;
    }

    if (isStaged) {
      unstageDirectLot(lotId);
      unstageLotPick(lotId);
      if (safeAddable.length) {
        setAddStaging((prev) => {
          const next = cloneAddStaging(prev);
          safeAddable.forEach((unit) => next.unitIds.delete(unit.id));
          return next;
        });
      }
      return;
    }

    // Por defecto: agregar solo la parcela. Las UF se eligen al expandir.
    stageDirectLot(lot);
  }, [
    addStaging,
    currentListDirectLotIds,
    lotHasUnitsInList,
    selectedList?.id,
    stageDirectLot,
    stageLotWithUnits,
    unstageDirectLot,
    unstageLotPick,
  ]);

  const handleToggleRecordUnit = useCallback((lot, unit, addableUnits) => {
    toggleStagedUnit(lot, unit, addableUnits);
  }, [toggleStagedUnit]);

  const handleToggleReviewUnit = useCallback((lotId, unitId) => {
    if (!lotId) {
      setAddStaging((prev) => {
        const next = cloneAddStaging(prev);
        next.unitIds.delete(unitId);
        clearStagingSource(next, `unit:${unitId}`);
        return next;
      });
      return;
    }

    const listId = selectedList?.id;
    const pickLot = addStaging.lotUnitPicks[lotId]?.lot;
    const unitPickOnly =
      currentListDirectLotIds.has(lotId)
      || lotHasUnitsInList(pickLot, listId);

    setAddStaging((prev) => {
      const pick = prev.lotUnitPicks[lotId];
      if (!pick) return prev;
      const unitIds = new Set(pick.unitIds);
      if (unitIds.has(unitId)) unitIds.delete(unitId);
      else unitIds.add(unitId);
      const next = cloneAddStaging(prev);
      if (unitIds.size === 0) {
        delete next.lotUnitPicks[lotId];
        if (!unitPickOnly) {
          next.directLotIds.add(lotId);
          next.lotMeta[lotId] = pick.lot;
          setStagingSource(next, lotId, next.itemSources[lotId] || 'records');
        } else {
          clearStagingSource(next, lotId);
        }
      } else {
        next.lotUnitPicks[lotId] = { ...pick, unitIds };
      }
      return next;
    });
  }, [addStaging.lotUnitPicks, currentListDirectLotIds, lotHasUnitsInList, selectedList?.id]);

  const resolveLotForMapPick = useCallback(async (lotId) => {
    let lot = allLots.find((row) => String(row.lot_id) === String(lotId));
    if (lot) return lot;

    try {
      const res = await getMyLots(withSpaceOwner());
      const nextLots = normalizeLotsData(res?.data);
      setAllLots(nextLots);
      lot = nextLots.find((row) => String(row.lot_id) === String(lotId));
      if (lot) return lot;
    } catch (e) {
      console.error('Error resolving lot for map pick:', e);
    }

    try {
      const lotData = await getLotDetail(lotId);
      if (lotData) {
        return {
          lot_id: lotId,
          lot_name: lotData.lot_name || lotData.name || lotId,
          province_name: lotData.province_name,
          department_name: lotData.department_name,
          lat: lotData.lat,
          lng: lotData.lng,
          boundary: lotData.boundary,
          units: [],
        };
      }
    } catch (e) {
      console.error('Error fetching catastral lot for map pick:', e);
    }

    return null;
  }, [allLots, withSpaceOwner]);

  const handleMapAddPick = useCallback(async (lotId, clickLngLat = null) => {
    const listId = selectedList?.id;
    if (!listId || !lotId) return;

    // Click en mapa durante agregar: siempre trabajar en tab "Desde el mapa".
    setAddSourceTab('map');

    // Toggle off si ya está en staging.
    if (addStaging.directLotIds.has(lotId) || addStaging.lotUnitPicks[lotId]) {
      unstageDirectLot(lotId);
      unstageLotPick(lotId);
      return;
    }

    // Resolver el lote puede pedir /getmylots completo y el detalle catastral:
    // sin este flag el panel no muestra nada durante esos segundos.
    setResolvingMapPickLotId(lotId);
    let resolved = null;
    try {
      resolved = await resolveLotForMapPick(lotId);
    } finally {
      setResolvingMapPickLotId(null);
    }

    if (!resolved) {
      toast.error('No se pudo obtener información del lote.');
      return;
    }

    // Asegurar lat/lng para pins a zoom bajo (API a veces no los trae).
    const clickLat = Number(clickLngLat?.lat);
    const clickLng = Number(clickLngLat?.lng);
    const lot = {
      ...resolved,
      lat: resolved.lat ?? (Number.isFinite(clickLat) ? clickLat : null),
      lng: resolved.lng ?? (Number.isFinite(clickLng) ? clickLng : null),
    };

    const addableUnits = getAddableUnitsForLot(lot, listId);
    const parcelAlreadyInList = currentListDirectLotIds.has(lotId);
    const unitsAlreadyInList = lotHasUnitsInList(lot, listId);

    // Lote ya vinculado (parcela o UF): solo permitir elegir UF pendientes.
    if (parcelAlreadyInList || unitsAlreadyInList) {
      if (addableUnits.length === 0) {
        toast.error(
          getDisplayUnits(lot).length > 0
            ? 'Todas las unidades de este lote ya están en la lista.'
            : 'Este lote ya está en la lista.'
        );
        return;
      }
      openUnitPickDraft(lot, addableUnits, 'map');
      setAddExpandedLotIds((prev) => {
        const next = new Set(prev);
        next.add(lot.lot_id);
        return next;
      });
      return;
    }

    if (addableUnits.length === 0) {
      const lotUnits = getDisplayUnits(lot);
      if (lotUnits.length > 0) {
        toast.error('Todas las unidades de este lote ya están en la lista.');
        return;
      }
      stageDirectLot(lot, 'map');
      return;
    }

    // Por defecto se agrega solo la parcela; el usuario elige UF al expandir.
    stageDirectLot(lot, 'map');
    setAddExpandedLotIds((prev) => {
      const next = new Set(prev);
      next.add(lot.lot_id);
      return next;
    });
  }, [
    selectedList?.id,
    currentListDirectLotIds,
    addStaging.directLotIds,
    addStaging.lotUnitPicks,
    resolveLotForMapPick,
    getAddableUnitsForLot,
    lotHasUnitsInList,
    openUnitPickDraft,
    stageDirectLot,
    unstageDirectLot,
    unstageLotPick,
  ]);

  const handleConfirmStagedAdds = async () => {
    const listId = selectedList?.id;
    if (!listId || stagedCount === 0 || reviewGroups.hasInvalidLotPicks) return;

    const { lot_ids, user_lot_ids } = extractListMembership(lots);
    const newLotIds = [...lot_ids];
    addStaging.directLotIds.forEach((lotId) => {
      const lot = addStaging.lotMeta[lotId] || allLots.find((row) => row.lot_id === lotId);
      if (lot && lotHasUnitsInList(lot, listId)) return;
      if (!newLotIds.includes(lotId)) newLotIds.push(lotId);
    });

    const newUnitIds = [...user_lot_ids];
    const pushUnitId = (unitId) => {
      if (unitId && !newUnitIds.includes(unitId)) newUnitIds.push(unitId);
    };
    addStaging.unitIds.forEach(pushUnitId);
    Object.values(addStaging.lotUnitPicks).forEach(({ unitIds }) => {
      unitIds.forEach(pushUnitId);
    });

    const ok = await syncListMembership({
      listId,
      lotIds: newLotIds,
      userLotIds: newUnitIds,
      successMessage: stagedCount === 1 ? 'Ítem agregado a la lista' : `${stagedCount} ítems agregados a la lista`,
      updatingKey: `add:${listId}`,
    });
    if (ok) closeAddMode({ restoreHighlights: false });
  };

  const addFlowCommonProps = {
    step: addStep,
    sourceTab: addSourceTab,
    onSourceTabChange: setAddSourceTab,
    addSearch,
    onAddSearchChange: setAddSearch,
    loadingAllLots,
    recordsLots: addableRecordsLots,
    listId: selectedList?.id,
    parcelInListIds: currentListDirectLotIds,
    staging: addStaging,
    expandedLotIds: addExpandedLotIds,
    onToggleExpand: toggleAddLotExpanded,
    onToggleRecordLot: handleToggleRecordLot,
    onToggleRecordUnit: handleToggleRecordUnit,
    onLocateLot,
    isRecordLotSelected,
    isRecordLotPartial,
    stagedCount,
    stagedMapLots,
    isResolvingMapPick: Boolean(resolvingMapPickLotId),
    reviewGroups,
    onToggleReviewUnit: handleToggleReviewUnit,
    onRemoveReviewDirectLot: unstageDirectLot,
    onRemoveReviewLotPick: unstageLotPick,
    isCommitting: updatingUnitId === `add:${selectedList?.id}`,
    onCancel: closeAddMode,
    onGoToReview: () => setAddStep('review'),
    onBackFromReview: () => setAddStep('pick'),
    onConfirm: handleConfirmStagedAdds,
  };

  const showAddFlowFooter = addMode
    && viewMode === 'detail'
    && !(addStep === 'pick' && addSourceTab === 'map' && stagedCount === 0);

  const togglePreviewOnMap = async (row) => {
    const same = previewListId === row.id;
    if (same) {
      previewRequestSeqRef.current += 1;
      setPreviewListId(null);
      emitHighlightLots([]);
      return;
    }

    previewSharedRequestSeqRef.current += 1;
    setPreviewSharedListId(null);
    setPreviewSharedLoadingId(null);
    clearCadIndexPreview();

    const requestId = ++previewRequestSeqRef.current;
    setPreviewListId(row.id);
    emitHighlightLots([]);
    setPreviewLoadingId(row.id);
    try {
      const res = await getMyLots(withSpaceOwner({ listId: row.id }));
      if (requestId !== previewRequestSeqRef.current) return;
      const nextLots = normalizeLotsData(res?.data);
      setPreviewListId(row.id);
      emitHighlightLots(nextLots);
    } catch (e) {
      if (requestId !== previewRequestSeqRef.current) return;
      console.error('Error previewing lots for list:', e);
      setPreviewListId(null);
      emitHighlightLots([]);
    } finally {
      if (requestId === previewRequestSeqRef.current) {
        setPreviewLoadingId(null);
      }
    }
  };

  const togglePreviewCadOnMap = async (project) => {
    const slug = String(project?.slug || '').trim();
    if (!slug) return;
    if (!canLocateCadProjectOnMap(project)) {
      notifyToast({
        title: 'Sin ubicación',
        text: 'Esta lista es un plano; no se puede ubicar en el mapa país.',
        icon: 'warning',
      });
      return;
    }

    const same = previewCadSlug === slug;
    if (same) {
      clearCadIndexPreview();
      return;
    }

    clearCadastralIndexPreview();
    const requestId = ++previewCadRequestSeqRef.current;
    const previewId = getCadProjectId(project);
    if (!previewId) {
      notifyToast({
        title: 'Error',
        text: 'Falta el id de la lista.',
        icon: 'error',
      });
      return;
    }
    setPreviewCadSlug(slug);
    setPreviewCadLoadingSlug(slug);
    try {
      const result = await getCadProject({ id: previewId });
      if (requestId !== previewCadRequestSeqRef.current) return;
      if (!result.ok || !result.project || !shouldPaintCadOnMap(result.project)) {
        setPreviewCadSlug(null);
        notifyToast({
          title: 'Sin ubicación',
          text: result.message || 'Esta lista no está georreferenciada en el mapa.',
          icon: 'warning',
        });
        onCadOverlayChange?.({ enabled: false });
        return;
      }
      setPreviewCadSlug(slug);
      onCadOverlayChange?.({
        enabled: true,
        mode: 'map',
        featureCollection: result.project.parcels,
        selectedId: null,
        onSelect: null,
        fitNonce: requestId,
      });
    } catch (err) {
      if (requestId !== previewCadRequestSeqRef.current) return;
      console.error('CAD: no se pudo ubicar en el mapa', err);
      setPreviewCadSlug(null);
      onCadOverlayChange?.({ enabled: false });
      notifyToast({
        title: 'Error',
        text: 'No se pudo ubicar la lista en el mapa.',
        icon: 'error',
      });
    } finally {
      if (requestId === previewCadRequestSeqRef.current) {
        setPreviewCadLoadingSlug(null);
      }
    }
  };

  const backToLists = () => {
    closeListLotDock();
    clearMapListPreview();
    setViewMode('lists');
    setSelectedList(null);
    setLots([]);
    setLotsSearch('');
    setAddMode(false);
    setAddStep('pick');
    setAddSourceTab('records');
    setAddStaging(createEmptyStaging());
    setAddExpandedLotIds(new Set());
    setAddSearch('');
    setAllLots([]);
  };

  const restoreListContextHighlights = useCallback(() => {
    if (viewMode === 'sharedDetail') {
      emitHighlightLots(sharedDetailLots);
      return;
    }
    if (viewMode === 'detail') {
      emitHighlightLots(filteredLotsRef.current);
    }
  }, [viewMode, sharedDetailLots, emitHighlightLots]);

  const closeListLotDock = useCallback(() => {
    activeDockKeyRef.current = null;
    setSharedLotDock(null);
    setSharedDockDetail(null);
    setSharedDockDetailLoading(false);
    setMineListLotDock(null);
    onSharedLotDockChange?.(null);
    onLotBuildingDisplayModeChange?.('hidden');
    restoreListContextHighlights();
  }, [
    onSharedLotDockChange,
    onLotBuildingDisplayModeChange,
    restoreListContextHighlights,
  ]);

  const openCadProject = useCallback((project, options = {}) => {
    if (!project) return;
    closeListLotDock();
    clearCadastralIndexPreview();
    previewCadRequestSeqRef.current += 1;
    setPreviewCadSlug(null);
    setPreviewCadLoadingSlug(null);
    setShowCreateListForm(false);
    setAddMode(false);
    setSelectedList(null);
    setSelectedSharedListMeta(null);
    setEditingCadSlug(null);
    setCadEditName('');
    const parcelId = options.parcelId == null ? null : String(options.parcelId).trim();
    const parcelLabel = typeof options.parcelLabel === 'string' ? options.parcelLabel.trim() : '';
    setCadInitialParcelId(parcelId || null);
    setSelectedCadProject(project);
    setCadSharePatch((prev) => (prev?.slug && prev.slug !== project.slug ? null : prev));
    setCadParcelCounts(null);
    setCadParcelLabel(parcelLabel || null);
    setCadGeorefOpen(false);
    setCadAutoGeoref(Boolean(options.startGeoref));
    setViewMode('cad');
    const skipMapView = isCadPlanDisplay(project) || Number(project?.srid) === 0;
    if (!skipMapView) {
      onCadViewChange?.({ open: true });
    }
  }, [clearCadastralIndexPreview, closeListLotDock, onCadViewChange]);

  const lastOpenCadDetailTsRef = useRef(0);
  useEffect(() => {
    const req = openCadDetailRequest;
    if (!req?.slug || !req.ts || req.ts === lastOpenCadDetailTsRef.current) return;
    lastOpenCadDetailTsRef.current = req.ts;
    const stub = req.project || { slug: req.slug, name: req.projectName || req.slug };
    const project = resolveCadProjectFromCatalog(stub, cadOwnedProjects, cadSharedProjects);
    openCadProject(project, { parcelId: req.parcelId, parcelLabel: req.parcelLabel });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- el ts evita reabrir el mismo pedido.
  }, [openCadDetailRequest, cadOwnedProjects, cadSharedProjects]);

  useEffect(() => {
    onCadListOpenChange?.(viewMode === 'cad' ? (selectedCadProject || true) : null);
  }, [onCadListOpenChange, selectedCadProject, viewMode]);

  useEffect(() => () => onCadListOpenChange?.(null), [onCadListOpenChange]);

  const backFromCad = useCallback(() => {
    if (onReturnToOrigin) {
      onReturnToOrigin();
      return;
    }
    setViewMode('lists');
    setSelectedCadProject(null);
    setEditingCadSlug(null);
    setCadEditName('');
    setCadSharePatch(null);
    setCadShareProject(null);
    setCadParcelCounts(null);
    setCadParcelLabel(null);
    setCadGeorefOpen(false);
    setCadAutoGeoref(false);
    setCadInitialParcelId(null);
    onCadOverlayChange?.({ enabled: false });
    onCadViewChange?.({ open: false });
  }, [onCadOverlayChange, onCadViewChange, onReturnToOrigin]);

  const applyCadProjectFromServer = useCallback((project) => {
    if (!project) return;
    setSelectedCadProject((prev) => {
      if (!prev) return prev;
      const sameId = getCadProjectId(prev) && getCadProjectId(prev) === getCadProjectId(project);
      const sameSlug = prev.slug && project.slug && prev.slug === project.slug;
      if (!sameId && !sameSlug) return prev;
      return mergeCadProjectFromReload(prev, project);
    });
    onCadProjectPinSync?.(project);
  }, [onCadProjectPinSync]);

  const handleCadProjectReloaded = useCallback((project) => {
    if (!project) return;
    applyCadProjectFromServer(project);
    reloadCadCollections();
    onCadProjectPinSync?.(project);
    if (isCadMapDisplay(project)) onCadViewChange?.({ open: true });
    else onCadViewChange?.({ open: false, retainOverlay: true, restoreCamera: true });
  }, [applyCadProjectFromServer, onCadProjectPinSync, onCadViewChange, reloadCadCollections]);

  useEffect(() => {
    if (viewMode !== 'cad') return;
    setSelectedCadProject((prev) => {
      if (!prev) return prev;
      const next = resolveCadProjectFromCatalog(prev, cadOwnedProjects, cadSharedProjects);
      if (
        next === prev
        || (
          next.can_publish === prev.can_publish
          && next.can_manage === prev.can_manage
          && next.can_edit === prev.can_edit
          && getCadProjectId(next) === getCadProjectId(prev)
        )
      ) {
        return prev;
      }
      return next;
    });
  }, [viewMode, cadOwnedProjects, cadSharedProjects]);

  const openListLotDock = useCallback(
    async ({
      kind,
      lot,
      unit,
      listName,
      listId,
      token,
      userLotId,
      sharedListUnits = [],
      isDirectLot = false,
      polygonInfo: presetPolygon,
      keepPreview = false,
      locate = true,
    }) => {
      if (!keepPreview) {
        previewRequestSeqRef.current += 1;
        previewSharedRequestSeqRef.current += 1;
        setPreviewListId(null);
        setPreviewLoadingId(null);
        setPreviewSharedListId(null);
        setPreviewSharedLoadingId(null);
      }

      const lotId = lot?.lot_id ? String(lot.lot_id) : null;
      const cachedLotData = lotId ? catastralCacheRef.current.get(lotId) || null : null;

      const buildPayload = (row) => {
        const polygonInfo = presetPolygon || buildSharedListDockPolygonInfo(row);
        if (!polygonInfo) return null;
        return {
          kind,
          listName: listName || 'Lista',
          listId: kind === 'mine' ? listId : null,
          polygonInfo,
          userLotId,
          token,
          lotId,
          lotRow: row,
          sharedListUnits: kind === 'shared' ? sharedListUnits : [],
          isDirectLot: kind === 'shared' ? isDirectLot : false,
        };
      };

      let lotRow = lot;
      if (cachedLotData) lotRow = mergeLotDetailFromApi(lotRow, cachedLotData) || lotRow;

      // Abrir el dock al instante con los datos del listado; el catastro se mergea en segundo plano.
      const payload = buildPayload(lotRow);
      if (!payload) return;

      const dockKey = `${kind}:${String(userLotId ?? lotId ?? '')}`;
      activeDockKeyRef.current = dockKey;

      if (kind === 'shared') {
        setSharedLotDock(payload);
        setMineListLotDock(null);
      } else {
        setMineListLotDock(payload);
        setSharedLotDock(null);
      }
      onSharedLotDockChange?.(payload);
      onLotBuildingDisplayModeChange?.('full');

      const triggerLocate = (row, polygonInfo) => {
        if (!locate || !row?.lot_id) return;
        onLocateLot?.(row.lot_id, enrichLotRowForLocate(row, polygonInfo));
      };

      const enrichedForLocate = enrichLotRowForLocate(lotRow, payload.polygonInfo);
      const canLocateNow = hasUsableBoundary(enrichedForLocate);
      if (canLocateNow) triggerLocate(lotRow, payload.polygonInfo);

      if (!lotId || cachedLotData || presetPolygon) {
        if (locate && !canLocateNow) triggerLocate(lotRow, payload.polygonInfo);
        return;
      }

      try {
        const lotData = await getLotDetail(lotId);
        if (!lotData) {
          if (locate && !canLocateNow) triggerLocate(lotRow, payload.polygonInfo);
          return;
        }
        catastralCacheRef.current.set(lotId, lotData);
        if (activeDockKeyRef.current !== dockKey) return;
        const mergedRow = mergeLotDetailFromApi(lot, lotData) || lot;
        const nextPayload = buildPayload(mergedRow);
        if (!nextPayload) return;
        if (kind === 'shared') setSharedLotDock(nextPayload);
        else setMineListLotDock(nextPayload);
        onSharedLotDockChange?.(nextPayload);
        if (locate) triggerLocate(mergedRow, nextPayload.polygonInfo);
      } catch (e) {
        console.error('Error loading catastral lot for list dock:', e);
      }
    },
    [onSharedLotDockChange, onLotBuildingDisplayModeChange, onLocateLot]
  );

  useEffect(() => {
    if (sharedLotDock?.kind !== 'shared') {
      setSharedDockDetail(null);
      setSharedDockDetailLoading(false);
      return undefined;
    }

    const { token, userLotId, lotId, lotRow, isDirectLot } = sharedLotDock;
    if (!token || !lotId) {
      setSharedDockDetail(null);
      setSharedDockDetailLoading(false);
      return undefined;
    }

    const sharedCacheKey = userLotId
      ? String(userLotId)
      : (isDirectLot ? `lot:${lotId}` : null);
    const cachedShared = sharedCacheKey
      ? sharedDockDetailCacheRef.current.get(sharedCacheKey) || null
      : null;
    const cachedLot = catastralCacheRef.current.get(String(lotId)) || null;

    let initial = lotRow || { lot_id: lotId };
    if (cachedShared) initial = mergeSharedListDetailFromApi(initial, cachedShared);
    if (cachedLot) initial = mergeLotDetailFromApi(initial, cachedLot);
    setSharedDockDetail(initial);

    const needSharedUnit = Boolean(token && userLotId && !cachedShared);
    const needSharedDirect = Boolean(
      token && isDirectLot && !userLotId && lotId && !cachedShared
    );
    const needLot = !cachedLot;

    if (!needSharedUnit && !needSharedDirect && !needLot) {
      setSharedDockDetailLoading(false);
      return undefined;
    }

    setSharedDockDetailLoading(!cachedShared && !cachedLot);

    let cancelled = false;
    let pending = (needSharedUnit || needSharedDirect ? 1 : 0) + (needLot ? 1 : 0);

    const finishRequest = () => {
      pending -= 1;
      if (cancelled || pending > 0) return;
      setSharedDockDetailLoading(false);
    };

    if (needSharedUnit) {
      void fetchSharedUnitDetail(token, userLotId)
        .then((sharedDetail) => {
          if (!sharedDetail) return;
          sharedDockDetailCacheRef.current.set(String(userLotId), sharedDetail);
          if (cancelled) return;
          setSharedDockDetail((prev) => mergeSharedListDetailFromApi(prev || lotRow, sharedDetail));
        })
        .finally(finishRequest);
    } else if (needSharedDirect) {
      void fetchSharedDirectLotDetail(token, lotId)
        .then((sharedDetail) => {
          if (!sharedDetail) return;
          sharedDockDetailCacheRef.current.set(`lot:${lotId}`, sharedDetail);
          if (cancelled) return;
          setSharedDockDetail((prev) => mergeSharedListDetailFromApi(prev || lotRow, sharedDetail));
        })
        .finally(finishRequest);
    }

    if (needLot) {
      void getLotDetail(lotId)
        .then((lotData) => {
          if (!lotData) return;
          catastralCacheRef.current.set(String(lotId), lotData);
          if (cancelled) return;
          setSharedDockDetail((prev) => mergeLotDetailFromApi(prev || lotRow, lotData));
        })
        .finally(finishRequest);
    }

    return () => {
      cancelled = true;
    };
  }, [sharedLotDock]);

  const handleOpenSharedLot = useCallback(
    (lot) => {
      const token = getShareTokenFromRow(selectedSharedListMeta);
      if (!token) {
        notifyToast({
          title: 'Sin enlace',
          text: 'No se pudo abrir el detalle de esta lista compartida.',
          icon: 'warning',
        });
        return;
      }
      const units = getDisplayUnits(lot);
      void openListLotDock({
        kind: 'shared',
        lot,
        unit: null,
        listName: sharedDetailListName || selectedSharedListMeta?.name,
        token,
        userLotId: null,
        sharedListUnits: units,
        isDirectLot: units.length === 0,
      });
    },
    [selectedSharedListMeta, sharedDetailListName, openListLotDock],
  );

  const handleOpenSharedDetailLot = useCallback(
    (lot, unit) => {
      const token = getShareTokenFromRow(selectedSharedListMeta);
      if (!token) {
        notifyToast({
          title: 'Sin enlace',
          text: 'No se pudo abrir el detalle de esta lista compartida.',
          icon: 'warning',
        });
        return;
      }
      const units = getDisplayUnits(lot);
      if (!units.length) {
        void openListLotDock({
          kind: 'shared',
          lot,
          unit: null,
          listName: sharedDetailListName || selectedSharedListMeta?.name,
          token,
          userLotId: null,
          sharedListUnits: [],
          isDirectLot: true,
        });
        return;
      }

      const targetUnit = unit || units[0];
      if (!targetUnit) return;

      const userLotId = targetUnit.id || targetUnit.user_lot_id;
      if (!userLotId) return;

      void openListLotDock({
        kind: 'shared',
        lot,
        unit: targetUnit,
        listName: sharedDetailListName || selectedSharedListMeta?.name,
        token,
        userLotId,
        sharedListUnits: units,
        isDirectLot: false,
      });
    },
    [
      selectedSharedListMeta,
      sharedDetailListName,
      openListLotDock,
    ]
  );

  const handleOpenMineListLot = useCallback(
    (lot) => {
      void openListLotDock({
        kind: 'mine',
        lot,
        unit: null,
        listName: selectedList?.name,
        listId: selectedList?.id,
        userLotId: null,
      });
    },
    [selectedList?.id, selectedList?.name, openListLotDock],
  );

  const handleOpenMineListDetailLot = useCallback(
    (lot, unit) => {
      const units = getDisplayUnits(lot);
      const targetUnit = unit || units[0];
      if (!targetUnit) return;

      void openListLotDock({
        kind: 'mine',
        lot,
        unit: targetUnit,
        listName: selectedList?.name,
        listId: selectedList?.id,
        userLotId: targetUnit.id || targetUnit.user_lot_id,
      });
    },
    [selectedList?.id, selectedList?.name, openListLotDock]
  );

  const openActivityItemFromLots = useCallback((detailLots, target, kind, listRow) => {
    const found = findActivityLotInList(detailLots, target);
    if (!found) {
      toast.error(target.itemType === 'unit'
        ? 'Esa unidad ya no está en la lista.'
        : 'Ese lote ya no está en la lista.');
      return;
    }
    const userLotId = found.unit ? (found.unit.id || found.unit.user_lot_id) : null;
    if (kind === 'shared') {
      const token = getShareTokenFromRow(listRow);
      if (!token) {
        toast.error('No se pudo abrir el detalle de esta lista.');
        return;
      }
      void openListLotDock({
        kind: 'shared',
        lot: found.lot,
        unit: found.unit,
        listName: listRow?.name || sharedDetailListName,
        token,
        userLotId,
        sharedListUnits: getDisplayUnits(found.lot),
        isDirectLot: !found.unit,
      });
      return;
    }
    void openListLotDock({
      kind: 'mine',
      lot: found.lot,
      unit: found.unit,
      listName: listRow?.name,
      listId: listRow?.id,
      userLotId,
    });
  }, [openListLotDock, sharedDetailListName]);

  // Click en una parcela del mapa que pertenece a la lista activa (detalle o preview):
  // abre el dock con contexto de lista (flecha "volver") en lugar del dock genérico del mapa.
  const lastMapClickTsRef = useRef(0);
  useEffect(() => {
    const req = mapLotClickRequest;
    if (!req?.lotId || !req.ts || req.ts === lastMapClickTsRef.current) return;
    if (addMode && addStep === 'pick') return;
    lastMapClickTsRef.current = req.ts;
    const lotId = String(req.lotId);

    const openFromLot = (lot, options) => {
      if (!lot) return;
      const units = getDisplayUnits(lot);
      if (!units.length) {
        void openListLotDock({
          lot,
          unit: null,
          userLotId: null,
          sharedListUnits: [],
          isDirectLot: true,
          locate: false,
          ...options,
        });
        return;
      }
      // Misma regla que la card: 2+ UF → detalle del lote (listado); 1 UF → esa unidad.
      if (units.length > 1) {
        void openListLotDock({
          lot,
          unit: null,
          userLotId: null,
          sharedListUnits: units,
          isDirectLot: false,
          locate: false,
          ...options,
        });
        return;
      }
      const targetUnit = units[0] || null;
      const targetUserLotId = targetUnit?.id || targetUnit?.user_lot_id;
      if (!targetUserLotId) return;
      void openListLotDock({
        lot,
        unit: targetUnit,
        userLotId: targetUserLotId,
        sharedListUnits: units,
        isDirectLot: false,
        locate: false,
        ...options,
      });
    };

    if (viewMode === 'detail') {
      openFromLot(lots.find((l) => String(l.lot_id) === lotId), {
        kind: 'mine',
        listName: selectedList?.name,
        listId: selectedList?.id,
      });
      return;
    }

    if (viewMode === 'sharedDetail') {
      const token = getShareTokenFromRow(selectedSharedListMeta);
      if (!token) return;
      openFromLot(sharedDetailLots.find((l) => String(l.lot_id) === lotId), {
        kind: 'shared',
        listName: sharedDetailListName || selectedSharedListMeta?.name,
        token,
      });
      return;
    }

    // Vista de listas con preview activo en el mapa: la fila resuelta viene en el request.
    if (!req.lot) return;
    if (previewListId) {
      const row = lists.find((l) => l.id === previewListId);
      openFromLot(req.lot, {
        kind: 'mine',
        listName: row?.name,
        listId: previewListId,
        keepPreview: true,
      });
      return;
    }
    if (previewSharedListId) {
      const row = sharedWithMeLists.find((l) => l.list_id === previewSharedListId);
      const token = getShareTokenFromRow(row);
      if (!token) return;
      openFromLot(req.lot, {
        kind: 'shared',
        listName: row?.name,
        token,
        keepPreview: true,
      });
    }
  }, [
    mapLotClickRequest,
    viewMode,
    lots,
    sharedDetailLots,
    selectedList?.id,
    selectedList?.name,
    selectedSharedListMeta,
    sharedDetailListName,
    previewListId,
    previewSharedListId,
    lists,
    sharedWithMeLists,
    openListLotDock,
    addMode,
    addStep,
  ]);

  const lastMapAddPickTsRef = useRef(0);
  useEffect(() => {
    const req = mapAddPickRequest;
    // Pick de agregar (registros o mapa): un click en parcela stagea vía mapa.
    if (!addMode || addStep !== 'pick') return;
    if (!req?.lotId || !req.ts || req.ts === lastMapAddPickTsRef.current) return;
    lastMapAddPickTsRef.current = req.ts;
    void handleMapAddPick(String(req.lotId), req.lngLat || null);
  }, [mapAddPickRequest, addMode, addStep, handleMapAddPick]);

  useEffect(() => {
    if (!onListAddPickModeChange) return;
    const inAddPick = addMode && addStep === 'pick';
    onListAddPickModeChange({
      // Intercepta clicks del mapa en todo el flujo de agregar (registros o mapa).
      active: inAddPick,
      // Tooltip de parcela solo en tab "Desde el mapa".
      showTooltip: inAddPick && addSourceTab === 'map',
      stagedLotIds: [
        ...Array.from(addStaging.directLotIds),
        ...Object.keys(addStaging.lotUnitPicks),
      ],
    });
  }, [addMode, addSourceTab, addStep, addStaging, onListAddPickModeChange]);

  useEffect(() => () => {
    onListAddPickModeChange?.({ active: false, showTooltip: false, stagedLotIds: [] });
  }, [onListAddPickModeChange]);

  useEffect(() => {
    if (!addMode || viewMode !== 'detail') {
      emitPendingAddLots([]);
      return undefined;
    }

    const memberIds = new Set(lots.map((lot) => String(lot?.lot_id)).filter(Boolean));
    const stagedIds = [
      ...Array.from(addStaging.directLotIds),
      ...Object.keys(addStaging.lotUnitPicks),
    ]
      .map(String)
      .filter((id) => id && !memberIds.has(id));

    const resolvePendingLot = (id) => (
      addStaging.lotMeta[id]
      || addStaging.lotUnitPicks[id]?.lot
      || allLots.find((row) => String(row?.lot_id) === id)
      || null
    );

    const withRepCoords = (lot) => {
      if (!lot) return null;
      const rep = getLotRepresentativeLatLng(lot);
      if (!rep) return lot;
      return {
        ...lot,
        lat: lot.lat ?? rep.lat,
        lng: lot.lng ?? rep.lng,
      };
    };

    const pendingLots = stagedIds.map(resolvePendingLot).filter(Boolean).map(withRepCoords);

    // Miembros: highlight + pins sólidos. Pendientes: outline PMTiles + pins a zoom bajo.
    emitHighlightLots(lots);
    emitPendingAddLots(pendingLots);

    const missingCoords = pendingLots.filter((lot) => !getLotRepresentativeLatLng(lot));
    if (missingCoords.length === 0) return undefined;

    let cancelled = false;
    (async () => {
      const enrichedById = new Map();
      await Promise.all(missingCoords.map(async (lot) => {
        try {
          const lotData = await getLotDetail(lot.lot_id);
          if (!lotData) return;
          const merged = withRepCoords({
            ...lot,
            lat: lot.lat ?? lotData.lat ?? lotData.latitude,
            lng: lot.lng ?? lotData.lng ?? lotData.longitude ?? lotData.lon,
            boundary: lot.boundary || lotData.boundary || null,
          });
          if (getLotRepresentativeLatLng(merged)) {
            enrichedById.set(String(lot.lot_id), merged);
          }
        } catch (e) {
          console.error('Error enriching pending lot coords for pins:', e);
        }
      }));

      if (cancelled || enrichedById.size === 0) return;

      emitPendingAddLots(
        pendingLots.map((lot) => enrichedById.get(String(lot.lot_id)) || lot)
      );
    })();

    return () => {
      cancelled = true;
    };
  }, [
    addMode,
    viewMode,
    lots,
    allLots,
    addStaging.directLotIds,
    addStaging.lotUnitPicks,
    addStaging.lotMeta,
    emitHighlightLots,
    emitPendingAddLots,
  ]);

  const backFromSharedDetail = useCallback(() => {
    closeListLotDock();
    clearMapListPreview();
    setViewMode('lists');
    // No forzamos tab: si vino desde 'all', vuelve a 'all'; si vino desde 'shared', vuelve a 'shared'.
    setSelectedSharedListMeta(null);
    setSharedDetailLots([]);
    setSharedDetailCounts({ totalLots: 0, totalUnits: 0 });
    setSharedDetailListName('');
    setSharedDetailError('');
    setSharedLotsSearch('');
    setLoadingSharedDetail(false);
  }, [clearMapListPreview, closeListLotDock]);

  const togglePreviewSharedOnMap = async (row) => {
    const listId = row?.list_id;
    const token = getShareTokenFromRow(row);
    if (!listId || !token) {
      notifyToast({
        title: 'Sin enlace',
        text: 'Esta fila no incluye token de compartido.',
        icon: 'warning',
      });
      return;
    }

    const same = previewSharedListId === listId;
    if (same) {
      previewSharedRequestSeqRef.current += 1;
      setPreviewSharedListId(null);
      emitHighlightLots([]);
      return;
    }

    previewRequestSeqRef.current += 1;
    setPreviewListId(null);
    setPreviewLoadingId(null);
    clearCadIndexPreview();

    const requestId = ++previewSharedRequestSeqRef.current;
    setPreviewSharedListId(listId);
    emitHighlightLots([]);
    setPreviewSharedLoadingId(listId);
    try {
      const res = await getSharedList(token, { format: 'grouped' });
      if (requestId !== previewSharedRequestSeqRef.current) return;
      const status = res?._httpStatus;
      if (status === 401 || status === 403 || status === 404 || !res?.data) {
        notifyToast({
          title: 'No disponible',
          text: res?.error || res?.message || 'No se pudo cargar la lista compartida.',
          icon: 'warning',
        });
        setPreviewSharedListId(null);
        emitHighlightLots([]);
        return;
      }
      const { groupedLots } = await resolveSharedListLotsForMap(res.data, { shareToken: token });
      if (requestId !== previewSharedRequestSeqRef.current) return;
      setPreviewSharedListId(listId);
      emitHighlightLots(groupedLots);
    } catch (e) {
      if (requestId !== previewSharedRequestSeqRef.current) return;
      console.error('Error previewing shared list:', e);
      setPreviewSharedListId(null);
      emitHighlightLots([]);
      notifyToast({ title: 'Error', text: 'No se pudo mostrar la lista en el mapa.', icon: 'error' });
    } finally {
      if (requestId === previewSharedRequestSeqRef.current) {
        setPreviewSharedLoadingId(null);
      }
    }
  };


  const openSharedListDetail = async (row) => {
    const token = getShareTokenFromRow(row);
    const canEdit = row?.can_edit === true;
    if (!token) {
      if (canEdit) {
        clearMapListPreview();
        setViewMode('sharedDetail');
        setSelectedSharedListMeta(row);
        setSharedDetailListName(row.name || 'Lista compartida');
        setSharedDetailLots([]);
        setSharedDetailCounts({ totalLots: 0, totalUnits: 0 });
        setSharedLotsSearch('');
        setSharedDetailError(
          'Esta lista no tiene link de visita todavía, así que no hay lotes para pintar. Como editor no se usa el listado del dueño: cuando la compartan vas a ver el contenido acá.',
        );
        setLoadingSharedDetail(false);
        return;
      }
      notifyToast({ title: 'Sin enlace', text: 'Esta fila no incluye token de compartido.', icon: 'warning' });
      return;
    }
    clearMapListPreview();
    setViewMode('sharedDetail');
    setSelectedSharedListMeta(row);
    setSharedDetailError('');
    setSharedLotsSearch('');
    setSharedDetailLots([]);
    setLoadingSharedDetail(true);
    try {
      // Editor de la lista: resumen comercial de la API (`viewer_can_manage`). Visitante: no.
      const res = await getSharedList(token, { format: 'grouped', includeCommercialSummary: canEdit });
      const status = res?._httpStatus;
      if (status === 401) {
        setSharedDetailError('Esta lista es restringida. Iniciá sesión para verla.');
        setSharedDetailLots([]);
        return;
      }
      if (status === 403) {
        setSharedDetailError('No tenés permiso para ver esta lista.');
        setSharedDetailLots([]);
        return;
      }
      if (status === 404 || !res?.data) {
        setSharedDetailError(res?.error || res?.message || 'Lista no disponible o el enlace expiró.');
        setSharedDetailLots([]);
        return;
      }
      const data = res.data;
      const listToken = getShareTokenFromRow(row) || data?.share_token || data?.shareToken || data?.token || '';
      const { groupedLots: resolvedLots, total, totalUnits } = await resolveSharedListLotsForMap(data, {
        shareToken: listToken,
      });
      // El enriquecimiento de coords puede reagrupar: re-pegar los bloques comerciales.
      const grouped = canEdit ? mergeListCommercial(resolvedLots, data.lots) : resolvedLots;
      setSharedDetailCounts({ totalLots: total, totalUnits });
      setSharedDetailListName(data.list_name || row.name || 'Lista compartida');
      setSelectedSharedListMeta((prev) => ({
        ...(prev || row),
        ...row,
        share_token: listToken,
        shareToken: listToken,
        token: listToken,
      }));
      setSharedDetailLots(grouped);
      setExpandedLotIds(new Set());
      emitHighlightLots(grouped);
    } catch (e) {
      console.error(e);
      setSharedDetailError('No se pudieron cargar los lotes.');
      setSharedDetailLots([]);
    } finally {
      setLoadingSharedDetail(false);
    }
  };

  const openManagedCollectionItem = useCallback((collection, target) => {
    if (!target || !collection) return;

    if (collection.kind === 'cad' || target.itemType === 'custom_parcel') {
      const projectId = String(collection.id || '');
      const project = [selectedCadProject, ...cadOwnedProjects, ...cadSharedProjects]
        .find((row) => getCadProjectId(row) === projectId);
      if (!project) return;
      openCadProject(project, {
        parcelId: target.parcelId,
        parcelLabel: target.label || '',
      });
      return;
    }

    if (String(selectedList?.id) === String(collection.id)) {
      openActivityItemFromLots(lots, target, 'mine', selectedList);
      return;
    }
    const sharedId = selectedSharedListMeta?.list_id || selectedSharedListMeta?.id;
    if (String(sharedId) === String(collection.id)) {
      openActivityItemFromLots(sharedDetailLots, target, 'shared', selectedSharedListMeta);
      return;
    }

    const mineRow = lists.find((row) => String(row?.id) === String(collection.id));
    if (mineRow) {
      pendingActivityOpenRef.current = {
        kind: 'mine',
        listId: mineRow.id,
        target,
        listRow: mineRow,
      };
      void openListDetail(mineRow);
      return;
    }
    const sharedRow = sharedWithMeLists.find((row) => (
      String(row?.list_id || row?.id) === String(collection.id)
    ));
    if (sharedRow) {
      pendingActivityOpenRef.current = {
        kind: 'shared',
        listId: sharedRow.list_id || sharedRow.id,
        target,
        listRow: sharedRow,
      };
      void openSharedListDetail(sharedRow);
    }
    // openListDetail / openSharedListDetail se recrean cada render (igual que el resto del panel).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    cadOwnedProjects,
    cadSharedProjects,
    lists,
    lots,
    openActivityItemFromLots,
    openCadProject,
    selectedCadProject,
    selectedList,
    selectedSharedListMeta,
    sharedDetailLots,
    sharedWithMeLists,
  ]);

  const handleOpenActivityItem = useCallback((target) => {
    const collection = activityDialogTarget;
    setActivityDialogTarget(null);
    openManagedCollectionItem(collection, target);
  }, [activityDialogTarget, openManagedCollectionItem]);

  const handleOpenInquiryItem = useCallback((target) => {
    const collection = inquiryDialogTarget;
    setInquiryDialogTarget(null);
    openManagedCollectionItem(collection, target);
  }, [inquiryDialogTarget, openManagedCollectionItem]);

  useEffect(() => {
    const pending = pendingActivityOpenRef.current;
    if (!pending) return;
    if (pending.kind === 'mine') {
      if (loadingLots) return;
      if (String(selectedList?.id) !== String(pending.listId)) return;
      pendingActivityOpenRef.current = null;
      openActivityItemFromLots(lots, pending.target, 'mine', pending.listRow || selectedList);
      return;
    }
    if (pending.kind === 'shared') {
      if (loadingSharedDetail) return;
      const sharedId = selectedSharedListMeta?.list_id || selectedSharedListMeta?.id;
      if (String(sharedId) !== String(pending.listId)) return;
      pendingActivityOpenRef.current = null;
      openActivityItemFromLots(
        sharedDetailLots,
        pending.target,
        'shared',
        pending.listRow || selectedSharedListMeta,
      );
    }
  }, [
    loadingLots,
    loadingSharedDetail,
    lots,
    openActivityItemFromLots,
    selectedList,
    selectedSharedListMeta,
    sharedDetailLots,
  ]);

  const handleLeaveSharedWithMeRow = async (row) => {
    const listId = row?.list_id;
    if (!listId) return;
    const listName = row?.name || 'esta lista';
    const confirm = await confirmDialog({
      title: '¿Dejar de ver esta lista?',
      description: `Se va a quitar tu acceso a "${listName}". Deja de aparecer en Compartidas conmigo y se corta el vínculo.`,
      confirmLabel: 'Dejar de ver',
      cancelLabel: 'Cancelar',
      variant: 'danger',
    });
    if (!confirm.isConfirmed) return;

    const busyKey = `list-${listId}`;
    setGuestShareBusyKey(busyKey);
    try {
      const res = await leaveSharedUserLotList({ list_id: listId });
      if (!res._httpOk) {
        notifyToast(guestShareActionErrorToast(res._httpStatus, res.error || res.message));
        return;
      }
      await refreshSharedWithMePanel();
      toast.success('Dejaste de ver la lista.');
    } catch (e) {
      console.error(e);
      notifyToast({ title: 'Error', text: 'No se pudo completar la acción.', icon: 'error' });
    } finally {
      setGuestShareBusyKey(null);
    }
  };

  const handleLeaveSharedCadProject = async (project) => {
    const slug = String(project?.slug || '').trim();
    const projectId = String(project?.project_id || project?.id || '').trim();
    if (!slug && !projectId) return;
    const name = project?.name || slug || 'esta lista';
    const confirm = await confirmDialog({
      title: '¿Dejar de ver esta lista?',
      description: `Se va a quitar tu acceso a "${name}". Deja de aparecer en Compartidas conmigo y se corta el vínculo.`,
      confirmLabel: 'Dejar de ver',
      cancelLabel: 'Cancelar',
      variant: 'danger',
    });
    if (!confirm.isConfirmed) return;

    if (!projectId) {
      notifyToast({
        title: 'Error',
        text: 'Falta el id de la lista.',
        icon: 'error',
      });
      return;
    }

    const busyKey = `cad-${slug || projectId}`;
    setGuestShareBusyKey(busyKey);
    try {
      const res = await leaveSharedCadProject({ projectId });
      if (!res.ok) {
        notifyToast(guestShareActionErrorToast(res._httpStatus, res.message));
        return;
      }
      await refreshSharedWithMePanel();
      toast.success('Dejaste de ver la lista.');
    } catch (e) {
      console.error(e);
      notifyToast({ title: 'Error', text: 'No se pudo completar la acción.', icon: 'error' });
    } finally {
      setGuestShareBusyKey(null);
    }
  };

  useEffect(() => {
    refreshLists();
    return () => refreshListsAbortRef.current?.abort();
  }, [refreshLists]);

  // Cargar listas compartidas tanto en tab 'shared' como en tab 'all'
  useEffect(() => {
    if (listsPanelTab !== 'shared' && listsPanelTab !== 'all') return;
    refreshSharedWithMeLists();
    return () => refreshSharedAbortRef.current?.abort();
  }, [listsPanelTab, refreshSharedWithMeLists]);
  // Al cambiar de espacio (chrome MapHeader), recargar inventario espejo.
  useEffect(() => {
    refreshLists();
    reloadCadCollections();
  }, [activeOwnerUserId, refreshLists, reloadCadCollections]);


  useEffect(() => {
    if (!selectedList?.id) return;
    const latest = lists.find((l) => l.id === selectedList.id);
    if (!latest) return;
    setSelectedList((prev) => (prev ? { ...prev, ...latest } : prev));
  }, [lists, selectedList?.id]);

  const handleClose = () => {
    closeListLotDock();
    setNewName('');
    setNewIsPublic(false);
    setEditingId(null);
    setEditName('');
    setEditIsPublic(false);
    setListSearch('');
    setVisibilityFilter('all');
    setListsPanelTab('all');
    setViewMode('lists');
    setSelectedList(null);
    setLots([]);
    setLotsSearch('');
    setUpdatingUnitId(null);
    setSelectedSharedListMeta(null);
    setSharedDetailLots([]);
    setSharedDetailCounts({ totalLots: 0, totalUnits: 0 });
    setSharedDetailListName('');
    setSharedDetailError('');
    setSharedLotsSearch('');
    setLoadingSharedDetail(false);
    closeAddMode();
    clearMapListPreview();
    setShowCreateListForm(false);
    setSelectedCadProject(null);
    onCadOverlayChange?.({ enabled: false });
    onCadViewChange?.({ open: false });
    onClose?.();
  };

  const handleCreate = async () => {
    const name = newName.trim();
    if (!name) {
      notifyToast({ title: 'Nombre requerido', text: 'Ingresá un nombre para la lista.', icon: 'warning' });
      return;
    }
    setCreating(true);
    try {
      // Espacio ajeno (F2): la lista nace en el espacio del owner; `is_public`
      // es del dueño (la API lo ignora para el invitado), así que no se manda.
      const res = await createUserLotList({
        name,
        ...(listWriteOwnerUserId ? {} : { is_public: newIsPublic }),
        ownerUserId: listWriteOwnerUserId,
      });
      if (res.data) {
        setNewName('');
        setNewIsPublic(false);
        setShowCreateListForm(false);
        await refreshLists();
        toast.success('Lista creada con éxito.');
      } else {
        notifyToast({
          title: 'Error',
          text: resolveSpaceGuestWriteError(res._httpStatus, listWriteOwnerUserId)
            || res.error || res.message || 'No se pudo crear la lista.',
          icon: 'error',
        });
      }
    } catch (e) {
      console.error(e);
      notifyToast({ title: 'Error', text: 'No se pudo crear la lista.', icon: 'error' });
    } finally {
      setCreating(false);
    }
  };

  const handleNewNameKeyDown = (e) => {
    if (e.key === 'Enter' && newName.trim() && !creating) handleCreate();
  };

  const startEdit = (row) => {
    setEditingId(row.id);
    setEditName(row.name || '');
    setEditIsPublic(!!row.is_public);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditName('');
    setEditIsPublic(false);
  };

  const saveEdit = async (listId) => {
    const name = editName.trim();
    setSavingId(listId);
    try {
      const row = lists.find((l) => l.id === listId);
      const trimmedName = name;
      const nameChanged = typeof row?.name === 'string' ? trimmedName !== row.name : !!trimmedName;
      // Espacio ajeno (F2): el invitado solo renombra; `is_public` es del dueño (403).
      const publicChanged = !listWriteOwnerUserId && !!row?.is_public !== !!editIsPublic;

      if (!trimmedName) {
        notifyToast({ title: 'Nombre requerido', icon: 'warning' });
        return;
      }
      if (!nameChanged && !publicChanged) {
        cancelEdit();
        return;
      }

      const res = await updateUserLotList({
        list_id: listId,
        ...(nameChanged ? { name: trimmedName } : {}),
        ...(publicChanged ? { is_public: !!editIsPublic } : {}),
        ownerUserId: listWriteOwnerUserId,
      });
      if (res.data) {
        if (selectedList?.id === listId) {
          setSelectedList((prev) => (prev ? {
            ...prev,
            name: trimmedName,
            ...(publicChanged ? { is_public: !!editIsPublic } : {}),
          } : prev));
        }
        cancelEdit();
        await refreshLists();
      } else {
        notifyToast({
          title: 'Error',
          text: resolveSpaceGuestWriteError(res._httpStatus, listWriteOwnerUserId, { notFoundAsForbidden: true })
            || res.error || res.message || 'No se pudo renombrar.',
          icon: 'error',
        });
      }
    } catch (e) {
      console.error(e);
      notifyToast({ title: 'Error', text: 'No se pudo actualizar la lista.', icon: 'error' });
    } finally {
      setSavingId(null);
    }
  };

  const handleEditKeyDown = (e, listId) => {
    if (e.key === 'Enter') saveEdit(listId);
    if (e.key === 'Escape') cancelEdit();
  };

  const startCadEdit = (project) => {
    if (!project?.slug) return;
    setEditingCadSlug(project.slug);
    setCadEditName(project.name || '');
  };

  const cancelCadEdit = () => {
    setEditingCadSlug(null);
    setCadEditName('');
  };

  const saveCadEdit = async (slug) => {
    const name = cadEditName.trim();
    if (!slug) return;
    if (!name) {
      notifyToast({ title: 'Nombre requerido', icon: 'warning' });
      return;
    }
    const currentName = String(selectedCadProject?.name || '').trim();
    if (name === currentName) {
      cancelCadEdit();
      return;
    }
    setSavingCadSlug(slug);
    try {
      const id = getCadProjectId(selectedCadProject);
      if (!id) {
        notifyToast({
          title: 'Error',
          text: 'Falta el id de la lista.',
          icon: 'error',
        });
        return;
      }
      const res = await updateCadProject({ id, name });
      if (res.ok && res.project) {
        const updated = res.project;
        setSelectedCadProject((prev) => (
          prev && prev.slug === slug ? mergeCadProjectSummary(prev, updated) : prev
        ));
        setCadShareProject((prev) => (
          prev && prev.slug === slug ? mergeCadProjectSummary(prev, updated) : prev
        ));
        cancelCadEdit();
        reloadCadCollections();
        toast.success('Lista renombrada.');
      } else {
        notifyToast({
          title: 'Error',
          text: res.message || 'No se pudo renombrar la lista.',
          icon: 'error',
        });
      }
    } catch (e) {
      console.error(e);
      notifyToast({ title: 'Error', text: 'No se pudo renombrar la lista.', icon: 'error' });
    } finally {
      setSavingCadSlug(null);
    }
  };

  const handleCadEditKeyDown = (e, slug) => {
    if (e.key === 'Enter') saveCadEdit(slug);
    if (e.key === 'Escape') cancelCadEdit();
  };

  const handleDeleteCadProject = async (project) => {
    // Eliminar es solo del dueño: en espacio ajeno nunca, aunque vengan flags.
    if (isViewingSharedSpace || !canPublishCadProject(project)) return;
    const slug = String(project?.slug || '').trim();
    const projectId = getCadProjectId(project);
    if (!slug && !projectId) return;
    const name = project?.name || slug;
    const countsFromOpenDetail = slug === selectedCadProject?.slug && cadParcelCounts;
    let parcelCount = countsFromOpenDetail
      ? Number(cadParcelCounts.total) || 0
      : countCadProjectParcels(project);
    let countKnown = Boolean(countsFromOpenDetail) || parcelCount > 0;
    if (!countKnown && projectId) {
      try {
        const loaded = await getCadProject({ id: projectId });
        if (loaded.ok) {
          parcelCount = countCadProjectParcels(loaded.project);
          countKnown = true;
        }
      } catch (e) {
        console.error('CAD listas: no se pudo contar parcelas antes de borrar', e);
      }
    }
    const parcelsCopy = !countKnown
      ? 'Se van a eliminar todos los lotes.'
      : parcelCount === 1
        ? 'Se va a eliminar 1 lote.'
        : `Se van a eliminar ${parcelCount} lotes.`;
    const confirm = await confirmDialog({
      title: '¿Eliminar lista?',
      description: `Se eliminará definitivamente la lista "${name}". ${parcelsCopy} Esta acción no se puede deshacer.`,
      confirmLabel: 'Eliminar',
      cancelLabel: 'Cancelar',
      variant: 'danger',
    });
    if (!confirm.isConfirmed) return;

    setDeletingCadSlug(slug || projectId);
    try {
      if (!projectId) {
        notifyToast({
          title: 'Error',
          text: 'Falta el id de la lista.',
          icon: 'error',
        });
        return;
      }
      const res = await deleteCadProject({ id: projectId });
      if (!res.ok) {
        notifyToast({
          title: 'Error',
          text: res.message || 'No se pudo eliminar la lista.',
          icon: 'error',
        });
        return;
      }
      const deleted = Number(res.data?.parcels_deleted);
      toast.success(
        Number.isFinite(deleted)
          ? `Lista eliminada (${deleted} lote${deleted === 1 ? '' : 's'}).`
          : 'Lista eliminada con éxito.',
      );
      if (selectedCadProject?.slug === slug) {
        backFromCad();
      }
      reloadCadCollections();
      onCadOwnerOverlayInvalidate?.({ id: projectId, slug });
    } catch (e) {
      console.error(e);
      notifyToast({ title: 'Error', text: 'No se pudo eliminar la lista.', icon: 'error' });
    } finally {
      setDeletingCadSlug(null);
    }
  };

  const handleDelete = async (row) => {
    if (isViewingSharedSpace) return;
    const lotCount = Number(row.item_count ?? 0) || 0;
    const confirm = await confirmDialog({
      title: '¿Eliminar lista?',
      description: lotCount > 0
        ? `Se eliminará definitivamente la lista "${row.name}". Antes se desvincularán ${lotCount} unidad(es) vinculadas a esta lista.`
        : `Se eliminará definitivamente la lista "${row.name}".`,
      confirmLabel: 'Eliminar',
      cancelLabel: 'Cancelar',
      variant: 'danger',
    });
    if (!confirm.isConfirmed) return;

    setDeletingId(row.id);
    try {
      if (lotCount > 0) {
        const listLotsRes = await getMyLots(withSpaceOwner({ listId: row.id }));
        const listLots = normalizeLotsData(listLotsRes?.data);
        const { lot_ids, user_lot_ids } = extractListMembership(listLots);

        if (lot_ids.length || user_lot_ids.length) {
          const clearRes = await updateUserLotList({
            list_id: row.id,
            lot_ids: [],
            user_lot_ids: [],
          });
          if (clearRes?.error) {
            notifyToast({
              title: 'No se pudo eliminar',
              text: clearRes.error || 'No se pudieron desvincular los ítems de la lista. Reintentá.',
              icon: 'error',
            });
            return;
          }
        }
      }

      const res = await deleteUserLotList(row.id);
      if (res.message && !res.error) {
        await refreshLists();
        toast.success('Lista eliminada con éxito.');
        if (selectedList?.id === row.id) {
          setViewMode('lists');
          setSelectedList(null);
          setLots([]);
          setLotsSearch('');
          setAddMode(false);
          setAddSearch('');
          setAllLots([]);
          cancelEdit();
          setDetailMenuAnchor(null);
          if (previewListId === row.id) {
            setPreviewListId(null);
            emitHighlightLots([]);
          }
        }
      } else if (res.item_count != null) {
        notifyToast({
          title: 'No se puede eliminar',
          text: res.error || `La lista tiene ${res.item_count} unidad(es).`,
          icon: 'error',
        });
      } else {
        notifyToast({ title: 'Error', text: res.error || res.message || 'No se pudo eliminar.', icon: 'error' });
      }
    } catch (e) {
      console.error(e);
      notifyToast({ title: 'Error', text: 'No se pudo eliminar la lista.', icon: 'error' });
    } finally {
      setDeletingId(null);
    }
  };

  // Espacio ajeno (F3): el invitado comparte la lista del owner como el dueño
  // (`listWriteOwnerUserId` → diálogo con `ownerUserId`).
  const handleShareToggle = async (row) => {
    openShareDialog(row);
  };

  const isDeleteDisabled = (row) => deletingId === row.id;

  const parcelFilterCount = countActiveParcelFilters(parcelFilters || {});
  const hasParcelFilters = parcelFilterCount > 0;

  // Buscador del detalle de lista propia (`utils/recordSearch`, igual que Registros):
  // agrupada → el lote queda si matchea él o alguno de sus registros; por registro →
  // no se filtran lotes acá sino cada card (`listDetailRecordItems`).
  const debouncedLotsSearch = useDebouncedValue(lotsSearch);
  const lotsSearchTerms = useMemo(() => parseSearchTerms(debouncedLotsSearch), [debouncedLotsSearch]);
  const hasLotsSearch = lotsSearchTerms.length > 0;
  const filteredLots = useMemo(() => {
    let rows = lots;
    if (hasLotsSearch && isRecordGrouped) {
      rows = rows.filter((l) => lotRowMatchesSearch(l, lotsSearchTerms));
    }
    if (hasParcelFilters) {
      rows = filterOwnedLotsByPanelFilters(rows, parcelFilters);
    }
    return filterRecordsByCommercial(rows, listCommercialFilters);
  }, [lots, hasLotsSearch, isRecordGrouped, lotsSearchTerms, hasParcelFilters, parcelFilters, listCommercialFilters]);

  const filteredLists = useMemo(() => {
    const term = listSearch.trim().toLowerCase();
    const byName = term
      ? lists.filter((l) => (l.name || '').toLowerCase().includes(term))
      : lists;

    return byName.filter((l) => matchesVisibilityFilter(l, visibilityFilter));
  }, [lists, listSearch, visibilityFilter]);

  const filteredSharedWithMeLists = useMemo(() => {
    const term = listSearch.trim().toLowerCase();
    if (!term) return sharedWithMeLists;
    return sharedWithMeLists.filter((l) => String(l?.name || '').toLowerCase().includes(term));
  }, [sharedWithMeLists, listSearch]);

  const cadOwnedVisible = useMemo(
    () => filterCadProjectsForLists(cadOwnedProjects, {
      search: listSearch,
      visibilityFilter,
    }),
    [cadOwnedProjects, listSearch, visibilityFilter],
  );

  const cadSharedVisible = useMemo(
    () => filterCadProjectsForLists(cadSharedProjects, {
      search: listSearch,
      visibilityFilter: 'all',
    }),
    [cadSharedProjects, listSearch],
  );

  // Búsqueda global en ambas; Público/Restringido (opcionales) solo en "Mis listas"
  const allUnifiedLists = useMemo(() => {
    const term = listSearch.trim().toLowerCase();

    const myItems = (term
      ? lists.filter((l) => (l.name || '').toLowerCase().includes(term))
      : lists
    ).filter((l) => matchesVisibilityFilter(l, visibilityFilter));

    // Compartidas: solo búsqueda por nombre (los chips de visibilidad no aplican)
    const sharedItems = term
      ? sharedWithMeLists.filter((l) => String(l?.name || '').toLowerCase().includes(term))
      : sharedWithMeLists;

    return [
      ...myItems.map((l) => ({ ...l, _type: 'mine' })),
      ...sharedItems.map((l) => ({ ...l, _type: 'shared' })),
    ];
  }, [lists, sharedWithMeLists, listSearch, visibilityFilter]);

  const mineUnifiedLists = useMemo(
    () => allUnifiedLists.filter((l) => l._type === 'mine'),
    [allUnifiedLists]
  );

  const sharedUnifiedLists = useMemo(
    () => allUnifiedLists.filter((l) => l._type === 'shared'),
    [allUnifiedLists]
  );

  const hasSharedCatastroCollections = sharedWithMeLists.length > 0;
  const hasSharedShowroomCollections = cadSharedProjects.length > 0;
  const showSharedListsSection = !isViewingSharedSpace
    && (hasSharedCatastroCollections || hasSharedShowroomCollections);

  const sharedOnlyUnifiedLists = useMemo(
    () => filteredSharedWithMeLists.map((l) => ({ ...l, _type: 'shared' })),
    [filteredSharedWithMeLists]
  );

  const filteredSharedDetailLots = useMemo(() => {
    const term = sharedLotsSearch.trim().toLowerCase();
    const rows = !term ? sharedDetailLots : sharedDetailLots
      .map((lot) => filterGroupedLotForSharedSearch(lot, term))
      .filter(Boolean);
    // Solo el editor recibe el bloque comercial; el visitante nunca ve los filtros.
    return selectedSharedListMeta?.can_edit ? filterRecordsByCommercial(rows, listCommercialFilters) : rows;
  }, [sharedDetailLots, sharedLotsSearch, selectedSharedListMeta?.can_edit, listCommercialFilters]);

  // Filtros comerciales: por lista; al cambiar de lista o de vista se limpian.
  useEffect(() => {
    setListCommercialFilters(DEFAULT_COMMERCIAL_FILTERS);
  }, [viewMode, selectedList?.id, selectedSharedListMeta?.list_id]);

  useEffect(() => {
    setVisibleCardCount(LIST_RENDER_CHUNK);
  }, [listCommercialFilters, viewMode, selectedList?.id, selectedSharedListMeta?.list_id, lotsSearch, sharedLotsSearch, parcelFilters, recordViewMode]);

  useEffect(() => {
    setListSortBy(DEFAULT_LIST_SORT);
  }, [viewMode, selectedList?.id, selectedSharedListMeta?.list_id]);

  const visibleCardTotalRef = useRef(0);
  visibleCardTotalRef.current = viewMode === 'sharedDetail'
    ? filteredSharedDetailLots.length
    : filteredLots.length;

  const handleCardsScroll = useCallback((e) => {
    const el = e.currentTarget;
    if (el.scrollHeight - el.scrollTop - el.clientHeight > 400) return;
    setVisibleCardCount((count) => (
      count >= visibleCardTotalRef.current ? count : count + LIST_RENDER_CHUNK
    ));
  }, []);

  const hasListFitFiltersActive = Boolean(
    (viewMode === 'detail' && (lotsSearch.trim() || hasParcelFilters)) ||
      (viewMode === 'sharedDetail' && sharedLotsSearch.trim())
  );

  const lotsToFitInView = useMemo(() => {
    if (viewMode === 'sharedDetail') {
      return hasListFitFiltersActive ? filteredSharedDetailLots : sharedDetailLots;
    }
    if (viewMode === 'detail') {
      return hasListFitFiltersActive ? filteredLots : lots;
    }
    return null;
  }, [
    viewMode,
    hasListFitFiltersActive,
    filteredSharedDetailLots,
    sharedDetailLots,
    filteredLots,
    lots,
  ]);

  const canShowFitInView = Boolean(
    onFitInView &&
      ((viewMode === 'detail' && lots.length > 0) ||
        (viewMode === 'sharedDetail' && sharedDetailLots.length > 0) ||
        (viewMode === 'lists' && (previewListId || previewSharedListId || previewCadSlug)))
  );

  const handleFitInViewClick = useCallback(() => {
    if (previewCadSlug && onCadFitInView) {
      onCadFitInView();
      return;
    }
    if (!onFitInView) return;
    if (Array.isArray(lotsToFitInView) && lotsToFitInView.length > 0) {
      onFitInView(lotsToFitInView);
      return;
    }
    onFitInView();
  }, [onCadFitInView, onFitInView, lotsToFitInView, previewCadSlug]);

  const fitInViewTooltip = hasListFitFiltersActive
    ? 'Encuadrar lotes filtrados en el mapa'
    : viewMode === 'sharedDetail'
      ? 'Encuadrar lotes compartidos en el mapa'
      : 'Encuadrar lotes de la lista en el mapa';

  const renderUnifiedListContent = (
    unifiedLists,
    cadProjects = [],
    { cadGuest = false, interleave = false } = {},
  ) => {
    const mixed = interleave
      ? mergeSharedWithMeItems(unifiedLists, cadProjects)
      : [
        ...unifiedLists.map((row) => ({ kind: 'list', key: `list-${row.list_id || row.id}`, row })),
        ...cadProjects.map((project) => ({
          kind: 'cad',
          key: `cad-${project.project_id || project.id || project.slug}`,
          project,
        })),
      ];

    return (
    <List dense disablePadding sx={listSx}>
      {mixed.map((item, idx) => {
        const isLast = idx === mixed.length - 1;
        if (item.kind === 'cad') {
          const project = item.project;
          const cadBusyKey = `cad-${project?.slug || project?.project_id || project?.id || ''}`;
          return (
            <CadCollectionListRow
              key={item.key}
              project={project}
              isLast={isLast}
              guest={cadGuest}
              spaceMirror={isViewingSharedSpace}
              spaceCanShare={Boolean(listWriteOwnerUserId)}
              onOpen={openCadProject}
              onShare={cadGuest ? undefined : openCadShare}
              onActivity={(project) => {
                const id = getCadProjectId(project);
                if (!id) return;
                setActivityDialogTarget({
                  kind: 'cad',
                  id,
                  name: project?.name || project?.slug || '',
                });
              }}
              onInquiries={SHOW_COLLECTION_LEADS_FROM_LIST_DETAIL ? (project) => {
                const id = getCadProjectId(project);
                if (!id) return;
                setInquiryDialogTarget({
                  kind: 'cad',
                  id,
                  name: project?.name || project?.slug || '',
                });
              } : undefined}
              onLeave={cadGuest ? handleLeaveSharedCadProject : undefined}
              onDelete={cadGuest || isViewingSharedSpace ? undefined : handleDeleteCadProject}
              deleting={deletingCadSlug === (project?.slug || '')}
              actioning={guestShareBusyKey === cadBusyKey}
              onLocate={togglePreviewCadOnMap}
              locateActive={previewCadSlug === (project?.slug || '')}
              locating={previewCadLoadingSlug === (project?.slug || '')}
              canLocate={canLocateCadProjectOnMap(project)}
            />
          );
        }
        const row = item.row;
        const isShared = row._type === 'shared';
        const shareBadge = !isShared ? getShareBadgeType(row) : 'none';
        const isPreviewActive = isShared
          ? previewSharedListId === row.list_id
          : previewListId === row.id;
        const lotCount = Number(row.lot_count ?? row.lotCount ?? 0) || 0;
        const unitCount = Number(row.item_count ?? row.unit_count ?? row.unitCount ?? 0) || 0;
        const hasCountFields = (
          row.lot_count != null || row.lotCount != null ||
          row.item_count != null || row.unit_count != null || row.unitCount != null
        );
        const hasShareToken = Boolean(getShareTokenFromRow(row));
        const canLocateOnMap = isShared
          ? hasShareToken && (!hasCountFields || lotCount > 0 || unitCount > 0)
          : (lotCount > 0 || unitCount > 0);
        const isLocateLoading = isShared
          ? previewSharedLoadingId === row.list_id
          : previewLoadingId === row.id;
        const listBusyKey = `list-${row.list_id}`;

        return (
          <ListItem
            key={item.key}
            disablePadding
            sx={getListItemRootSx(isLast)}
          >
            <Box
              sx={{
                ...getListRowSurfaceSx(false),
                ...(isPreviewActive ? listRowPreviewActiveSx : {}),
              }}
            >
              <Box sx={viewRowBoxSx}>
                <Box sx={viewRowTitleRowSx}>
                  <Typography
                    onClick={() => (isShared ? openSharedListDetail(row) : openListDetail(row))}
                    sx={{ ...listNameTypographySx, cursor: 'pointer' }}
                  >
                    {row.name || 'Lista'}
                  </Typography>

                  <Box sx={rowActionsBoxSx}>
                    <Tooltip
                      title={
                        !canLocateOnMap
                          ? 'No hay lotes ni unidades para ubicar'
                          : isPreviewActive
                            ? 'Ocultar del mapa'
                            : 'Ubicar en el mapa'
                      }
                      placement="top"
                      arrow
                    >
                      <span>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (!canLocateOnMap) return;
                            if (isShared) togglePreviewSharedOnMap(row);
                            else togglePreviewOnMap(row);
                          }}
                          disabled={!canLocateOnMap || isLocateLoading}
                          sx={{
                            color: isPreviewActive && canLocateOnMap ? colors.primary : '#FFFFFF',
                            p: 0.5,
                            '&:hover': {
                              color: colors.primary,
                              bgcolor: 'rgba(255,255,255,0.06)',
                            },
                            '&.Mui-disabled': {
                              color: 'rgba(171,171,171,0.35)',
                            },
                          }}
                          aria-label="Ubicar en el mapa"
                        >
                          {isLocateLoading ? (
                            <CircularProgress size={16} sx={{ color: '#9CA3AF' }} />
                          ) : (
                            <MyLocationOutlinedIcon sx={{ fontSize: 18 }} />
                          )}
                        </IconButton>
                      </span>
                    </Tooltip>

                    <Tooltip title="Más acciones" placement="top" arrow>
                      <IconButton
                        size="small"
                        onClick={(e) => (isShared ? openSharedRowMenu(e, row) : openRowMenu(e, row))}
                        sx={listCardActionIconSx}
                        aria-label="Más acciones"
                      >
                        <MoreVertIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    </Tooltip>

                    {!isShared && (
                      <Menu
                        anchorEl={rowMenuAnchor}
                        open={Boolean(rowMenuAnchor) && rowMenuId === row.id}
                        onClose={closeRowMenu}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                        PaperProps={ACTION_MENU_PAPER_PROPS}
                        MenuListProps={ACTION_MENU_LIST_PROPS}
                      >
                        <ActionMenuItem
                          onClick={() => { closeRowMenu(); setActivityDialogTarget({ kind: 'list', id: row.id, name: row.name }); }}
                          icon={<HistoryOutlinedIcon sx={{ fontSize: 20 }} />}
                          label="Historial"
                        />
                        {SHOW_COLLECTION_LEADS_FROM_LIST_DETAIL ? (
                          <ActionMenuItem
                            onClick={() => { closeRowMenu(); setInquiryDialogTarget({ kind: 'list', id: row.id, name: row.name }); }}
                            icon={<MailOutlineIcon sx={{ fontSize: 20 }} />}
                            label="Leads y consultas"
                          />
                        ) : null}
                        <ActionMenuItem
                          onClick={() => { closeRowMenu(); handleShareToggle(row); }}
                          icon={<ShareIcon sx={{ fontSize: 20 }} />}
                          label="Compartir"
                        />
                        {!isViewingSharedSpace && (
                          <ActionMenuItem
                            onClick={() => { closeRowMenu(); handleDelete(row); }}
                            icon={<DeleteOutlineIcon sx={{ fontSize: 20 }} />}
                            label="Eliminar"
                          />
                        )}
                      </Menu>
                    )}

                    {isShared && (
                      <Menu
                        anchorEl={sharedRowMenuAnchor}
                        open={Boolean(sharedRowMenuAnchor) && sharedRowMenuId === row.list_id}
                        onClose={closeSharedRowMenu}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                        PaperProps={ACTION_MENU_PAPER_PROPS}
                        MenuListProps={ACTION_MENU_LIST_PROPS}
                      >
                        {row.can_edit ? (
                          <>
                            <ActionMenuItem
                              onClick={() => {
                                closeSharedRowMenu();
                                setActivityDialogTarget({
                                  kind: 'list',
                                  id: row.list_id || row.id,
                                  name: row.name,
                                });
                              }}
                              icon={<HistoryOutlinedIcon sx={{ fontSize: 20 }} />}
                              label="Historial"
                            />
                            {SHOW_COLLECTION_LEADS_FROM_LIST_DETAIL ? (
                              <ActionMenuItem
                                onClick={() => {
                                  closeSharedRowMenu();
                                  setInquiryDialogTarget({
                                    kind: 'list',
                                    id: row.list_id || row.id,
                                    name: row.name,
                                  });
                                }}
                                icon={<MailOutlineIcon sx={{ fontSize: 20 }} />}
                                label="Leads y consultas"
                              />
                            ) : null}
                          </>
                        ) : null}
                        <ActionMenuItem
                          onClick={() => {
                            closeSharedRowMenu();
                            handleLeaveSharedWithMeRow(row);
                          }}
                          disabled={guestShareBusyKey === listBusyKey}
                          loading={guestShareBusyKey === listBusyKey}
                          icon={<CloseIcon sx={{ fontSize: 20 }} />}
                          label="Dejar de ver"
                        />
                      </Menu>
                    )}
                  </Box>
                </Box>

                <Box
                  onClick={() => (isShared ? openSharedListDetail(row) : openListDetail(row))}
                  sx={{ ...listCardMetaRowSx, cursor: 'pointer' }}
                >
                  <Typography sx={getListItemCountTypographySx()}>
                    {isShared
                      ? (row.shared_with_me_at
                          ? `Compartida el ${new Date(row.shared_with_me_at).toLocaleDateString()}`
                          : 'Lista compartida')
                      : formatListMembershipCountsLabel({
                          lotCount: row.lot_count ?? 0,
                          unitCount: row.item_count ?? 0,
                          lotContentCount: row.lot_content_count ?? 0,
                        })}
                  </Typography>
                  {isShared && row.can_edit && (
                    <Chip
                      size="small"
                      label="Editor"
                      sx={getListVisibilityBadgeSx('public')}
                    />
                  )}
                  {shareBadge === 'public' && (
                    <Chip
                      size="small"
                      icon={<LanguageIcon />}
                      label="Público"
                      sx={getListVisibilityBadgeSx('public')}
                    />
                  )}
                  {shareBadge === 'restricted' && (
                    <Chip
                      size="small"
                      icon={<LockOutlinedIcon />}
                      label="Restringido"
                      sx={getListVisibilityBadgeSx('private')}
                    />
                  )}
                </Box>
              </Box>
            </Box>
          </ListItem>
        );
      })}
    </List>
    );
  };

  const listLotDock = sharedLotDock || mineListLotDock;
  const activeListUnitId = listLotDock?.userLotId ?? null;
  const activeListLotId = listLotDock && !listLotDock?.userLotId
    ? listLotDock?.lotId
    : null;

  // «Ubicar en el mapa» desde ⋮ de una card del detalle: centra y deja la card
  // marcada (mismo mecanismo y estilo que Registros, `useLocatedLot`). El lote
  // ya está pintado como parte de la lista, así que solo se marca la card.
  const {
    locatedLotId: listLocatedLotId,
    toggleLocatedLot: handleLocateListLot,
    clearLocatedLot: clearListLocatedLot,
    isLotLocated: isListLotLocated,
    isRecordLocated: isListRecordLocated,
  } = useLocatedLot({ onLocate: onLocateLot });
  // Fade arriba / abajo en la lista del detalle (mismo uso que Historial).
  const { scrollRef: listScrollRef, fadeTop: listFadeTop, fadeBottom: listFadeBottom } = useScrollEdgeFade();

  // Cambiar de vista / lista / espacio, o entrar a «Agregar»: se limpia.
  useEffect(() => {
    clearListLocatedLot();
  }, [
    viewMode,
    selectedList?.id,
    selectedSharedListMeta?.list_id,
    addMode,
    isViewingSharedSpace,
    clearListLocatedLot,
  ]);

  // Abrir la ficha (card, UF o click en el mapa) la limpia, como en Registros.
  useEffect(() => {
    if (listLotDock) clearListLocatedLot();
  }, [listLotDock, clearListLocatedLot]);

  // Si la búsqueda o un filtro saca la card (o se elimina de la lista), el lote
  // deja de pintarse y la marca no tiene dónde quedar: se limpia.
  useEffect(() => {
    if (!listLocatedLotId) return;
    const rows = viewMode === 'sharedDetail' ? filteredSharedDetailLots : filteredLots;
    if (rows.some((lot) => String(lot?.lot_id) === listLocatedLotId)) return;
    clearListLocatedLot();
  }, [listLocatedLotId, viewMode, filteredLots, filteredSharedDetailLots, clearListLocatedLot]);
  const listLotDockContextLabel = listLotDock?.listName ? `Lista: ${listLotDock.listName}` : 'Lista';
  const cadListName = selectedCadProject?.name || selectedCadProject?.slug || 'Lista';
  const cadListNameUpper = formatCadProjectDisplayName(cadListName);
  const cadParcelOpen = Boolean(cadParcelLabel);
  const canSeeCadInquiryStats = viewMode === 'cad' && (
    canPublishCadProject(selectedCadProject)
    || canManageCadProject(selectedCadProject)
    || canEditCadCollection(selectedCadProject)
  );
  const canSeeListInquiryStats = viewMode === 'detail'
    || (viewMode === 'sharedDetail' && Boolean(selectedSharedListMeta?.can_edit));
  const inquiryStats = useCollectionInquiryStats(
    canSeeCadInquiryStats
      ? { projectId: getCadProjectId(selectedCadProject) }
      : canSeeListInquiryStats
        ? {
          listId: viewMode === 'sharedDetail'
            ? (selectedSharedListMeta?.list_id || selectedSharedListMeta?.id)
            : selectedList?.id,
        }
        : { enabled: false },
  );
  // Leads F1/F2 (docs/maps/leads.md § Leads y consultas de la lista): un solo
  // getleadcounts para las listas del espacio (comunes + CAD propias), con
  // caché. Lista compartida conmigo (editor solo de lista) → Consultas.
  // Oculto por decisión de la meet del 2026-10-15: no se usa por ahora
  // (utilidad muy puntual). Reactivar si se necesita.
  // (`SHOW_COLLECTION_LEADS_FROM_LIST_DETAIL`).
  const selectedCadProjectId = viewMode === 'cad' ? getCadProjectId(selectedCadProject) : '';
  const cadOwnedProjectIds = useMemo(
    () => cadOwnedProjects.map((project) => getCadProjectId(project)).filter(Boolean),
    [cadOwnedProjects],
  );
  const leadsFocus = SHOW_COLLECTION_LEADS_FROM_LIST_DETAIL && viewMode === 'detail' && selectedList?.id
    ? { kind: 'list', id: selectedList.id }
    : SHOW_COLLECTION_LEADS_FROM_LIST_DETAIL && selectedCadProjectId && cadOwnedProjectIds.includes(selectedCadProjectId)
      ? { kind: 'showroom', id: selectedCadProjectId }
      : null;
  const leadCountIds = orderLeadCountsIds({
    listIds: lists.map((row) => row?.id),
    projectIds: cadOwnedProjectIds,
    focus: leadsFocus,
  });
  const leadCounts = useCollectionLeadCounts({
    ...leadCountIds,
    ownerUserId: listWriteOwnerUserId,
    enabled: SHOW_COLLECTION_LEADS_FROM_LIST_DETAIL,
  });
  const collectionLeadsEntry = resolveCollectionLeadsEntry({
    eligible: Boolean(leadsFocus) && !addMode,
    status: leadCounts.status,
    counts: leadsFocus ? leadCounts.getCounts(leadsFocus.kind, leadsFocus.id) : null,
    canViewLeads: inquiryStats.canViewLeads,
  });
  const detailLeadsProps = collectionLeadsEntry.mode === 'leads' && leadsFocus
    ? {
      unseen: collectionLeadsEntry.unseen,
      onOpen: () => setInquiryDialogTarget({
        kind: leadsFocus.kind === 'showroom' ? 'cad' : 'list',
        id: leadsFocus.id,
        name: leadsFocus.kind === 'showroom'
          ? (selectedCadProject?.name || selectedCadProject?.slug || '')
          : (selectedList?.name || ''),
      }),
    }
    : null;
  const listDetailCounts = getListCountsFromGroupedLots(lots);
  const listDetailCountsLabel = formatRecordsSummary(
    selectedList?.lot_count ?? listDetailCounts.lot_count,
    selectedList?.item_count ?? listDetailCounts.item_count,
    selectedList?.lot_content_count ?? listDetailCounts.lot_content_count,
  );
  // Totales del toolbar (como Registros): con búsqueda / filtros, cuentan lo filtrado.
  // `{ label, tooltip }` de `formatRecordsSummary` («N lotes · M registros»).
  const listDetailHasLocalFilter = Boolean(lotsSearch.trim()) || hasParcelFilters
    || countActiveCommercialFilters(listCommercialFilters) > 0;
  const listDetailToolbarCountsLabel = listDetailHasLocalFilter
    ? (() => {
      const c = getListCountsFromGroupedLots(filteredLots);
      return formatRecordsSummary(c.lot_count, c.item_count, c.lot_content_count);
    })()
    : listDetailCountsLabel;
  const sharedDetailHasLocalFilter = Boolean(sharedLotsSearch.trim())
    || (selectedSharedListMeta?.can_edit && countActiveCommercialFilters(listCommercialFilters) > 0);
  const sharedDetailToolbarCountsLabel = sharedDetailHasLocalFilter
    ? (() => {
      const c = getListCountsFromGroupedLots(filteredSharedDetailLots);
      return formatRecordsSummary(c.lot_count, c.item_count);
    })()
    : null;
  const sharedDetailCountsLabel = formatRecordsSummary(
    sharedDetailCounts.totalLots,
    sharedDetailCounts.totalUnits,
  );
  const sharedDetailToolbarLabel = sharedDetailToolbarCountsLabel || sharedDetailCountsLabel;
  const cadDetailCountsLabel = cadParcelCounts
    ? formatCadParcelsCountsLabel(cadParcelCounts)
    : '';
  const getLotInquiryTotal = useCallback((lot) => (
    getCollectionInquiryItemCounts(
      inquiryStats.byItem,
      lot?.lot_id,
      lot?.user_lot_id,
    )?.total || 0
  ), [inquiryStats.byItem]);
  // Orden comercial: desempata por nombre (orden base estable).
  const sortListLots = useCallback((rows) => (
    isCommercialSort(listSortBy)
      ? sortRecordsCommercial(sortSharedGroupedLots(rows, DEFAULT_LIST_SORT), listSortBy)
      : sortSharedGroupedLots(rows, listSortBy, { getInquiryTotal: getLotInquiryTotal })
  ), [listSortBy, getLotInquiryTotal]);
  const sortedFilteredLots = useMemo(
    () => sortListLots(filteredLots),
    [filteredLots, sortListLots],
  );
  /** El bloque llega solo si se gestiona la lista (dueño, editor o invitado del espacio). */
  const listDetailHasCommercial = useMemo(
    () => lots.some((lot) => getRecordCommercialBlock(lot) != null),
    [lots],
  );
  const sortedSharedDetailLots = useMemo(
    () => (
      selectedSharedListMeta?.can_edit
        ? sortListLots(filteredSharedDetailLots)
        : filteredSharedDetailLots
    ),
    [filteredSharedDetailLots, sortListLots, selectedSharedListMeta?.can_edit],
  );
  const visibleFilteredLots = useMemo(
    () => sortedFilteredLots.slice(0, visibleCardCount),
    [sortedFilteredLots, visibleCardCount],
  );
  const visibleSharedDetailLots = useMemo(
    () => sortedSharedDetailLots.slice(0, visibleCardCount),
    [sortedSharedDetailLots, visibleCardCount],
  );
  // Vista por registro: una card por lote (contenido / ítem directo) y por UF.
  // Parte de los lotes ya filtrados y ordenados; filtros y órdenes comerciales y
  // de precio / superficie / consultas se aplican por card.
  const getRecordInquiryTotal = useCallback((item) => (
    getCollectionInquiryItemCounts(
      inquiryStats.byItem,
      item?.record?.user_lot_id || item?.lotId,
    )?.total || 0
  ), [inquiryStats.byItem]);
  const sortRecordItems = useCallback((items) => (
    isCommercialSort(listSortBy)
      ? sortRecordsCommercial(items, listSortBy)
      : sortRecordCardItems(items, listSortBy, { getInquiryTotal: getRecordInquiryTotal })
  ), [listSortBy, getRecordInquiryTotal]);
  // Fuente `api` (recordsSource): getmyrecords?list_id= para la lista propia (o
  // del espacio, como editor). La compartida conmigo sigue con getsharedlist.
  const { cards: listApiRecordCards } = useApiRecordCards({
    enabled: isRecordsApiSource() && !isRecordGrouped && viewMode === 'detail' && Boolean(selectedList?.id),
    ready: !loadingLots,
    listId: selectedList?.id || null,
    ownerUserId: spaceOwnerIdForFetch || null,
    includeCad: false,
    reloadKey: `${lots.length}:${lots.reduce((n, lot) => n + getDisplayUnits(lot).length, 0)}`,
  });
  const listDetailRecordItems = useMemo(() => {
    if (isRecordGrouped || viewMode !== 'detail') return [];
    const items = buildRecordCardItems(sortedFilteredLots, { apiCards: listApiRecordCards });
    const searched = hasLotsSearch
      ? items.filter((item) => recordItemMatchesSearch(item, lotsSearchTerms))
      : items;
    return sortRecordItems(filterRecordsByCommercial(searched, listCommercialFilters));
  }, [isRecordGrouped, viewMode, sortedFilteredLots, listCommercialFilters, sortRecordItems, listApiRecordCards, hasLotsSearch, lotsSearchTerms]);
  // Lotes que se pintan en el mapa: en la vista por registro con búsqueda, los de
  // las cards que matchean; si no, los filtrados.
  const detailHighlightLots = useMemo(() => {
    if (isRecordGrouped || !hasLotsSearch) return filteredLots;
    const lotIds = new Set(listDetailRecordItems.map((item) => String(item.lotId)).filter(Boolean));
    return filteredLots.filter((lot) => lotIds.has(String(lot.lot_id)));
  }, [isRecordGrouped, hasLotsSearch, filteredLots, listDetailRecordItems]);
  filteredLotsRef.current = detailHighlightLots;

  useEffect(() => {
    if (viewMode !== 'detail' || addMode) return undefined;
    emitHighlightLots(detailHighlightLots);
    return undefined;
  }, [viewMode, addMode, detailHighlightLots, emitHighlightLots]);
  const sharedDetailRecordItems = useMemo(() => {
    if (isRecordGrouped || viewMode !== 'sharedDetail') return [];
    const items = buildRecordCardItems(sortedSharedDetailLots);
    // El visitante no tiene bloque comercial ni ve esos filtros / órdenes.
    if (!selectedSharedListMeta?.can_edit) return items;
    return sortRecordItems(filterRecordsByCommercial(items, listCommercialFilters));
  }, [
    isRecordGrouped,
    viewMode,
    sortedSharedDetailLots,
    selectedSharedListMeta?.can_edit,
    listCommercialFilters,
    sortRecordItems,
  ]);
  const visibleListRecordItems = useMemo(
    () => listDetailRecordItems.slice(0, visibleCardCount),
    [listDetailRecordItems, visibleCardCount],
  );
  const visibleSharedRecordItems = useMemo(
    () => sharedDetailRecordItems.slice(0, visibleCardCount),
    [sharedDetailRecordItems, visibleCardCount],
  );

  const detailHoverActive = (viewMode === 'detail' || viewMode === 'sharedDetail') && !addMode;
  const hoveredDetailLotId = detailHoverActive && mapHoveredLotId != null
    ? String(mapHoveredLotId)
    : null;

  useEffect(() => {
    onListDetailHoverActive?.(detailHoverActive);
    return () => onListDetailHoverActive?.(false);
  }, [detailHoverActive, onListDetailHoverActive]);

  useEffect(() => {
    if (!hoveredDetailLotId) return undefined;
    const rows = viewMode === 'sharedDetail'
      ? (isRecordGrouped ? sortedSharedDetailLots : sharedDetailRecordItems)
      : (isRecordGrouped ? sortedFilteredLots : listDetailRecordItems);
    const index = rows.findIndex((row) => String(row?.lotId ?? row?.lot_id) === hoveredDetailLotId);
    if (index < 0) return undefined;
    setVisibleCardCount((count) => Math.max(
      count,
      Math.ceil((index + 1) / LIST_RENDER_CHUNK) * LIST_RENDER_CHUNK,
    ));
    return undefined;
  }, [
    hoveredDetailLotId,
    viewMode,
    isRecordGrouped,
    sortedSharedDetailLots,
    sharedDetailRecordItems,
    sortedFilteredLots,
    listDetailRecordItems,
  ]);

  useEffect(() => {
    if (!hoveredDetailLotId) return undefined;
    const target = document.querySelector(`[data-list-lot-id="${CSS.escape(hoveredDetailLotId)}"]`);
    if (!target) return undefined;
    const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
    target.scrollIntoView?.({ block: 'nearest', behavior: reduceMotion ? 'auto' : 'smooth' });
    return undefined;
  }, [
    hoveredDetailLotId,
    visibleListRecordItems,
    visibleSharedRecordItems,
    visibleFilteredLots,
    visibleSharedDetailLots,
  ]);

  const handleDetailCardHover = useCallback((payload) => {
    onListRecordHover?.(payload?.lotId ? payload : null);
  }, [onListRecordHover]);

  const handleDetailLotHover = useCallback((lotId) => {
    onListRecordHover?.(lotId ? { lotId: String(lotId), item: null } : null);
  }, [onListRecordHover]);
  if (!isRecordGrouped) {
    visibleCardTotalRef.current = viewMode === 'sharedDetail'
      ? sharedDetailRecordItems.length
      : listDetailRecordItems.length;
  }
  // Totales con filtros locales en la vista por registro: cuentan las cards visibles.
  const formatRecordItemsCounts = (items, withContent = true) => {
    const c = countRecordCardItems(items);
    return withContent
      ? formatRecordsSummary(c.lots, c.units, c.lotContent)
      : formatRecordsSummary(c.lots, c.units);
  };
  const listDetailCountsText = !isRecordGrouped && listDetailHasLocalFilter
    ? formatRecordItemsCounts(listDetailRecordItems)
    : listDetailToolbarCountsLabel;
  const sharedDetailCountsText = !isRecordGrouped && sharedDetailHasLocalFilter
    ? formatRecordItemsCounts(sharedDetailRecordItems, false)
    : sharedDetailToolbarLabel;
  const {
    headerToOrigin: listsHeaderBackToOrigin,
    headerBackTitle: listsHeaderBackTitle,
    cadParcelToOrigin,
    cadParcelBackTitle,
    cadParcelSubtitle,
  } = resolveListsPanelReturnOrigin({
    hasOrigin: Boolean(onReturnToOrigin),
    originLabel: returnOriginLabel,
    viewMode,
    addMode,
    openedAtCadParcel: Boolean(openCadDetailRequest?.parcelId || openCadDetailRequest?.parcelLabel),
    cadListNameUpper,
  });

  // Sin ninguna colección: se oculta el buscador. Mis listas igual se pinta
  // (ahí está el +). Compartidas no se muestra si está vacía.
  const isListsPanelEmpty =
    viewMode === 'lists' &&
    !loadingLists &&
    !loadingCadCollections &&
    ((listsPanelTab === 'all' && !loadingSharedWithMe && lists.length === 0 && sharedWithMeLists.length === 0
      && cadOwnedProjects.length === 0 && cadSharedProjects.length === 0) ||
      (listsPanelTab === 'mine' && lists.length === 0 && cadOwnedProjects.length === 0));

  const openCreateListFromEmpty = () => {
    setNewListKind('manual');
    setCadWorkLocksEmptyList(false);
    setShowCreateListForm(true);
  };

  const closeCreateListForm = () => {
    if (creating) return;
    setShowCreateListForm(false);
    setNewName('');
    setNewIsPublic(false);
    setNewListKind('manual');
    setCadWorkLocksEmptyList(false);
  };

  /**
   * Lista creada desde un plano: se refresca el índice, se abre su detalle y
   * se ofrece Referenciar de entrada. Cancelar la deja en canvas.
   */
  const handleCadListCreated = (project) => {
    setShowCreateListForm(false);
    setNewListKind('manual');
    setCadWorkLocksEmptyList(false);
    setNewName('');
    reloadCadCollections();
    if (!project) return;
    // Recién creada: siempre plano en metros, sin georreferenciar (contrato del alta).
    openCadProject({
      ...project,
      display_mode: project.display_mode || 'canvas',
      srid: project.srid ?? 0,
    }, { startGeoref: true });
  };

  // DockPanel arma `{ ...view, source: 'dock', anchorId }`; acá la ficha vive en Listas.
  const listDockShowMarketComparables = onShowMarketComparables
    ? (view) => onShowMarketComparables({ ...view, source: 'lists' })
    : undefined;

  return (
    <>
      {listLotDock && (
        <DockPanel
          uiLeftInset={uiLeftInset}
          polygonInfo={listLotDock.polygonInfo}
          listContextLabel={listLotDockContextLabel}
          restrictToListId={listLotDock.kind === 'mine' ? listLotDock.listId : null}
          initialUserLotId={listLotDock.kind === 'mine' ? listLotDock.userLotId : null}
          readOnly={listLotDock.kind === 'shared'}
          collectionCanEdit={listLotDock.kind === 'shared' && selectedSharedListMeta?.can_edit === true}
          sharedToken={listLotDock.token || ''}
          sharedUserLotId={listLotDock.kind === 'shared' ? listLotDock.userLotId : null}
          sharedListUnits={listLotDock.kind === 'shared' ? (listLotDock.sharedListUnits || EMPTY_SHARED_LIST_UNITS) : EMPTY_SHARED_LIST_UNITS}
          skipSharedLotDetailFetch={listLotDock.kind === 'shared'}
          sharedLotDetail={listLotDock.kind === 'shared' ? sharedDockDetail : null}
          lotDetailLoading={listLotDock.kind === 'shared' ? sharedDockDetailLoading : false}
          onClose={closeListLotDock}
          lotBuildingDisplayMode={lotBuildingDisplayMode}
          onLotBuildingDisplayModeChange={onLotBuildingDisplayModeChange}
          lotBuildingStatus={lotBuildingStatus}
          // «Ver en el mapa» de la comparación de mercado (lote / UF de una lista propia). El
          // modo vive en MapPage con `source: 'lists'`: sale si se cierra Listas o se abre un
          // lote en el Dock principal; al salir vuelve esta ficha (Listas queda montada).
          onShowMarketComparables={listLotDock.kind === 'mine' ? listDockShowMarketComparables : undefined}
        />
      )}

      {/* El panel queda montado (oculto) mientras el dock está abierto:
          cerrar el lote vuelve a la lista exactamente donde estaba (scroll incluido). */}
      <PanelShell uiLeftInset={uiLeftInset} onClose={onClose} hidden={Boolean(listLotDock)}>
      {({ snap }) => (
    <Paper
      sx={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        bgcolor: PANEL_BG,
        color: colors.textPrimary,
        boxShadow: '0 18px 45px rgba(15,23,42,0.55)',
        border: `1px solid ${PANEL_BORDER}`,
        borderRadius: { xs: 0, sm: '16px' },
      }}
    >
      <Box sx={{ bgcolor: PANEL_BG }}>
        <PanelHeader
          startAdornment={
            (viewMode === 'detail' || viewMode === 'sharedDetail' || (viewMode === 'cad' && !cadParcelOpen)) ? (
              <Tooltip
                title={listsHeaderBackTitle}
                placement="top"
                arrow
              >
                <IconButton
                  onClick={
                    listsHeaderBackToOrigin
                      ? onReturnToOrigin
                      : viewMode === 'sharedDetail'
                      ? backFromSharedDetail
                      : viewMode === 'cad'
                        ? backFromCad
                      : addMode
                        ? (addStep === 'review' ? () => setAddStep('pick') : closeAddMode)
                        : backToLists
                  }
                  size="small"
                  aria-label={listsHeaderBackTitle}
                  sx={{
                    color: '#E8F4F4',
                    p: 0.5,
                    mr: 0.25,
                    '&:hover': { color: '#FFFFFF', bgcolor: 'rgba(255,255,255,0.06)' },
                  }}
                >
                  <ArrowBackIcon sx={{ fontSize: 20 }} />
                </IconButton>
              </Tooltip>
            ) : null
          }
          title={
            viewMode === 'sharedDetail' ? (
              'LISTA'
            ) : viewMode === 'cad' && cadParcelOpen ? (
              `DETALLE ${cadParcelLabel.toUpperCase()}`
            ) : viewMode === 'cad' ? (
              'LISTA'
            ) : viewMode === 'detail' ? (
              addMode
                ? (addStep === 'review' ? 'REVISAR SELECCIÓN' : 'AGREGAR LOTES Y UNIDADES')
                : 'LISTA'
            ) : showCreateListForm ? (
              'NUEVA LISTA'
            ) : (
              'LISTAS'
            )
          }
          subtitle={
            viewMode === 'cad' && cadParcelOpen
              ? cadParcelSubtitle
              : viewMode === 'cad'
              ? (canManageCadProject(selectedCadProject) || canEditCadCollection(selectedCadProject)
                ? 'Gestiona tus lotes y unidades'
                : 'Solo lectura')
              : viewMode === 'lists' && !showCreateListForm
              ? (isViewingSharedSpace
                ? resolveSpaceGuestListsSubtitle(activeSpace)
                : 'Creá listas personalizadas para ordenar tus lotes y unidades.')
              : viewMode === 'detail' && addMode && addStep !== 'review'
                ? 'Seleccioná cómo querés agregar lotes y unidades a esta lista.'
                : viewMode === 'detail' && !addMode
                  ? 'Gestiona tus lotes y unidades'
                  : undefined
          }
          description={
            viewMode === 'sharedDetail' ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 1.25,
                    px: 1.5,
                    py: 1.25,
                    height: 60,
                    borderRadius: '8px',
                    border: `2px solid ${colors.infoBlue}`,
                    bgcolor: colors.backgroundDark,
                  }}
                >
                  <Box
                    sx={{
                      width: 30,
                      height: 30,
                      bgcolor: '#0C4BB5',
                      borderRadius: '8px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                      mt: '1px',
                    }}
                  >
                    <InfoOutlinedIcon sx={{ fontSize: 18, color: '#fff' }} />
                  </Box>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography
                      sx={{
                        fontSize: '13px',
                        fontWeight: 700,
                        lineHeight: 1.35,
                        color: '#F9FAFB',
                        fontFamily: typography.fontFamily.primary,
                      }}
                    >
                      {selectedSharedListMeta?.can_edit ? 'Editor de esta lista' : 'Lista compartida.'}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: '13px',
                        fontWeight: 400,
                        lineHeight: 1.35,
                        color: 'rgba(255,255,255,0.75)',
                        fontFamily: typography.fontFamily.primary,
                      }}
                    >
                      {selectedSharedListMeta?.can_edit
                        ? 'Podés cambiar el contenido.'
                        : 'Solo lectura'}
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                  <Typography sx={detailListNameSx}>
                    {sharedDetailListName || selectedSharedListMeta?.name || 'Compartida'}
                  </Typography>
                  <ListDetailInquiryMetaRow
                    countsLabel=""
                    total={SHOW_COLLECTION_LEADS_FROM_LIST_DETAIL && selectedSharedListMeta?.can_edit ? inquiryStats.total : 0}
                    unseen={SHOW_COLLECTION_LEADS_FROM_LIST_DETAIL && selectedSharedListMeta?.can_edit ? inquiryStats.unseenTotal : 0}
                    sx={{ pl: 1 }}
                    onOpenInquiries={SHOW_COLLECTION_LEADS_FROM_LIST_DETAIL && selectedSharedListMeta?.can_edit ? () => {
                      setInquiryDialogTarget({
                        kind: 'list',
                        id: selectedSharedListMeta.list_id || selectedSharedListMeta.id,
                        name: sharedDetailListName || selectedSharedListMeta.name,
                      });
                    } : undefined}
                  />
                </Box>
              </Box>
            ) : viewMode === 'detail' ? (
              addMode ? (
                <Typography
                  sx={detailListNameSx}
                  title={selectedList?.name || 'Lista'}
                >
                  {selectedList?.name || 'Lista'}
                </Typography>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%', minWidth: 0 }}>
                  <Box sx={detailListNameRowSx}>
                    <Box sx={detailListNamePrimarySx}>
                      {editingId === selectedList?.id ? (
                        <TextField
                          autoFocus
                          size="small"
                          fullWidth
                          value={editName}
                          onChange={(e) => setEditName(e.target.value)}
                          onKeyDown={(e) => {
                            if (selectedList?.id) handleEditKeyDown(e, selectedList.id);
                          }}
                          sx={detailListNameEditFieldSx}
                          inputProps={{ maxLength: 80 }}
                        />
                      ) : (
                        <Typography
                          sx={detailListNameSx}
                          title={selectedList?.name || 'Lista'}
                        >
                          {selectedList?.name || 'Lista'}
                        </Typography>
                      )}
                    </Box>
                    <Box sx={detailListNameActionsSx}>
                        {editingId === selectedList?.id ? (
                          <>
                            <Tooltip title="Guardar" placement="top" arrow>
                              <span>
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    if (selectedList?.id) saveEdit(selectedList.id);
                                  }}
                                  disabled={savingId === selectedList?.id || !editName.trim()}
                                  sx={saveEditIconButtonSx}
                                >
                                  {savingId === selectedList?.id ? (
                                    <CircularProgress size={16} sx={saveEditProgressSx} />
                                  ) : (
                                    <CheckIcon sx={{ fontSize: 17 }} />
                                  )}
                                </IconButton>
                              </span>
                            </Tooltip>
                            <Tooltip title="Cancelar" placement="top" arrow>
                              <IconButton size="small" onClick={cancelEdit} sx={cancelEditIconButtonSx}>
                                <CloseIcon sx={{ fontSize: 17 }} />
                              </IconButton>
                            </Tooltip>
                          </>
                        ) : (
                          <Button
                            variant="contained"
                            startIcon={<AddIcon sx={{ fontSize: '16px !important' }} />}
                            onClick={openAddLotsInline}
                            sx={{ ...detailAddButtonSx, width: '100%' }}
                          >
                            Agregar
                          </Button>
                        )}
                    </Box>
                  </Box>
                  <ListDetailInquiryMetaRow
                    countsLabel=""
                    total={SHOW_COLLECTION_LEADS_FROM_LIST_DETAIL ? inquiryStats.total : 0}
                    unseen={SHOW_COLLECTION_LEADS_FROM_LIST_DETAIL ? inquiryStats.unseenTotal : 0}
                    sx={{ pl: 1 }}
                    leads={SHOW_COLLECTION_LEADS_FROM_LIST_DETAIL ? detailLeadsProps : null}
                    onOpenInquiries={SHOW_COLLECTION_LEADS_FROM_LIST_DETAIL ? () => {
                      if (!selectedList) return;
                      setInquiryDialogTarget({
                        kind: 'list',
                        id: selectedList.id,
                        name: selectedList.name,
                      });
                    } : undefined}
                  />
                </Box>
              )
            ) : viewMode === 'cad' && !cadParcelOpen ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%', minWidth: 0 }}>
                <Box sx={cadDetailNameRowSx}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    {editingCadSlug === selectedCadProject?.slug ? (
                      <TextField
                        autoFocus
                        size="small"
                        fullWidth
                        value={cadEditName}
                        onChange={(e) => setCadEditName(e.target.value)}
                        onKeyDown={(e) => {
                          if (selectedCadProject?.slug) handleCadEditKeyDown(e, selectedCadProject.slug);
                        }}
                        sx={detailListNameEditFieldSx}
                        inputProps={{ maxLength: CAD_PROJECT_NAME_MAX }}
                      />
                    ) : (
                      <Typography
                        sx={cadDetailNameSx}
                        title={selectedCadProject?.name || selectedCadProject?.slug || ''}
                      >
                        {selectedCadProject?.name || selectedCadProject?.slug || 'Lista'}
                      </Typography>
                    )}
                  </Box>
                  {editingCadSlug === selectedCadProject?.slug ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexShrink: 0 }}>
                      <Tooltip title="Guardar" placement="top" arrow>
                        <span>
                          <IconButton
                            size="small"
                            onClick={() => {
                              if (selectedCadProject?.slug) saveCadEdit(selectedCadProject.slug);
                            }}
                            disabled={
                              savingCadSlug === selectedCadProject?.slug
                              || !cadEditName.trim()
                            }
                            sx={saveEditIconButtonSx}
                          >
                            {savingCadSlug === selectedCadProject?.slug ? (
                              <CircularProgress size={16} sx={saveEditProgressSx} />
                            ) : (
                              <CheckIcon sx={{ fontSize: 17 }} />
                            )}
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Cancelar" placement="top" arrow>
                        <IconButton size="small" onClick={cancelCadEdit} sx={cancelEditIconButtonSx}>
                          <CloseIcon sx={{ fontSize: 17 }} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  ) : null}
                </Box>
                <ListDetailInquiryMetaRow
                  countsLabel={cadDetailCountsLabel}
                  total={SHOW_COLLECTION_LEADS_FROM_LIST_DETAIL ? inquiryStats.total : 0}
                  unseen={SHOW_COLLECTION_LEADS_FROM_LIST_DETAIL ? inquiryStats.unseenTotal : 0}
                  sx={{ mt: 0.25 }}
                  leads={SHOW_COLLECTION_LEADS_FROM_LIST_DETAIL ? detailLeadsProps : null}
                  onOpenInquiries={SHOW_COLLECTION_LEADS_FROM_LIST_DETAIL ? () => {
                    const id = getCadProjectId(selectedCadProject);
                    if (!id) return;
                    setInquiryDialogTarget({
                      kind: 'cad',
                      id,
                      name: selectedCadProject?.name || selectedCadProject?.slug || '',
                    });
                  } : undefined}
                />
              </Box>
            ) : undefined
          }
          actions={
            showCreateListForm && viewMode === 'lists' ? (
              <IconButton
                onClick={closeCreateListForm}
                aria-label="Cerrar"
                disabled={creating}
                sx={panelCloseIconButtonSx}
              >
                <CloseIcon />
              </IconButton>
            ) : (
            <>
              {canShowFitInView && (viewMode === 'lists' || viewMode === 'sharedDetail') && (
                <Tooltip title={fitInViewTooltip} placement="bottom" arrow>
                  <IconButton
                    onClick={handleFitInViewClick}
                    size="small"
                    sx={{
                      color: '#9CA3AF',
                      '&:hover': { color: '#F9FAFB', bgcolor: 'rgba(255,255,255,0.06)' },
                    }}
                  >
                    <FitScreenIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              {viewMode === 'sharedDetail' && selectedSharedListMeta?.can_edit && (
                <>
                  <Tooltip title="Acciones de la lista" placement="top" arrow>
                    <IconButton
                      onClick={openDetailMenu}
                      size="small"
                      sx={{
                        color: '#E8F4F4',
                        '&:hover': { color: '#FFFFFF', bgcolor: 'rgba(255,255,255,0.06)' },
                      }}
                    >
                      <MoreVertIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Menu
                    anchorEl={detailMenuAnchor}
                    open={Boolean(detailMenuAnchor)}
                    onClose={closeDetailMenu}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                    PaperProps={ACTION_MENU_PAPER_PROPS}
                    MenuListProps={ACTION_MENU_LIST_PROPS}
                  >
                    <ActionMenuItem
                      onClick={() => {
                        closeDetailMenu();
                        setActivityDialogTarget({
                          kind: 'list',
                          id: selectedSharedListMeta.list_id || selectedSharedListMeta.id,
                          name: sharedDetailListName || selectedSharedListMeta.name,
                        });
                      }}
                      icon={<HistoryOutlinedIcon sx={{ fontSize: 20 }} />}
                      label="Historial"
                    />
                    {SHOW_COLLECTION_LEADS_FROM_LIST_DETAIL ? (
                      <ActionMenuItem
                        onClick={() => {
                          closeDetailMenu();
                          setInquiryDialogTarget({
                            kind: 'list',
                            id: selectedSharedListMeta.list_id || selectedSharedListMeta.id,
                            name: sharedDetailListName || selectedSharedListMeta.name,
                          });
                        }}
                        icon={<MailOutlineIcon sx={{ fontSize: 20 }} />}
                        label="Leads y consultas"
                      />
                    ) : null}
                  </Menu>
                </>
              )}
              {viewMode === 'detail' && !addMode && (
                <>
                  <Tooltip title="Acciones de la lista" placement="top" arrow>
                    <IconButton
                      onClick={openDetailMenu}
                      size="small"
                      sx={{
                        color: '#E8F4F4',
                        '&:hover': { color: '#FFFFFF', bgcolor: 'rgba(255,255,255,0.06)' },
                      }}
                    >
                      <MoreVertIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Menu
                    anchorEl={detailMenuAnchor}
                    open={Boolean(detailMenuAnchor)}
                    onClose={closeDetailMenu}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                    PaperProps={ACTION_MENU_PAPER_PROPS}
                    MenuListProps={ACTION_MENU_LIST_PROPS}
                  >
                    {canShowFitInView && (
                      <ActionMenuItem
                        onClick={() => {
                          closeDetailMenu();
                          handleFitInViewClick();
                        }}
                        icon={<FitScreenIcon sx={{ fontSize: 20 }} />}
                        label="Encuadrar en mapa"
                      />
                    )}
                    <ActionMenuItem
                      onClick={() => {
                        closeDetailMenu();
                        if (selectedList) {
                          setActivityDialogTarget({
                            kind: 'list',
                            id: selectedList.id,
                            name: selectedList.name,
                          });
                        }
                      }}
                      disabled={!selectedList}
                      icon={<HistoryOutlinedIcon sx={{ fontSize: 20 }} />}
                      label="Historial"
                    />
                    {SHOW_COLLECTION_LEADS_FROM_LIST_DETAIL ? (
                      <ActionMenuItem
                        onClick={() => {
                          closeDetailMenu();
                          if (selectedList) {
                            setInquiryDialogTarget({
                              kind: 'list',
                              id: selectedList.id,
                              name: selectedList.name,
                            });
                          }
                        }}
                        disabled={!selectedList}
                        icon={<MailOutlineIcon sx={{ fontSize: 20 }} />}
                        label="Leads y consultas"
                      />
                    ) : null}
                    <ActionMenuItem
                      onClick={() => {
                        closeDetailMenu();
                        if (selectedList) startEdit(selectedList);
                      }}
                      disabled={!selectedList}
                      icon={<EditIcon sx={{ fontSize: 20 }} />}
                      label="Renombrar"
                    />
                    <ActionMenuItem
                      onClick={() => {
                        closeDetailMenu();
                        if (selectedList) handleShareToggle(selectedList);
                      }}
                      disabled={!selectedList}
                      icon={<ShareIcon sx={{ fontSize: 20 }} />}
                      label="Compartir"
                    />
                    {!isViewingSharedSpace && (
                      <Tooltip
                        title={selectedList && isDeleteDisabled(selectedList) ? getDeleteTooltip(selectedList) : ''}
                        placement="left"
                        arrow
                        disableHoverListener={!selectedList || !isDeleteDisabled(selectedList)}
                      >
                        <span>
                          <ActionMenuItem
                            onClick={() => {
                              closeDetailMenu();
                              if (selectedList) handleDelete(selectedList);
                            }}
                            disabled={!selectedList || isDeleteDisabled(selectedList)}
                            loading={selectedList && deletingId === selectedList.id}
                            icon={<DeleteOutlineIcon sx={{ fontSize: 20 }} />}
                            label="Eliminar"
                          />
                        </span>
                      </Tooltip>
                    )}
                  </Menu>
                </>
              )}
              {viewMode === 'cad' && cadParcelOpen && (
                <Tooltip title={cadParcelBackTitle} placement="top" arrow>
                  <IconButton
                    onClick={() => {
                      if (cadParcelToOrigin) onReturnToOrigin();
                      else cadDeselectParcelRef.current?.();
                    }}
                    size="small"
                    aria-label={cadParcelBackTitle}
                    sx={{
                      color: '#E8F4F4',
                      p: 0.5,
                      '&:hover': { color: '#FFFFFF', bgcolor: 'rgba(255,255,255,0.06)' },
                    }}
                  >
                    <ArrowBackIcon sx={{ fontSize: 20 }} />
                  </IconButton>
                </Tooltip>
              )}
              {viewMode === 'cad' && !cadParcelOpen && !cadGeorefOpen && (
                <>
                  <Tooltip title="Acciones de la lista" placement="top" arrow>
                    <IconButton
                      onClick={openDetailMenu}
                      size="small"
                      sx={{
                        color: '#E8F4F4',
                        '&:hover': { color: '#FFFFFF', bgcolor: 'rgba(255,255,255,0.06)' },
                      }}
                    >
                      <MoreVertIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Menu
                    anchorEl={detailMenuAnchor}
                    open={Boolean(detailMenuAnchor)}
                    onClose={closeDetailMenu}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                    PaperProps={ACTION_MENU_PAPER_PROPS}
                    MenuListProps={ACTION_MENU_LIST_PROPS}
                  >
                    <ActionMenuItem
                      onClick={() => {
                        closeDetailMenu();
                        onCadFitInView?.();
                      }}
                      icon={<FitScreenIcon sx={{ fontSize: 20 }} />}
                      label="Encuadrar en mapa"
                    />
                    {(canPublishCadProject(selectedCadProject)
                      || canManageCadProject(selectedCadProject)
                      || canEditCadCollection(selectedCadProject)) ? (
                      <ActionMenuItem
                        onClick={() => {
                          closeDetailMenu();
                          const id = getCadProjectId(selectedCadProject);
                          if (!id) return;
                          setActivityDialogTarget({
                            kind: 'cad',
                            id,
                            name: selectedCadProject?.name || selectedCadProject?.slug || '',
                          });
                        }}
                        icon={<HistoryOutlinedIcon sx={{ fontSize: 20 }} />}
                        label="Historial"
                      />
                    ) : null}
                    {SHOW_COLLECTION_LEADS_FROM_LIST_DETAIL
                      && (canPublishCadProject(selectedCadProject)
                        || canManageCadProject(selectedCadProject)
                        || canEditCadCollection(selectedCadProject)) ? (
                      <ActionMenuItem
                        onClick={() => {
                          closeDetailMenu();
                          const id = getCadProjectId(selectedCadProject);
                          if (!id) return;
                          setInquiryDialogTarget({
                            kind: 'cad',
                            id,
                            name: selectedCadProject?.name || selectedCadProject?.slug || '',
                          });
                        }}
                        icon={<MailOutlineIcon sx={{ fontSize: 20 }} />}
                        label="Leads y consultas"
                      />
                    ) : null}
                    {(canPublishCadProject(selectedCadProject)
                      || canManageCadProject(selectedCadProject)
                      || canEditCadCollection(selectedCadProject)) ? (
                      <ActionMenuItem
                        onClick={() => {
                          closeDetailMenu();
                          cadStartPinsRef.current?.();
                        }}
                        icon={<PushPinOutlinedIcon sx={{ fontSize: 20 }} />}
                        label="Pines"
                      />
                    ) : null}
                    {/* F3-CAD: en espacio ajeno solo Compartir (Referenciar,
                        Renombrar y Eliminar siguen siendo del dueño). */}
                    {listWriteOwnerUserId && !canPublishCadProject(selectedCadProject) ? (
                      <ActionMenuItem
                        onClick={() => {
                          closeDetailMenu();
                          openCadShare(selectedCadProject);
                        }}
                        icon={<ShareIcon sx={{ fontSize: 20 }} />}
                        label="Compartir"
                      />
                    ) : null}
                    {canPublishCadProject(selectedCadProject) ? (
                      <>
                        <ActionMenuItem
                          onClick={() => {
                            closeDetailMenu();
                            cadStartGeorefRef.current?.();
                          }}
                          icon={hasCadGeorefAnchor(selectedCadProject)
                            ? <EditLocationAltOutlinedIcon sx={{ fontSize: 20 }} />
                            : <AddLocationAltOutlinedIcon sx={{ fontSize: 20 }} />}
                          label={hasCadGeorefAnchor(selectedCadProject)
                            ? 'Editar referencia'
                            : 'Referenciar'}
                        />
                        <ActionMenuItem
                          onClick={() => {
                            closeDetailMenu();
                            if (selectedCadProject) startCadEdit(selectedCadProject);
                          }}
                          disabled={!selectedCadProject}
                          icon={<EditIcon sx={{ fontSize: 20 }} />}
                          label="Renombrar"
                        />
                        <ActionMenuItem
                          onClick={() => {
                            closeDetailMenu();
                            openCadShare(selectedCadProject);
                          }}
                          icon={<ShareIcon sx={{ fontSize: 20 }} />}
                          label="Compartir"
                        />
                        {!isViewingSharedSpace && (
                          <ActionMenuItem
                            onClick={() => {
                              closeDetailMenu();
                              if (selectedCadProject) handleDeleteCadProject(selectedCadProject);
                            }}
                            disabled={!selectedCadProject || deletingCadSlug === selectedCadProject?.slug}
                            loading={deletingCadSlug === selectedCadProject?.slug}
                            icon={<DeleteOutlineIcon sx={{ fontSize: 20 }} />}
                            label="Eliminar"
                          />
                        )}
                      </>
                    ) : null}
                  </Menu>
                </>
              )}
              <IconButton
                onClick={handleClose}
                aria-label="Cerrar"
                sx={panelCloseIconButtonSx}
              >
                <CloseIcon />
              </IconButton>
            </>
            )
          }
        />
      </Box>

      {(snap === 'expanded' || snap === undefined) && (
        <>
          {viewMode === 'lists' ? (
            !showCreateListForm && !isListsPanelEmpty && (
            <Box sx={{ ...fixedTopZoneSx, pt: 2.5, pb: 0 }}>
              <Box sx={listFiltersBoxSx}>
                <Box sx={listSearchToolbarSx}>
                  <TextField
                    size="small"
                    placeholder="Buscar lista por nombre"
                    value={listSearch}
                    onChange={(e) => setListSearch(e.target.value)}
                    sx={{ ...listSearchFieldSx, mt: 0 }}
                    inputProps={{ maxLength: 80 }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon sx={listSearchIconSx} />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Box>
              </Box>
            </Box>
            )
          ) : viewMode === 'sharedDetail' ? (
            <Box sx={fixedTopZoneSx}>
              <Box sx={{ ...listFiltersBoxSx, gridTemplateColumns: { xs: '1fr', sm: '1fr' } }}>
                <TextField
                  size="small"
                  placeholder="Buscar por título, dirección o barrio..."
                  value={sharedLotsSearch}
                  onChange={(e) => setSharedLotsSearch(e.target.value)}
                  sx={selectedSharedListMeta?.can_edit ? withCommercialAdornmentSx(listSearchFieldSx) : listSearchFieldSx}
                  inputProps={{ maxLength: 80 }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={listSearchIconSx} />
                      </InputAdornment>
                    ),
                    // Solo el editor: el visitante y el share nunca ven filtros comerciales.
                    ...(selectedSharedListMeta?.can_edit ? {
                      endAdornment: (
                        <CommercialFiltersAdornment value={listCommercialFilters} onChange={setListCommercialFilters} />
                      ),
                    } : {}),
                  }}
                />
                <Box sx={recordCountsRowSx}>
                  <RecordCountsLabel tooltip={sharedDetailCountsText.tooltip}>{sharedDetailCountsText.label}</RecordCountsLabel>
                  <Box sx={recordToolbarControlsSx}>
                    {selectedSharedListMeta?.can_edit ? (
                      <ListSortMenu
                        value={listSortBy}
                        onChange={setListSortBy}
                        options={LIST_DETAIL_COMMERCIAL_SORT_OPTIONS}
                        registrosStyle
                      />
                    ) : null}
                    <RecordViewModeToggle
                      value={recordViewMode}
                      onChange={setRecordViewMode}
                      canUseGrid={canUseRecordGrid}
                    />
                  </Box>
                </Box>
              </Box>
            </Box>
          ) : viewMode === 'cad' ? null : (
            <Box sx={fixedTopZoneSx}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, width: '100%' }}>
                {addMode ? (
                  <AddLotsToListFlow section="top" {...addFlowCommonProps} />
                ) : (
                  (lots.length > 0 || Boolean(lotsSearch.trim()) || hasParcelFilters) && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, width: '100%' }}>
                      <TextField
                        size="small"
                        placeholder="Buscar por título, dirección o barrio..."
                        value={lotsSearch}
                        onChange={(e) => setLotsSearch(e.target.value)}
                        sx={listDetailHasCommercial
                          ? withCommercialAdornmentSx({ ...listSearchFieldSx, mt: 0 })
                          : { ...listSearchFieldSx, mt: 0 }}
                        inputProps={{ maxLength: 80 }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <SearchIcon sx={listSearchIconSx} />
                            </InputAdornment>
                          ),
                          // Solo si se gestiona la lista (llegó el bloque comercial).
                          ...(listDetailHasCommercial ? {
                            endAdornment: (
                              <CommercialFiltersAdornment value={listCommercialFilters} onChange={setListCommercialFilters} />
                            ),
                          } : {}),
                        }}
                      />
                      <Box sx={recordCountsRowSx}>
                        <RecordCountsLabel tooltip={listDetailCountsText.tooltip}>{listDetailCountsText.label}</RecordCountsLabel>
                        <Box sx={recordToolbarControlsSx}>
                          <ListSortMenu
                            value={listSortBy}
                            onChange={setListSortBy}
                            options={listDetailHasCommercial ? LIST_DETAIL_COMMERCIAL_SORT_OPTIONS : MANAGE_LIST_SORT_OPTIONS}
                            registrosStyle
                          />
                          <RecordViewModeToggle
                            value={recordViewMode}
                            onChange={setRecordViewMode}
                            canUseGrid={canUseRecordGrid}
                          />
                        </Box>
                      </Box>
                    </Box>
                  )
                )}
              </Box>
            </Box>
          )}
        </>
      )}

      <Box
        ref={listScrollRef}
        onScroll={handleCardsScroll}
        data-testid="lists-scroll"
        sx={
          viewMode === 'cad'
            ? cadDetailZoneSx
            : viewMode === 'lists'
              ? (showCreateListForm && newListKind === 'cad'
                ? listsCreateFromPlanZoneSx
                : listsIndexZoneSx)
              // Detalle de lista (propia o compartida): la lista principal lleva fade.
              : { ...scrollableListZoneSx, ...getScrollEdgeFadeSx(listFadeTop, listFadeBottom) }
        }
      >
        {viewMode === 'lists' && showCreateListForm ? (
          <Box sx={createListFormBoxSx}>
            <NewListKindChoice
              value={newListKind}
              onChange={setNewListKind}
              disableEmptyList={cadWorkLocksEmptyList}
            />

            {newListKind === 'cad' ? (
              <CadUploadFlow
                onCancel={closeCreateListForm}
                onCreated={handleCadListCreated}
                onCadWorkChange={setCadWorkLocksEmptyList}
              />
            ) : (
              <>
                <Box>
                  <Typography component="label" htmlFor="nueva-lista-nombre" sx={createListLabelSx}>
                    Nombre de la lista
                  </Typography>
                  <TextField
                    id="nueva-lista-nombre"
                    fullWidth
                    placeholder="Escribí un nombre para la lista..."
                    value={newName}
                    onChange={(e) => setNewName(e.target.value)}
                    onKeyDown={handleNewNameKeyDown}
                    sx={createListNameFieldSx}
                    inputProps={{ maxLength: 80 }}
                    autoFocus
                    disabled={creating}
                  />
                </Box>
                <Box sx={createListActionsRowSx}>
                  <Button
                    variant="outlined"
                    onClick={closeCreateListForm}
                    disabled={creating}
                    sx={createListCancelButtonSx}
                  >
                    Cancelar
                  </Button>
                  <Button
                    variant="contained"
                    onClick={handleCreate}
                    disabled={creating || !newName.trim()}
                    startIcon={
                      creating ? (
                        <CircularProgress size={16} thickness={5} sx={{ color: 'inherit' }} />
                      ) : null
                    }
                    sx={createListSubmitButtonSx}
                  >
                    Crear lista
                  </Button>
                </Box>
              </>
            )}
          </Box>
        ) : viewMode === 'cad' ? (
          <CadInListsDetail
            listProject={selectedCadProject}
            initialParcelId={cadInitialParcelId}
            onBack={backFromCad}
            onOverlayChange={onCadOverlayChange}
            sharePatch={cadSharePatch}
            onParcelCountsChange={setCadParcelCounts}
            onParcelOpenChange={setCadParcelLabel}
            deselectParcelRef={cadDeselectParcelRef}
            startGeorefRef={cadStartGeorefRef}
            startPinsRef={cadStartPinsRef}
            autoStartGeoref={cadAutoGeoref}
            listFilters={cadParcelFilters}
            onFilterValueChange={onCadFilterValueChange}
            onAreaExtentChange={setCadAreaExtent}
            georefPickRequest={mapGeorefPickRequest}
            pinPlaceRequest={mapPinPlaceRequest}
            onProjectLoaded={applyCadProjectFromServer}
            onProjectReloaded={handleCadProjectReloaded}
            onLocateLot={onLocateLot}
            onQueryCadRefLotBbox={onQueryCadRefLotBbox}
            onRefLotHighlightChange={handleCadRefLotHighlight}
            onGeorefOpenChange={setCadGeorefOpen}
            inquiryByItem={inquiryStats.byItem}
            spaceMirror={isViewingSharedSpace}
            spaceOwnerUserId={listWriteOwnerUserId}
            onOpenLead={onOpenLead}
            onShowMarketComparables={onShowMarketComparables}
          />
        ) : viewMode === 'sharedDetail' ? (
          loadingSharedDetail ? (
            isRecordGrouped
              ? <LoadingLotCards count={7} />
              : <Box sx={{ mt: 1 }}><LoadingRecordCards count={recordViewMode === 'grid' ? 6 : 5} layout={recordViewMode} /></Box>
          ) : sharedDetailError ? (
            <Box sx={{ ...emptyStateBoxSx, py: 3, gap: 2 }}>
              <Typography sx={emptyStateTitleSx}>{sharedDetailError}</Typography>
              <Button
                variant="outlined"
                size="small"
                onClick={backFromSharedDetail}
                sx={{
                  textTransform: 'none',
                  borderColor: 'rgba(255,255,255,0.2)',
                  color: '#e5e7eb',
                  '&:hover': { borderColor: 'rgba(255,255,255,0.35)', bgcolor: 'rgba(255,255,255,0.05)' },
                }}
              >
                Volver
              </Button>
            </Box>
          ) : (isRecordGrouped ? filteredSharedDetailLots.length : sharedDetailRecordItems.length) === 0 ? (
            <Box sx={emptyStateBoxSx}>
              <ListAltIcon sx={emptyStateIconSx} />
              <Typography sx={emptyStateTitleSx}>
                {sharedDetailLots.length === 0 ? 'No hay unidades en esta lista' : 'No hay resultados que coincidan'}
              </Typography>
              <Typography sx={emptyStateHintSx}>
                {sharedDetailLots.length === 0
                  ? (sharedDetailCounts.totalLots > 0 || sharedDetailCounts.totalUnits > 0
                    ? 'Hay ítems en la lista, pero no se pudieron mostrar con el alcance actual del share.'
                    : 'El dueño aún no agregó ítems o el alcance del share los oculta.')
                  : sharedLotsSearch.trim()
                    ? `Ningún registro coincide con «${sharedLotsSearch.trim()}». Probá con otro título, dirección o barrio.`
                    : 'Probá otra búsqueda o limpiá el filtro.'}
              </Typography>
            </Box>
          ) : !isRecordGrouped ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1 }}>
              <ListDetailRecordCards
                items={visibleSharedRecordItems}
                layout={recordViewMode}
                readOnly
                highlightedLotId={hoveredDetailLotId}
                isRecordLocated={(item) => isListRecordLocated(item.key)}
                onLocateLot={handleLocateListLot}
                onOpenUnit={handleOpenSharedDetailLot}
                onOpenLot={handleOpenSharedLot}
                onHoverLot={handleDetailCardHover}
              />
              {sharedDetailRecordItems.length > visibleSharedRecordItems.length && (
                <Typography
                  sx={{
                    textAlign: 'center',
                    py: 1,
                    fontSize: '11.5px',
                    color: 'rgba(156,163,175,0.75)',
                    fontFamily: typography.fontFamily.primary,
                  }}
                >
                  Mostrando {visibleSharedRecordItems.length} de {sharedDetailRecordItems.length} registros · seguí desplazándote para ver más
                </Typography>
              )}
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {visibleSharedDetailLots.map((lot) => (
                <ListDetailLotCard
                  key={lot.lot_id}
                  lot={lot}
                  isExpanded={expandedLotIds.has(lot.lot_id)}
                  onToggleExpand={toggleLotExpanded}
                  onOpenSharedUnit={handleOpenSharedDetailLot}
                  onOpenSharedLot={handleOpenSharedLot}
                  onLocateLot={handleLocateListLot}
                  onHoverLot={handleDetailLotHover}
                  locateActive={isListLotLocated(lot.lot_id)}
                  isMapHovered={hoveredDetailLotId != null && String(lot.lot_id) === hoveredDetailLotId}
                  readOnly
                  activeSharedUserLotId={activeListUnitId}
                  activeSharedLotId={activeListLotId}
                  inquiryByItem={selectedSharedListMeta?.can_edit ? inquiryStats.byItem : null}
                />
              ))}
              {filteredSharedDetailLots.length > visibleSharedDetailLots.length && (
                <Typography
                  sx={{
                    textAlign: 'center',
                    py: 1,
                    fontSize: '11.5px',
                    color: 'rgba(156,163,175,0.75)',
                    fontFamily: typography.fontFamily.primary,
                  }}
                >
                  Mostrando {visibleSharedDetailLots.length} de {filteredSharedDetailLots.length} lotes · seguí desplazándote para ver más
                </Typography>
              )}
            </Box>
          )

        ) : viewMode === 'lists' && listsPanelTab === 'all' ? (
          // ─── TAB TODAS: Mis listas siempre; compartidas solo si hay ──────────
          (loadingLists || loadingSharedWithMe || loadingCadCollections) ? (
            <LoadingListRows count={7} />
          ) : (
            <Box sx={listsIndexSectionsStackSx}>
              <MyListsSection
                expanded={myListsSectionExpanded}
                onToggle={() => setMyListsSectionExpanded((v) => !v)}
                hasItems={mineUnifiedLists.length > 0 || cadOwnedVisible.length > 0}
                fillHeight
                toolbar={(
                  <Box sx={{ width: '100%' }}>
                    <Box sx={listsSectionToolbarRowSx}>
                      <Box
                        sx={{ ...listFilterChipsRowSx, mb: 0, ml: 0, flex: 1 }}
                        role="group"
                        aria-label="Filtrar mis listas por visibilidad"
                      >
                        <Button
                          disableElevation
                          onClick={() => setVisibilityFilter('all')}
                          aria-pressed={visibilityFilter === 'all'}
                          sx={getListFilterChipSx(visibilityFilter === 'all')}
                        >
                          Todos
                        </Button>
                        <Tooltip title="Público" placement="top" arrow>
                          <Button
                            disableElevation
                            onClick={() => setVisibilityFilter('public')}
                            aria-pressed={visibilityFilter === 'public'}
                            aria-label="Público"
                            sx={getListFilterIconChipSx(visibilityFilter === 'public')}
                          >
                            <LanguageIcon sx={{ fontSize: 16 }} />
                          </Button>
                        </Tooltip>
                        <Tooltip title="Restringido" placement="top" arrow>
                          <Button
                            disableElevation
                            onClick={() => setVisibilityFilter('private')}
                            aria-pressed={visibilityFilter === 'private'}
                            aria-label="Restringido"
                            sx={getListFilterIconChipSx(visibilityFilter === 'private')}
                          >
                            <LockOutlinedIcon sx={{ fontSize: 16 }} />
                          </Button>
                        </Tooltip>
                      </Box>
                      {!showCreateListForm && (
                        <Tooltip title="Agregar nueva lista" placement="top" arrow>
                          <IconButton
                            onClick={() => setShowCreateListForm(true)}
                            aria-label="Agregar nueva lista"
                            sx={listAddButtonSx}
                          >
                            <AddIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </Box>
                )}
                emptyContent={lists.length === 0 && cadOwnedProjects.length === 0 && !isViewingSharedSpace ? (
                  // Sin listas propias: estado vacío visual (patrón Leads) con el mismo CTA que el +.
                  <PanelEmptyState
                    testId="my-lists-empty-state"
                    icon={PlaylistAddIcon}
                    title="Todavía no tenés listas."
                    hint="Creá una lista para ordenar tus lotes y unidades."
                    actionLabel={showCreateListForm ? null : 'Crear lista'}
                    onAction={() => setShowCreateListForm(true)}
                  />
                ) : (
                  <Typography sx={listsSectionEmptySx}>
                    {lists.length === 0 && cadOwnedProjects.length === 0
                      ? `${SPACE_GUEST_EMPTY_LISTS_TITLE}.`
                      : listSearch.trim()
                        ? 'No hay listas propias que coincidan con la búsqueda o el filtro.'
                        : 'No hay listas propias con ese filtro de visibilidad.'}
                  </Typography>
                )}
              >
                {renderUnifiedListContent(mineUnifiedLists, cadOwnedVisible)}
              </MyListsSection>

              {showSharedListsSection && (
                <SharedWithMeListsSection
                  expanded={sharedListsSectionExpanded}
                  onToggle={() => setSharedListsSectionExpanded((v) => !v)}
                  hasItems={sharedUnifiedLists.length > 0 || cadSharedVisible.length > 0}
                  fillHeight
                  emptyContent={
                    <Typography sx={listsSectionEmptySx}>
                      {listSearch.trim()
                        ? 'No hay listas compartidas que coincidan con la búsqueda.'
                        : 'No hay listas compartidas que coincidan.'}
                    </Typography>
                  }
                >
                  {renderUnifiedListContent(sharedUnifiedLists, cadSharedVisible, {
                    cadGuest: true,
                    interleave: true,
                  })}
                </SharedWithMeListsSection>
              )}
            </Box>
          )

        ) : viewMode === 'lists' && listsPanelTab === 'shared' ? (
          // ─── TAB COMPARTIDAS CONMIGO ──────────────────────────────────────────
          loadingSharedWithMe ? (
            <LoadingListRows count={7} />
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {sharedWithMeLists.length === 0 ? (
                <Box sx={emptyStateBoxSx}>
                  <Typography sx={emptyStateTitleSx}>No hay listas compartidas contigo</Typography>
                  <Typography sx={emptyStateHintSx}>
                    Acá ves las que te compartieron para mirar y las que te invitaron a editar.
                  </Typography>
                </Box>
              ) : filteredSharedWithMeLists.length === 0 ? (
                <Box sx={emptyStateBoxSx}>
                  <ListAltIcon sx={emptyStateIconSx} />
                  <Typography sx={emptyStateTitleSx}>No hay listas que coincidan</Typography>
                  <Typography sx={emptyStateHintSx}>Probá con otra búsqueda o limpiá el filtro.</Typography>
                </Box>
              ) : (
                renderUnifiedListContent(sharedOnlyUnifiedLists, cadSharedVisible, {
                  cadGuest: true,
                  interleave: true,
                })
              )}
            </Box>
          )

        ) : viewMode === 'lists' && listsPanelTab === 'mine' && loadingLists ? (
          // ─── TAB LISTAS ───────────────────────────────────────────────────
          <LoadingListRows count={7} />
        ) : viewMode === 'lists' && listsPanelTab === 'mine' && lists.length === 0 ? (
          <Box sx={emptyStateBoxSx}>
            <Box sx={emptyStateIconBoxSx}>
              <ListAltIcon sx={emptyStateIconSx} />
            </Box>
            <Typography sx={emptyStateTitleSx}>
              {isViewingSharedSpace ? SPACE_GUEST_EMPTY_LISTS_TITLE : 'Todavía no tenés listas'}
            </Typography>
            <Typography sx={emptyStateHintSx}>
              {isViewingSharedSpace
                ? SPACE_GUEST_EMPTY_LISTS_HINT
                : 'Creá tu primera lista para ordenar tus lotes y unidades'}
            </Typography>
            {!showCreateListForm && (
              <Button
                variant="contained"
                startIcon={<AddCircleOutlineIcon sx={emptyStateCreateIconSx} />}
                onClick={openCreateListFromEmpty}
                sx={emptyStateCreateButtonSx}
              >
                Crear lista
              </Button>
            )}
          </Box>
        ) : viewMode === 'lists' && listsPanelTab === 'mine' && filteredLists.length === 0 ? (
          <Box sx={emptyStateBoxSx}>
            <ListAltIcon sx={emptyStateIconSx} />
            <Typography sx={emptyStateTitleSx}>No hay listas para mostrar</Typography>
            <Typography sx={emptyStateHintSx}>
              Probá ajustar el buscador o el filtro de visibilidad.
            </Typography>
          </Box>
        ) : viewMode === 'lists' && listsPanelTab === 'mine' ? (
          <List dense disablePadding sx={listSx}>
            {filteredLists.map((row, idx) => (
              <ListItem
                key={row.id}
                disablePadding
                sx={getListItemRootSx(idx === filteredLists.length - 1)}
              >
                <Box
                  sx={{
                    ...getListRowSurfaceSx(editingId === row.id),
                    bgcolor: '#000000',
                    ...(previewListId === row.id && editingId !== row.id
                      ? listRowPreviewActiveSx
                      : {}),
                    ...(editingId === row.id
                      ? {}
                      : {
                          '&:hover': {
                            borderColor:
                              previewListId === row.id
                                ? 'rgba(34,197,94,0.45)'
                                : 'rgba(255,255,255,0.12)',
                            bgcolor: '#000000',
                          },
                        }),
                  }}
                >
                  {editingId === row.id ? (
                    <Box sx={editRowBoxSx}>
                      <TextField
                        size="small"
                        fullWidth
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                        onKeyDown={(e) => handleEditKeyDown(e, row.id)}
                        sx={textFieldSx}
                        autoFocus
                        inputProps={{ maxLength: 80 }}
                      />
                      {/* Espacio ajeno: público / privado es del dueño (solo renombrar). */}
                      {!isViewingSharedSpace && (
                      <Tooltip title={editIsPublic ? 'Lista pública' : 'Lista privada'} placement="top">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                          <Switch
                            size="small"
                            checked={!!editIsPublic}
                            onChange={(e) => setEditIsPublic(e.target.checked)}
                            sx={{
                              '& .MuiSwitch-switchBase.Mui-checked': { color: '#4ade80' },
                              '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: 'rgba(34,197,94,0.6)' },
                            }}
                          />
                          <Typography
                            variant="caption"
                            sx={{
                              fontSize: '11.5px',
                              color: editIsPublic ? '#4ade80' : 'rgba(156,163,175,0.9)',
                              userSelect: 'none',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {editIsPublic ? 'Pública' : 'Privada'}
                          </Typography>
                        </Box>
                      </Tooltip>
                      )}
                      <Tooltip title="Guardar (Enter)" placement="top">
                        <span>
                          <IconButton
                            size="small"
                            onClick={() => saveEdit(row.id)}
                            disabled={savingId === row.id || !editName.trim()}
                            sx={saveEditIconButtonSx}
                          >
                            {savingId === row.id
                              ? <CircularProgress size={16} sx={saveEditProgressSx} />
                              : <CheckIcon sx={{ fontSize: 17 }} />}
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Cancelar (Esc)" placement="top">
                        <IconButton size="small" onClick={cancelEdit} sx={cancelEditIconButtonSx}>
                          <CloseIcon sx={{ fontSize: 17 }} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  ) : (
                    <Box sx={viewRowBoxSx}>
                      <Box
                        onClick={() => openListDetail(row)}
                        sx={{ ...viewRowMainSx, cursor: 'pointer' }}
                      >
                        <Box sx={viewRowTitleRowSx}>
                          <Typography sx={listNameTypographySx}>
                            {row.name}
                          </Typography>
                          {getShareBadgeType(row) === 'restricted' ? (
                            <Tooltip title="Solo invitados" placement="top" arrow>
                              <Chip
                                size="small"
                                icon={<PeopleOutlineIcon />}
                                label=""
                                aria-label="Lista restringida"
                                sx={{
                                  '& .MuiChip-icon': { color: '#4ade80', fontSize: '14px !important', ml: '4px', mr: 0 },
                                  '& .MuiChip-label': { px: 0 },
                                  minWidth: 18,
                                }}
                              />
                            </Tooltip>
                          ) : getShareBadgeType(row) === 'public' ? (
                            <Tooltip title="Link público activo" placement="top" arrow>
                              <Chip
                                size="small"
                                icon={<LanguageIcon />}
                                label=""
                                aria-label="Link público activo"
                                sx={{
                                  '& .MuiChip-icon': { color: '#4ade80', fontSize: '14px !important', ml: '4px', mr: 0 },
                                  '& .MuiChip-label': { px: 0 },
                                  minWidth: 18,
                                }}
                              />
                            </Tooltip>
                          ) : null}
                        </Box>
                        <Box sx={listCardMetaRowSx}>
                          <Typography sx={getListItemCountTypographySx(row.item_count > 0)}>
                            {formatListMembershipCountsLabel({
                              lotCount: row.lot_count ?? 0,
                              unitCount: row.item_count ?? 0,
                              lotContentCount: row.lot_content_count ?? 0,
                            })}
                          </Typography>
                        </Box>
                      </Box>

                      <Box sx={rowActionsBoxSx}>
                        <Tooltip
                          title={previewListId === row.id ? 'Ocultar lotes en el mapa' : 'Mostrar lotes en el mapa'}
                          placement="top"
                          arrow
                        >
                          <IconButton
                            size="small"
                            onClick={(e) => { e.stopPropagation(); togglePreviewOnMap(row); }}
                            sx={{ color: '#9CA3AF', '&:hover': { color: '#F9FAFB', bgcolor: 'rgba(255,255,255,0.06)' } }}
                          >
                            {previewLoadingId === row.id ? (
                              <CircularProgress size={16} sx={{ color: '#9CA3AF' }} />
                            ) : previewListId === row.id ? (
                              <VisibilityOffOutlinedIcon fontSize="small" />
                            ) : (
                              <VisibilityOutlinedIcon fontSize="small" />
                            )}
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Más acciones" placement="top" arrow>
                          <IconButton
                            size="small"
                            onClick={(e) => openRowMenu(e, row)}
                            sx={listCardActionIconSx}
                            aria-label="Más acciones"
                          >
                            <MoreVertIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>

                        <Menu
                          anchorEl={rowMenuAnchor}
                          open={Boolean(rowMenuAnchor) && rowMenuId === row.id}
                          onClose={closeRowMenu}
                          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                          PaperProps={ACTION_MENU_PAPER_PROPS}
                          MenuListProps={ACTION_MENU_LIST_PROPS}
                        >
                          <ActionMenuItem
                            onClick={() => { closeRowMenu(); setActivityDialogTarget({ kind: 'list', id: row.id, name: row.name }); }}
                            icon={<HistoryOutlinedIcon sx={{ fontSize: 20 }} />}
                            label="Historial"
                          />
                          {SHOW_COLLECTION_LEADS_FROM_LIST_DETAIL ? (
                            <ActionMenuItem
                              onClick={() => { closeRowMenu(); setInquiryDialogTarget({ kind: 'list', id: row.id, name: row.name }); }}
                              icon={<MailOutlineIcon sx={{ fontSize: 20 }} />}
                              label="Leads y consultas"
                            />
                          ) : null}
                          <ActionMenuItem
                            onClick={() => { closeRowMenu(); startEdit(row); }}
                            icon={<EditIcon sx={{ fontSize: 20 }} />}
                            label="Renombrar"
                          />
                          <ActionMenuItem
                            onClick={() => { closeRowMenu(); handleShareToggle(row); }}
                            icon={<ShareIcon sx={{ fontSize: 20 }} />}
                            label="Compartir"
                          />
                        </Menu>
                      </Box>
                    </Box>
                  )}
                </Box>
              </ListItem>
            ))}
          </List>

        ) : addMode ? (
          loadingAllLots && addSourceTab === 'records' ? (
            <LoadingListRows count={8} />
          ) : (
            <AddLotsToListFlow section="main" {...addFlowCommonProps} />
          )

        ) : loadingLots ? (
          // ─── DETALLE DE LISTA: cargando ───────────────────────────────────────
          isRecordGrouped
            ? <LoadingLotCards count={7} />
            : <Box sx={{ mt: 1 }}><LoadingRecordCards count={recordViewMode === 'grid' ? 6 : 5} layout={recordViewMode} /></Box>
        ) : (isRecordGrouped ? filteredLots.length : listDetailRecordItems.length) === 0 ? (
          <Box sx={emptyStateBoxSx}>
            {lots.length === 0 && !lotsSearch.trim() && !hasParcelFilters ? (
              <>
                <Box sx={emptyStateIconBoxSx}>
                  <FolderOutlinedIcon sx={emptyStateIconSx} />
                </Box>
                <Typography sx={emptyStateTitleSx}>Esta lista está vacía</Typography>
                <Typography sx={emptyStateHintSx}>
                  Agregá lotes y unidades funcionales para empezar a organizarlos en esta lista.
                </Typography>
              </>
            ) : (
              <>
                <ListAltIcon sx={emptyStateIconSx} />
                <Typography sx={emptyStateTitleSx}>No hay resultados que coincidan</Typography>
                <Typography sx={emptyStateHintSx}>
                  {hasLotsSearch
                    ? `Ningún registro coincide con «${debouncedLotsSearch.trim()}». Probá con otro título, dirección o barrio${hasParcelFilters || countActiveCommercialFilters(listCommercialFilters) > 0 ? ' o limpiá los filtros' : ''}.`
                    : hasParcelFilters
                      ? 'No hay lotes que coincidan con los filtros aplicados.'
                      : 'Probá otra búsqueda o limpiá el filtro.'}
                </Typography>
              </>
            )}
          </Box>
        ) : !isRecordGrouped ? (
          // ─── DETALLE DE LISTA: una card por registro ──────────────────────────
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1 }}>
            <ListDetailRecordCards
              items={visibleListRecordItems}
              layout={recordViewMode}
              highlightedLotId={hoveredDetailLotId}
              isRecordLocated={(item) => isListRecordLocated(item.key)}
              onLocateLot={handleLocateListLot}
              onOpenUnit={handleOpenMineListDetailLot}
              onOpenLot={handleOpenMineListLot}
              onHoverLot={handleDetailCardHover}
              onRemoveLot={isViewingSharedSpace ? undefined : handleRemoveLotFromCurrentList}
              onRemoveUnit={isViewingSharedSpace ? undefined : handleRemoveFromCurrentList}
              updatingUnitId={updatingUnitId}
            />
            {listDetailRecordItems.length > visibleListRecordItems.length && (
              <Typography
                sx={{
                  textAlign: 'center',
                  py: 1,
                  fontSize: '11.5px',
                  color: 'rgba(156,163,175,0.75)',
                  fontFamily: typography.fontFamily.primary,
                }}
              >
                Mostrando {visibleListRecordItems.length} de {listDetailRecordItems.length} registros · seguí desplazándote para ver más
              </Typography>
            )}
          </Box>
        ) : (
          // ─── DETALLE DE LISTA: lotes ──────────────────────────────────────────
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1 }}>
            {visibleFilteredLots.map((lot) => (
              <ListDetailLotCard
                key={lot.lot_id}
                lot={lot}
                isExpanded={expandedLotIds.has(lot.lot_id)}
                onToggleExpand={toggleLotExpanded}
                onOpenListUnit={handleOpenMineListDetailLot}
                onOpenListLot={handleOpenMineListLot}
                onLocateLot={handleLocateListLot}
                onHoverLot={handleDetailLotHover}
                locateActive={isListLotLocated(lot.lot_id)}
                isMapHovered={hoveredDetailLotId != null && String(lot.lot_id) === hoveredDetailLotId}
                onRemoveLot={isViewingSharedSpace ? undefined : handleRemoveLotFromCurrentList}
                onRemoveUnit={isViewingSharedSpace ? undefined : handleRemoveFromCurrentList}
                showFavorite={!isViewingSharedSpace}
                updatingUnitId={updatingUnitId}
                activeSharedUserLotId={activeListUnitId}
                activeSharedLotId={activeListLotId}
                inquiryByItem={inquiryStats.byItem}
              />
            ))}
            {filteredLots.length > visibleFilteredLots.length && (
              <Typography
                sx={{
                  textAlign: 'center',
                  py: 1,
                  fontSize: '11.5px',
                  color: 'rgba(156,163,175,0.75)',
                  fontFamily: typography.fontFamily.primary,
                }}
              >
                Mostrando {visibleFilteredLots.length} de {filteredLots.length} lotes · seguí desplazándote para ver más
              </Typography>
            )}
          </Box>
        )}
      </Box>

      {showAddFlowFooter && (
        <Box sx={{ ...fixedBottomZoneSx, pt: 0, pb: 0, px: 0, borderTop: '1px solid rgba(255,255,255,0.07)' }}>
          <AddLotsToListFlow section="footer" {...addFlowCommonProps} />
        </Box>
      )}

      <ShareUserLotListDialog
        key={listWriteOwnerUserId || 'own-space'}
        open={shareDialogOpen}
        ownerUserId={listWriteOwnerUserId}
        listId={shareDialogList?.id || ''}
        listName={shareDialogList?.name || ''}
        lotCount={shareDialogList?.lot_count ?? shareDialogList?.lotCount ?? null}
        unitCount={
          shareDialogList?.item_count
          ?? shareDialogList?.unit_count
          ?? shareDialogList?.unitCount
          ?? null
        }
        lotContentCount={
          shareDialogList?.lot_content_count
          ?? shareDialogList?.lotContentCount
          ?? null
        }
        onClose={async () => {
          closeShareDialog();
          await refreshLists();
        }}
      />
      <ShareCadProjectDialog
        key={`cad-share-${listWriteOwnerUserId || 'own-space'}`}
        open={Boolean(cadShareProject)}
        slug={cadShareProject?.slug || ''}
        projectId={getCadProjectId(cadShareProject)}
        projectName={cadShareProject?.name || cadShareProject?.slug || ''}
        ownerUserId={listWriteOwnerUserId}
        onClose={closeCadShare}
        onSaved={(saved) => {
          setCadSharePatch(saved);
          setSelectedCadProject((prev) => (
            prev && prev.slug === saved?.slug ? mergeCadProjectSummary(prev, saved) : prev
          ));
          setCadShareProject((prev) => (prev ? mergeCadProjectSummary(prev, saved) : prev));
          reloadCadCollections();
        }}
      />
      <CollectionActivityDialog
        open={Boolean(activityDialogTarget)}
        kind={activityDialogTarget?.kind || 'list'}
        collectionId={activityDialogTarget?.id || ''}
        collectionName={activityDialogTarget?.name || ''}
        onClose={() => setActivityDialogTarget(null)}
        onOpenItem={handleOpenActivityItem}
      />
      {/* Oculto por decisión de la meet del 2026-10-15: no se usa por ahora
          (utilidad muy puntual). Reactivar si se necesita.
          (`SHOW_COLLECTION_LEADS_FROM_LIST_DETAIL`). */}
      {SHOW_COLLECTION_LEADS_FROM_LIST_DETAIL ? (
        <CollectionLeadsDialog
          open={Boolean(inquiryDialogTarget)}
          kind={inquiryDialogTarget?.kind || 'list'}
          collectionId={inquiryDialogTarget?.id || ''}
          collectionName={inquiryDialogTarget?.name || ''}
          onClose={() => setInquiryDialogTarget(null)}
          onOpenItem={handleOpenInquiryItem}
          onStatsChange={inquiryStats.apply}
          ownerUserId={listWriteOwnerUserId}
          onOpenLead={onOpenLead}
        />
      ) : null}
    </Paper>
    )}
      </PanelShell>
    </>
  );
}